// @ts-nocheck
import { Fragment, useEffect, useMemo, useState, useRef, useCallback, useContext } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";
import { BACKEND_BASE_URL } from "../../config/app.config";
import { StateContext } from "../../store/context";
import { StockSearchModal } from "../Compare/_components/stockSelectorModal";
import "./AnnualReportExtraction.css";



const resolveAnnualReportBaseUrl = () => {
  const envUrl =
    process.env.REACT_APP_ANNUAL_REPORT_API ??
    process.env.REACT_APP_API_URL ??
    process.env.REACT_APP_BASE_URL ??
    "";

    
  if (typeof window !== "undefined") {
    const origin = window.location.origin || "";
    const isHttps = window.location.protocol === "https:";
    const candidate = (envUrl || BACKEND_BASE_URL || "").replace(/\/+$/, "");

    if (isHttps && candidate.startsWith("http://")) {
      if (origin && !origin.includes("localhost")) {
        return origin;
      }
      return candidate.replace(/^http:\/\//, "https://");
    }

    if (candidate) {
      return candidate;
    }

    if (origin) {
      return origin;
    }
  }

  return (envUrl || BACKEND_BASE_URL || "http://localhost:8000").replace(
    /\/+$/,
    ""
  );
};

const API_BASE_URL = resolveAnnualReportBaseUrl();
const toAuthorizationHeader = (token) => {
  const trimmed = String(token || '').trim();
  if (!trimmed) {
    return '';
  }
  return /^bearer\s+/i.test(trimmed) ? trimmed : `Bearer ${trimmed}`;
};

const isValidFiscalYear = (value) => /^\d{4}$/.test(String(value || '').trim());
const isIsoDateString = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());
const buildFallbackQuarterOptions = (year) => {
  const numericYear = Number(year);
  if (!Number.isInteger(numericYear) || numericYear < 1900) {
    return [];
  }

  return [
    { quarter: 1, date: `${numericYear}-03-31` },
    { quarter: 2, date: `${numericYear}-06-30` },
    { quarter: 3, date: `${numericYear}-09-30` },
    { quarter: 4, date: `${numericYear}-12-31` },
  ].map((entry) => ({
    year: numericYear,
    quarter: entry.quarter,
    date: entry.date,
    label: `Q${entry.quarter} | ${entry.date}`,
    saved: false,
    finalizedAt: null,
  }));
};

const SOP_METRICS = [
  'Revenues',
  'Gross Profits',
  'Operating Profit',
  'Interest Expense',
  'Interest Income',
  'Profit Before Tax',
  'Taxation',
  'Net Profits',
  'EPS',
  'Share Price',
  'Total Number Of Issued Shares',
  'Fixed Assets',
  'Total Current Assets',
  'Total Assets',
  'Inventories',
  'Trade Receivables',
  'Cash',
  'Total Equity',
  'Trade Payables',
  'Total Current Liabilities',
  'Total Liabilities',
  'Total Debt',
  'Book Value',
  'Depreciation',
  'Amortization',
  'Cash From Operating Activities',
  'Capital Expenditure',
  'Cash From Investing Activities',
  'Net Borrowings',
  'Cash From Financing Activities',
];

const MANUAL_ENTRY_SOP_METRICS = [
  'Share Price',
  'Total Number Of Issued Shares',
];
const MANUAL_ENTRY_SOP_METRIC_KEYS = new Set(
  MANUAL_ENTRY_SOP_METRICS.map((metric) => metric.toLowerCase().replace(/[^a-z0-9]/g, ''))
);

const PREFERRED_STATEMENT_ORDER = [
  'Profit or Loss',
  'Comprehensive Income',
  'Financial Position',
  'Changes in Equity',
  'Cash Flows',
];

const WORKSPACE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'statements', label: 'Statements' },
  { id: 'sop', label: 'SOP Summary' },
  { id: 'exports', label: 'Exports' },
];

const UPLOAD_MODES = [
  { value: 'pdf_upload', label: 'PDF Upload' },
  { value: 'statement_upload', label: 'Statement Upload' },
];

const STATEMENT_UPLOAD_OPTIONS = [
  { value: 'Profit or Loss', label: 'Profit or Loss' },
  { value: 'Financial Position', label: 'Balance Sheet' },
  { value: 'Cash Flows', label: 'Cash Flow' },
];
const STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION = 3;

const buildEmptyStatementUploadImages = () => (
  STATEMENT_UPLOAD_OPTIONS.reduce((acc, option) => {
    acc[option.value] = [];
    return acc;
  }, {})
);

const getImageExtensionFromMimeType = (mimeType) => {
  const text = toTrimmed(mimeType).toLowerCase();
  if (text.includes('png')) return 'png';
  if (text.includes('jpeg') || text.includes('jpg')) return 'jpg';
  if (text.includes('webp')) return 'webp';
  if (text.includes('gif')) return 'gif';
  if (text.includes('bmp')) return 'bmp';
  return 'png';
};

const inferCalendarQuarterFromDate = (dateValue) => {
  const raw = String(dateValue || '').trim();
  if (!isIsoDateString(raw)) {
    return null;
  }
  const month = Number(raw.slice(5, 7));
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return Math.floor((month - 1) / 3) + 1;
};

const getMimeTypeFromDataUrl = (dataUrl) => {
  const raw = toTrimmed(dataUrl);
  const match = raw.match(/^data:([^;]+);base64,/i);
  return match?.[1] ? match[1].toLowerCase() : '';
};

const getStatementUploadLabel = (statementName) => {
  const match = STATEMENT_UPLOAD_OPTIONS.find((option) => option.value === statementName);
  return match?.label || statementName || 'Statement';
};

const parseNumericValue = (input) => {
  if (input === null || typeof input === 'undefined') {
    return null;
  }
  let str = input.toString().trim();
  if (!str) {
    return null;
  }

  str = str.replace(/[\u2212\u2012\u2013\u2014]/g, '-'); // map unicode minus variants

  let isNegative = false;
  if (/^\(.*\)$/.test(str)) {
    isNegative = true;
    str = str.slice(1, -1);
  }

  str = str.replace(/[, ]+/g, '');

  if (str.endsWith('-')) {
    isNegative = true;
    str = str.slice(0, -1);
  }

  const normalised = str.replace(/[^0-9.\-]/g, '');
  if (!normalised || normalised === '-' || normalised === '.' || normalised === '-.') {
    return null;
  }
  const value = Number(normalised);
  if (!Number.isFinite(value)) {
    return null;
  }
  if (isNegative) {
    return -Math.abs(value);
  }
  return value;
};

const formatNumericValue = (value) => {
  if (!Number.isFinite(value)) {
    return null;
  }
  const formatter = new Intl.NumberFormat('en-US', {
    useGrouping: true,
    maximumFractionDigits: 10,
  });
  const formatted = formatter.format(value);
  return formatted
    .replace(/\.0+$/, '')
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
};

const stripOuterQuotes = (value) => value.replace(/^["']+|["']+$/g, '');

const stripMarkdownWrappers = (value) => {
  let output = toTrimmed(value);
  if (!output) {
    return '';
  }

  let previous = '';
  while (output && output !== previous) {
    previous = output;
    output = output.replace(/^`{1,3}\s*([\s\S]*?)\s*`{1,3}$/, '$1').trim();
    output = output.replace(/^\*\*\s*([\s\S]*?)\s*\*\*$/, '$1').trim();
    output = output.replace(/^__\s*([\s\S]*?)\s*__$/, '$1').trim();
    output = output.replace(/^\*\s*([\s\S]*?)\s*\*$/, '$1').trim();
    output = output.replace(/^_\s*([\s\S]*?)\s*_$/, '$1').trim();
  }

  return output;
};

const formatSopDisplayValue = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '-';
  }
  if (typeof value === 'number') {
    const formatted = formatNumericValue(value);
    return formatted ?? value.toString();
  }

  const raw = value.toString().trim();
  if (!raw) {
    return '-';
  }

  const withoutQuotes = stripOuterQuotes(stripMarkdownWrappers(raw));
  if (!withoutQuotes || withoutQuotes === '-') {
    return '-';
  }

  const hasPercent = /%|percent|pct/i.test(withoutQuotes);
  if (hasPercent) {
    return withoutQuotes;
  }

  const numeric = parseNumericValue(withoutQuotes);
  if (numeric === null) {
    return withoutQuotes;
  }

  const formatted = formatNumericValue(Math.abs(numeric)) ?? Math.abs(numeric).toString();
  if (numeric < 0) {
    return raw.includes('(') ? `(${formatted})` : `-${formatted}`;
  }
  return formatted;
};

const appendThreeZeros = (input) => {
  if (input === null || typeof input === 'undefined') {
    return null;
  }
  const raw = input.toString();
  if (!raw.trim() || /%|percent|pct/i.test(raw)) {
    return null;
  }
  const numeric = parseNumericValue(raw);
  if (numeric === null) {
    return null;
  }
  return formatNumericValue(numeric * 1000);
};

const appendSixZeros = (input) => {
  if (input === null || typeof input === 'undefined') {
    return null;
  }
  const raw = input.toString();
  if (!raw.trim() || /%|percent|pct/i.test(raw)) {
    return null;
  }
  const numeric = parseNumericValue(raw);
  if (numeric === null) {
    return null;
  }
  return formatNumericValue(numeric * 1000000);
};

const COLUMN_MONTH_LOOKUP = new Map([
  ['jan', 0],
  ['january', 0],
  ['feb', 1],
  ['february', 1],
  ['mar', 2],
  ['march', 2],
  ['apr', 3],
  ['april', 3],
  ['may', 4],
  ['jun', 5],
  ['june', 5],
  ['jul', 6],
  ['july', 6],
  ['aug', 7],
  ['august', 7],
  ['sep', 8],
  ['sept', 8],
  ['september', 8],
  ['oct', 9],
  ['october', 9],
  ['nov', 10],
  ['november', 10],
  ['dec', 11],
  ['december', 11],
]);

const NON_QUARTER_COLUMN_PATTERN = /\b(year\s+ended|annual|12\s+months(?:\s+ended)?|twelve\s+months(?:\s+ended)?|nine\s+months(?:\s+ended)?|9\s+months(?:\s+ended)?|six\s+months(?:\s+ended)?|6\s+months(?:\s+ended)?|half[-\s]?year|semi[-\s]?annual|ytd|year[-\s]?to[-\s]?date)\b/i;

const isVarianceColumnHeader = (header) => {
  const lower = String(header || '').toLowerCase();
  if (!lower) {
    return false;
  }
  return /\bvariance\b/.test(lower) || /%|percent|pct/.test(lower);
};

const isReferenceColumnHeader = (header) => {
  const lower = String(header || '').toLowerCase().trim();
  if (!lower) {
    return false;
  }
  return /^(note|notes|ref|refs|reference|references|no|no\.|n\/o|n\.o\.)$/.test(lower)
    || /\b(note|notes|reference)\b/.test(lower);
};

const isLikelyQuarterColumn = (header) => {
  const lower = String(header || '').toLowerCase();
  if (!lower) {
    return false;
  }
  if (/(q\s*[1-4])/.test(lower)) {
    return true;
  }
  if (/\b(20\d{2}|19\d{2})\b/.test(lower) && /(quarter|months|period)/.test(lower)) {
    return true;
  }
  const monthDetected = Array.from(COLUMN_MONTH_LOOKUP.keys()).some((token) => lower.includes(token));
  return monthDetected && /\b(20\d{2}|19\d{2})\b/.test(lower);
};

const isNonQuarterColumn = (header) => NON_QUARTER_COLUMN_PATTERN.test(String(header || '').toLowerCase());

const parseColumnDateForRanking = (header) => {
  const rawHeader = String(header || '');
  if (!rawHeader) {
    return null;
  }

  const dottedDate = rawHeader.match(/\b([0-3]?\d)[./-]([01]?\d)[./-](20\d{2}|19\d{2})\b/);
  if (dottedDate) {
    const day = Number(dottedDate[1]);
    const month = Number(dottedDate[2]) - 1;
    const year = Number(dottedDate[3]);
    if (
      Number.isInteger(day) && day >= 1 && day <= 31
      && Number.isInteger(month) && month >= 0 && month <= 11
      && Number.isInteger(year)
    ) {
      return new Date(Date.UTC(year, month, day));
    }
  }

  const lower = rawHeader.toLowerCase();
  const yearMatch = lower.match(/(20\d{2}|19\d{2})/);
  if (!yearMatch) {
    return null;
  }
  const year = Number(yearMatch[1]);

  let month = null;
  for (const [token, index] of COLUMN_MONTH_LOOKUP.entries()) {
    if (lower.includes(token)) {
      month = index;
      break;
    }
  }

  if (month === null) {
    const quarterMatch = lower.match(/q\s*([1-4])/i);
    if (quarterMatch) {
      const quarter = Number(quarterMatch[1]);
      if (quarter >= 1 && quarter <= 4) {
        month = quarter * 3 - 1;
      }
    }
  }

  if (month === null) {
    month = 11;
  }

  return new Date(Date.UTC(year, month, 1));
};

const scoreColumnForRanking = (header, index) => {
  const parsedDate = parseColumnDateForRanking(header);
  if (parsedDate) {
    return parsedDate.getTime();
  }
  const yearMatch = String(header || '').match(/(20\d{2}|19\d{2})/);
  if (yearMatch) {
    return Number(yearMatch[1]) * 1000 - index;
  }
  return -index;
};

const rankPreferredStatementColumns = (columns) => {
  if (!Array.isArray(columns) || !columns.length) {
    return [];
  }

  const cleanedColumns = columns
    .map((column) => String(column || '').trim())
    .filter(Boolean)
    .filter((column) => !isVarianceColumnHeader(column) && !isReferenceColumnHeader(column));

  if (!cleanedColumns.length) {
    return [];
  }

  // Prefer true quarterly columns (e.g. "Q3 2025", "Sep 2025").
  // But if none exist, fall back to annual/year-ended columns (e.g. "Year ended March 2026") —
  // these are valid for Q4 / final-quarter reports and must NOT be discarded.
  const quarterlyColumns = cleanedColumns.filter((column) => (
    isLikelyQuarterColumn(column) && !isNonQuarterColumn(column)
  ));
  const annualColumns = cleanedColumns.filter((column) => (
    isNonQuarterColumn(column) && parseColumnDateForRanking(column) !== null
  ));
  // Use quarterly first; if none, use annual date-bearing columns; last resort: everything
  const candidateColumns = quarterlyColumns.length
    ? quarterlyColumns
    : annualColumns.length
      ? annualColumns
      : cleanedColumns;

  const hasGroupOrConsolidated = candidateColumns.some((column) => (
    /\b(group|consolidated)\b/i.test(column)
  ));
  const groupScopedColumns = hasGroupOrConsolidated
    ? candidateColumns.filter((column) => /\b(group|consolidated)\b/i.test(column))
    : candidateColumns;
  const rankingPool = groupScopedColumns.length ? groupScopedColumns : candidateColumns;

  const scored = rankingPool.map((column, index) => {
    const lower = column.toLowerCase();
    const groupBoost = /\b(group|consolidated)\b/.test(lower) ? 10 : 0;
    const quarterBoost = isLikelyQuarterColumn(column) && !isNonQuarterColumn(column) ? 2 : 0;
    return {
      column,
      dateScore: scoreColumnForRanking(column, index),
      preference: groupBoost + quarterBoost,
      index,
    };
  });

  scored.sort((a, b) => {
    if (a.dateScore !== b.dateScore) {
      return b.dateScore - a.dateScore;
    }
    if (a.preference !== b.preference) {
      return b.preference - a.preference;
    }
    return a.index - b.index;
  });

  if (!scored.length) {
    return [];
  }

  const latestDateScore = scored[0].dateScore;
  return scored
    .filter((entry) => entry.dateScore === latestDateScore)
    .map((entry) => entry.column);
};

const convertValueToPositive = (input) => {
  const numeric = parseNumericValue(input);
  if (numeric === null) {
    return null;
  }
  return formatNumericValue(Math.abs(numeric));
};

const convertValueToNegative = (input) => {
  const numeric = parseNumericValue(input);
  if (numeric === null) {
    return null;
  }
  const absolute = Math.abs(numeric);
  if (absolute === 0) {
    return formatNumericValue(0);
  }
  const formatted = formatNumericValue(absolute);
  return formatted === null ? null : `(${formatted})`;
};

const getRowValueSignAction = (row, columns) => {
  const numericValues = columns
    .map((column) => parseNumericValue(row?.[column]))
    .filter((value) => value !== null);

  if (!numericValues.length) {
    return {
      targetSign: 'positive',
      label: 'Make Values Positive',
    };
  }

  const hasNegativeValue = numericValues.some((value) => value < 0);
  return hasNegativeValue
    ? {
      targetSign: 'positive',
      label: 'Make Values Positive',
    }
    : {
      targetSign: 'negative',
      label: 'Make Values Negative',
    };
};

const resolveStatementMultiplierFactor = (statementMultiplierApplied, statementName) => {
  const targetStatement = toTrimmed(statementName);
  if (!targetStatement || !statementMultiplierApplied || typeof statementMultiplierApplied !== 'object') {
    return 1;
  }

  const normalizedTarget = normaliseKey(targetStatement);
  const matchedEntry = Object.entries(statementMultiplierApplied).find(([key]) => (
    normaliseKey(key) === normalizedTarget
  ));
  const multiplierKey = matchedEntry?.[1];

  if (multiplierKey === 'x1000000') {
    return 1000000;
  }
  if (multiplierKey === 'x1000') {
    return 1000;
  }
  return 1;
};

const isSopMetricMultiplierProtected = (metricName) => {
  const metricKey = normaliseMetricKey(resolveSopMetricName(metricName));
  if (!metricKey) {
    return false;
  }
  return (
    metricKey === normaliseMetricKey('EPS')
    || metricKey === normaliseMetricKey('Book Value')
  );
};

const getMultiplierAwareSopValue = (entry, statementMultiplierApplied) => {
  const displayValue = formatSopDisplayValue(entry?.value);
  if (!entry || typeof entry !== 'object') {
    return displayValue;
  }
  if (entry.manual) {
    return displayValue;
  }
  if (isSopMetricMultiplierProtected(entry.metric)) {
    return displayValue;
  }

  const multiplierFactor = resolveStatementMultiplierFactor(
    statementMultiplierApplied,
    entry.statement
  );
  if (!Number.isFinite(multiplierFactor) || multiplierFactor <= 1) {
    return displayValue;
  }

  const numeric = parseNumericValue(entry.value);
  if (numeric === null) {
    return displayValue;
  }

  const scaled = numeric * multiplierFactor;
  return formatSopDisplayValue(scaled);
};

const normaliseSopValue = (value) => {
  return formatSopDisplayValue(value);
};

const cleanSopText = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return value.toString().trim();
};

const normaliseCashFlowQuarterAdjustment = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const asTrimmed = (input) => {
    if (input === null || typeof input === 'undefined') {
      return '';
    }
    return input.toString().trim();
  };

  const selectedQuarter = asTrimmed(value.selectedQuarter);
  const extractedCumulative = asTrimmed(value.extractedCumulative);
  const previousCumulative = asTrimmed(value.previousCumulative);
  const quarterValue = asTrimmed(value.quarterValue);

  if (!extractedCumulative && !previousCumulative && !quarterValue) {
    return null;
  }

  return {
    selectedQuarter,
    extractedCumulative: extractedCumulative || '-',
    previousCumulative: previousCumulative || '-',
    quarterValue: quarterValue || '-',
    _disabled: value._disabled !== undefined ? Boolean(value._disabled) : undefined,
  };
};

const normaliseProfitLossQuarterAdjustment = (value) =>
  normaliseCashFlowQuarterAdjustment(value);

const isTrue = (value) =>
  value === true || String(value || '').trim().toLowerCase() === 'true';

const getStockSelectionKey = (stock) => {
  const id = stock?.id;
  if (id !== null && typeof id !== 'undefined' && String(id).trim()) {
    return `id:${String(id).trim()}`;
  }
  const ticker = String(stock?.ticker_symbol || '').trim().toUpperCase();
  const market = String(stock?.market_name || stock?.stock_market_id || '').trim().toUpperCase();
  return ticker ? `ticker:${ticker}:${market}` : '';
};

function toTrimmed(value) {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  return value.toString().trim();
}

const isDashPlaceholder = (value) => {
  const raw = toTrimmed(value);
  return Boolean(raw) && /^[-\u2012\u2013\u2014\u2212]+$/.test(raw);
};

const formatAccountingValue = (value) => {
  if (!Number.isFinite(value)) {
    return '-';
  }
  const absolute = Math.abs(value);
  const formatted = formatNumericValue(absolute) ?? absolute.toString();
  return value < 0 ? `(${formatted})` : formatted;
};

const stripCashFlowQuarterCalcSuffix = (value) => (
  toTrimmed(value).replace(/\s*\|\s*Cashflow quarter calc:[\s\S]*$/i, '').trim()
);

const stripProfitLossQuarterCalcSuffix = (value) => (
  toTrimmed(value).replace(/\s*\|\s*Profit\/loss Q4 calc:[\s\S]*$/i, '').trim()
);

const getCashFlowScaleFactor = (scaleKey) => {
  if (scaleKey === 'x1000000') {
    return 1000000;
  }
  if (scaleKey === 'x1000') {
    return 1000;
  }
  return 1;
};

const scaleCashFlowQuarterAdjustment = (entry, scaleKey, statementMultiplierApplied) => {
  const baseAdjustment = normaliseCashFlowQuarterAdjustment(entry?.cashFlowQuarterAdjustment);
  const previousCumulativeValue = parseNumericValue(baseAdjustment?.previousCumulative);
  const extractedCumulativeValue = parseNumericValue(
    entry?.value ?? baseAdjustment?.extractedCumulative
  );

  if (previousCumulativeValue === null) {
    return null;
  }
  if (extractedCumulativeValue === null) {
    return null;
  }

  const requestedScaleFactor = getCashFlowScaleFactor(scaleKey);
  const cashFlowStatementMultiplier = resolveStatementMultiplierFactor(
    statementMultiplierApplied,
    'Cash Flows'
  );
  const effectiveScaleFactor = (
    Number.isFinite(cashFlowStatementMultiplier)
    && cashFlowStatementMultiplier > 1
  )
    ? Math.max(1, requestedScaleFactor / cashFlowStatementMultiplier)
    : requestedScaleFactor;

  const scaledExtracted = extractedCumulativeValue * effectiveScaleFactor;
  const quarterValue = scaledExtracted - previousCumulativeValue;
  const adjustment = {
    selectedQuarter: baseAdjustment?.selectedQuarter || '',
    extractedCumulative: formatAccountingValue(scaledExtracted),
    previousCumulative: formatAccountingValue(previousCumulativeValue),
    quarterValue: formatAccountingValue(quarterValue),
  };
  const calcText = `Cashflow quarter calc: extracted cumulative ${adjustment.extractedCumulative} - previous FY quarters ${adjustment.previousCumulative} = this quarter ${adjustment.quarterValue}`;
  const baseSource = stripCashFlowQuarterCalcSuffix(entry?.sourceLine);

  return {
    ...adjustment,
    value: adjustment.quarterValue,
    sourceLine: baseSource ? `${baseSource} | ${calcText}` : calcText,
  };
};

const recomputeProfitLossQuarterAdjustment = (entry, linkedSummary, statementMultiplierApplied) => {
  const baseAdjustment = normaliseProfitLossQuarterAdjustment(entry?.profitLossQuarterAdjustment);
  let previousCumulativeValue = parseNumericValue(baseAdjustment?.previousCumulative);
  if (previousCumulativeValue === null) {
    previousCumulativeValue = 0;
  }
  
  const statementName = linkedSummary?.statement || entry?.statement || 'Profit or Loss';
  const multiplierFactor = resolveStatementMultiplierFactor(
    statementMultiplierApplied,
    statementName
  );

  let extractedCumulativeValue = 0;
  let isAlreadyScaled = false;

  const parsedLinkedValue = linkedSummary ? parseNumericValue(linkedSummary.value) : null;
  const parsedBaseValue = baseAdjustment ? parseNumericValue(baseAdjustment.extractedCumulative) : null;

  if (entry?.manual) {
    extractedCumulativeValue = parseNumericValue(entry?.value) ?? 0;
    isAlreadyScaled = true;
  } else if (parsedLinkedValue !== null) {
    extractedCumulativeValue = parsedLinkedValue;
    isAlreadyScaled = multiplierFactor > 1;
  } else if (!linkedSummary) {
    // If it's not manual and not linked to any statement row, the extracted value is 0
    extractedCumulativeValue = 0;
    isAlreadyScaled = false;
  } else if (parsedBaseValue !== null) {
    extractedCumulativeValue = parsedBaseValue;
    isAlreadyScaled = true;
  } else {
    extractedCumulativeValue = parseNumericValue(entry?.value) ?? 0;
    isAlreadyScaled = false;
  }

  const quarterValue = extractedCumulativeValue - previousCumulativeValue;
  const adjustment = {
    selectedQuarter: baseAdjustment?.selectedQuarter || '',
    extractedCumulative: formatAccountingValue(extractedCumulativeValue),
    previousCumulative: formatAccountingValue(previousCumulativeValue),
    quarterValue: formatAccountingValue(quarterValue),
  };
  const calcText = `Profit/loss Q4 calc: extracted cumulative ${adjustment.extractedCumulative} - previous FY quarters ${adjustment.previousCumulative} = this quarter ${adjustment.quarterValue}`;
  const baseSource = stripProfitLossQuarterCalcSuffix(
    linkedSummary?.sourceLine || (entry?.manual ? entry?.sourceLine : '')
  );

  return {
    ...adjustment,
    value: adjustment.quarterValue,
    sourceLine: baseSource ? `${baseSource} | ${calcText}` : '',
  };
};

const normaliseKey = (value) => toTrimmed(value).toLowerCase();

const normaliseMetricKey = (value) =>
  toTrimmed(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const isManualEntrySopMetric = (metric) =>
  MANUAL_ENTRY_SOP_METRIC_KEYS.has(normaliseMetricKey(metric));

const normaliseStatementLookupKey = (value) => {
  const compact = normaliseMetricKey(value);
  if (!compact) {
    return '';
  }
  if (compact.includes('balancesheet') || compact.includes('financialposition')) {
    return 'financialposition';
  }
  if (compact.includes('cashflow')) {
    return 'cashflows';
  }
  if (compact.includes('changesinequity')) {
    return 'changesinequity';
  }
  if (compact.includes('comprehensiveincome')) {
    return 'comprehensiveincome';
  }
  if (compact.includes('profitorloss') || compact.includes('incomestatement')) {
    return 'profitorloss';
  }
  return compact;
};

const getStatementImageDataUrl = (image) => {
  const directValue = toTrimmed(
    typeof image === 'string'
      ? image
      : image?.dataUrl || image?.src || image?.url
  );
  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(directValue)) {
    return directValue;
  }

  const rawBase64 = toTrimmed(
    typeof image === 'string'
      ? ''
      : image?.base64 || image?.image_base64
  );
  if (/^data:image\/[a-z0-9.+-]+;base64,/i.test(rawBase64)) {
    return rawBase64;
  }
  if (rawBase64 && /^[a-z0-9+/=\s]+$/i.test(rawBase64)) {
    const mimeType = toTrimmed(image?.mimeType || image?.mime_type) || 'image/png';
    return `data:${mimeType};base64,${rawBase64.replace(/\s+/g, '')}`;
  }

  return '';
};

const normaliseStatementUploadImagesByStatement = (rawValue) => {
  const empty = buildEmptyStatementUploadImages();
  if (!rawValue || typeof rawValue !== 'object') {
    return empty;
  }

  const statementLookup = new Map();
  STATEMENT_UPLOAD_OPTIONS.forEach((option) => {
    const statementValue = toTrimmed(option?.value);
    const statementLabel = toTrimmed(option?.label);
    const statementValueKey = normaliseStatementLookupKey(statementValue);
    const statementLabelKey = normaliseStatementLookupKey(statementLabel);
    if (statementValueKey) {
      statementLookup.set(statementValueKey, statementValue);
    }
    if (statementLabelKey) {
      statementLookup.set(statementLabelKey, statementValue);
    }
  });

  const resolved = { ...empty };
  Object.entries(rawValue || {}).forEach(([rawStatementName, images]) => {
    const statementKey = normaliseStatementLookupKey(rawStatementName);
    const statementName = statementLookup.get(statementKey) || '';
    if (!statementName || !Array.isArray(images)) {
      return;
    }

    const cleaned = images
      .map((image, index) => {
        const dataUrl = getStatementImageDataUrl(image);
        if (!/^data:image\/[a-z0-9.+-]+;base64,/i.test(dataUrl)) {
          return null;
        }

        const resolvedMimeType = toTrimmed(image?.mimeType)
          || toTrimmed(image?.mime_type)
          || getMimeTypeFromDataUrl(dataUrl)
          || 'image/png';
        const imageExtension = getImageExtensionFromMimeType(resolvedMimeType);
        const imageName = toTrimmed(image?.name)
          || `${statementName}-${index + 1}.${imageExtension}`;
        const imageSize = Number(image?.size);

        return {
          id:
            toTrimmed(image?.id)
            || `${normaliseMetricKey(statementName)}-saved-${index + 1}`,
          name: imageName,
          mimeType: resolvedMimeType,
          size: Number.isFinite(imageSize) && imageSize > 0 ? Math.round(imageSize) : 0,
          source: toTrimmed(image?.source) || 'saved',
          dataUrl,
        };
      })
      .filter(Boolean);

    resolved[statementName] = cleaned.slice(0, STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION);
  });

  return resolved;
};

const buildColumnHeaderLines = (value) => {
  const text = toTrimmed(value);
  if (!text) {
    return [];
  }

  const buildLinesForSegment = (segment) => {
    const words = toTrimmed(segment).split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
      return words.length ? [words.join(' ')] : [];
    }

    const lines = [];
    let index = 0;
    while (index < words.length) {
      const remaining = words.length - index;
      const take = remaining <= 3 ? remaining : (remaining % 2 === 1 ? 3 : 2);
      lines.push(words.slice(index, index + take).join(' '));
      index += take;
    }
    return lines;
  };

  const pipeSegments = text.split(/\s*\|\s*/).filter(Boolean);
  if (pipeSegments.length > 1) {
    return pipeSegments.flatMap((segment) => buildLinesForSegment(segment));
  }

  return buildLinesForSegment(text);
};

const SOP_METRIC_ALIASES = {
  revenue: 'Revenues',
  revenues: 'Revenues',
  grossprofit: 'Gross Profits',
  grossprofits: 'Gross Profits',
  operatingprofit: 'Operating Profit',
  operatingprofits: 'Operating Profit',
  interestexpense: 'Interest Expense',
  interestincome: 'Interest Income',
  pbt: 'Profit Before Tax',
  profitbeforetax: 'Profit Before Tax',
  profitbeforetaxation: 'Profit Before Tax',
  profitbeforetaxes: 'Profit Before Tax',
  pretaxprofit: 'Profit Before Tax',
  pretaxprofits: 'Profit Before Tax',
  pretaxincome: 'Profit Before Tax',
  tax: 'Taxation',
  taxation: 'Taxation',
  taxes: 'Taxation',
  incometax: 'Taxation',
  taxexpense: 'Taxation',
  taxexpenses: 'Taxation',
  netprofit: 'Net Profits',
  netprofits: 'Net Profits',
  eps: 'EPS',
  epsactuals: 'EPS',
  earningspershare: 'EPS',
  dividendspershare: 'Dividends Per Share',
  dps: 'Dividends Per Share',
  fixedassets: 'Fixed Assets',
  inventory: 'Inventories',
  inventories: 'Inventories',
  tradereceivable: 'Trade Receivables',
  tradereceivables: 'Trade Receivables',
  tradepayable: 'Trade Payables',
  tradepayables: 'Trade Payables',
  cash: 'Cash',
  currentassets: 'Total Current Assets',
  totalcurrentassets: 'Total Current Assets',
  currentliabilities: 'Total Current Liabilities',
  totalcurrentliabilities: 'Total Current Liabilities',
  totalassets: 'Total Assets',
  totalequity: 'Total Equity',
  totalliabilities: 'Total Liabilities',
  totaldebt: 'Total Debt',
  bookvalue: 'Book Value',
  netassetpershare: 'Book Value',
  netassetvaluepershare: 'Book Value',
  netassetspershare: 'Book Value',
  netassetsvaluepershare: 'Book Value',
  netassetsperordinaryshare: 'Book Value',
  netassetvalueperordinaryshare: 'Book Value',
  netassetsattributabletoordinaryshareholderspershare: 'Book Value',
  netassetsattributabletoequityholderspershare: 'Book Value',
  netassetsattributabletoownerspershare: 'Book Value',
  netassetspershareattributabletoequityholders: 'Book Value',
  netassetvalueattributabletoequityholderspershare: 'Book Value',
  navperordinaryshare: 'Book Value',
  navpershare: 'Book Value',
  bookvaluesplitadjusted: 'Book Value',
  depreciation: 'Depreciation',
  depreciationqtrly: 'Depreciation',
  depqtrly: 'Depreciation',
  amortisation: 'Amortization',
  amortization: 'Amortization',
  amortizationqtrly: 'Amortization',
  amortisationqtrly: 'Amortization',
  amorqtrly: 'Amortization',
  operatingcashflow: 'Cash From Operating Activities',
  cashfromoperatingactivities: 'Cash From Operating Activities',
  cfoqtrly: 'Cash From Operating Activities',
  ocfqtrly: 'Cash From Operating Activities',
  capitalexpenditure: 'Capital Expenditure',
  capitalexpenditureoutflow: 'Capital Expenditure',
  capex: 'Capital Expenditure',
  capitalexpqtrly: 'Capital Expenditure',
  capexpqtrly: 'Capital Expenditure',
  capexqtrly: 'Capital Expenditure',
  investingcashflow: 'Cash From Investing Activities',
  cashfrominvestingactivities: 'Cash From Investing Activities',
  cfiqtrly: 'Cash From Investing Activities',
  icfqtrly: 'Cash From Investing Activities',
  financingcashflow: 'Cash From Financing Activities',
  cashfromfinancingactivities: 'Cash From Financing Activities',
  cffqtrly: 'Cash From Financing Activities',
  netborrowing: 'Net Borrowings',
  netborrowings: 'Net Borrowings',
  netborrowingsqtrly: 'Net Borrowings',
  netborrowingsqrtly: 'Net Borrowings',
  shareprice: 'Share Price',
  shareprices: 'Share Price',
  sharesoutstanding: 'Total Number Of Issued Shares',
  totalnumberofshares: 'Total Number Of Issued Shares',
  totalnumberofissuedshares: 'Total Number Of Issued Shares',
  totnoofshares: 'Total Number Of Issued Shares',
};

const resolveSopMetricName = (metric) => {
  const key = normaliseMetricKey(metric);
  if (!key) {
    return '';
  }
  const alias = SOP_METRIC_ALIASES[key];
  if (alias) {
    return alias;
  }
  const direct = SOP_METRICS.find((entry) => normaliseMetricKey(entry) === key);
  return direct || toTrimmed(metric);
};

const CASH_FLOW_QUARTER_METRIC_KEYS = new Set([
  normaliseMetricKey('Cash From Operating Activities'),
  normaliseMetricKey('Cash From Investing Activities'),
  normaliseMetricKey('Cash From Financing Activities'),
  normaliseMetricKey('Capital Expenditure'),
  normaliseMetricKey('Capital Expenditure Outflow'),
  normaliseMetricKey('Net Borrowings'),
  normaliseMetricKey('Depreciation'),
  normaliseMetricKey('Amortization'),
  // Backward compatibility for old short names in stored/manual data.
  normaliseMetricKey('CFO Qtrly'),
  normaliseMetricKey('CFI Qtrly'),
  normaliseMetricKey('CFF Qtrly'),
  normaliseMetricKey('Cap Exp Qtrly'),
  normaliseMetricKey('Capex'),
  normaliseMetricKey('Net Borrowings Qtrly'),
  normaliseMetricKey('Dep Qtrly'),
  normaliseMetricKey('Amor Qtrly'),
  normaliseMetricKey('Amortisation'),
]);

const PROFIT_LOSS_Q4_METRIC_KEYS = new Set([
  'revenue',
  'revenues',
  'grossprofit',
  'grossprofits',
  'operatingprofit',
  'operatingprofits',
  'interestexpense',
  'interestincome',
  'pbt',
  'profitbeforetax',
  'profitbeforetaxation',
  'profitbeforetaxes',
  'pretaxprofit',
  'pretaxprofits',
  'pretaxincome',
  'taxation',
  'tax',
  'taxes',
  'incometax',
  'taxexpense',
  'taxexpenses',
  'netprofit',
  'netprofits',
  'epsactuals',
  'eps',
]);

const DIRECT_TOTAL_METRIC_KEYS = new Set([
  normaliseMetricKey('Total Assets'),
  normaliseMetricKey('Total Current Assets'),
  normaliseMetricKey('Total Liabilities'),
  normaliseMetricKey('Total Current Liabilities'),
  normaliseMetricKey('Total Debt'),
  normaliseMetricKey('Total Equity'),
]);

const resolveDirectTotalScore = (metricName, lineItemText) => {
  const metricKey = normaliseMetricKey(metricName);
  const text = toTrimmed(lineItemText).toLowerCase();
  if (!metricKey || !text) {
    return 0;
  }

  const hasCurrent = /\bcurrent\b/.test(text);
  const hasNonCurrent = /\bnon[-\s]?current\b/.test(text);
  const hasLongTerm = /\blong[-\s]?term\b/.test(text);
  const hasShortTerm = /\bshort[-\s]?term\b/.test(text);
  const isComponentLine = hasCurrent || hasNonCurrent || hasLongTerm || hasShortTerm;

  if (metricKey === normaliseMetricKey('Total Assets')) {
    if (/\btotal\s+assets\b/.test(text) && !isComponentLine) return 200;
    if (/\bassets\b/.test(text) && /\btotal\b/.test(text) && !isComponentLine) return 120;
    return 0;
  }
  if (metricKey === normaliseMetricKey('Total Current Assets')) {
    if (/\btotal\s+current\s+assets\b/.test(text)) return 200;
    if (/\bcurrent\s+assets\b/.test(text) && /\btotal\b/.test(text)) return 130;
    return 0;
  }
  if (metricKey === normaliseMetricKey('Total Liabilities')) {
    if (/\btotal\s+liabilit(?:y|ies)\b/.test(text) && !isComponentLine) return 200;
    if (/\bliabilit(?:y|ies)\b/.test(text) && /\btotal\b/.test(text) && !isComponentLine) return 120;
    return 0;
  }
  if (metricKey === normaliseMetricKey('Total Current Liabilities')) {
    if (/\btotal\s+current\s+liabilit(?:y|ies)\b/.test(text)) return 200;
    if (/\bcurrent\s+liabilit(?:y|ies)\b/.test(text) && /\btotal\b/.test(text)) return 130;
    return 0;
  }
  if (metricKey === normaliseMetricKey('Total Equity')) {
    if (/\btotal\s+equity\b/.test(text)) return 200;
    if (/\btotal\s+shareholders?\s+equity\b/.test(text)) return 180;
    if (/\bequity\b/.test(text) && /\btotal\b/.test(text)) return 120;
    return 0;
  }
  if (metricKey === normaliseMetricKey('Total Debt')) {
    if (/\btotal\s+debt\b/.test(text)) return 220;
    if (/\btotal\s+borrowings\b/.test(text)) return 200;
    if (/\btotal\s+interest[-\s]?bearing\b/.test(text)) return 180;
    if (/\bborrowings?\b/.test(text) && /\btotal\b/.test(text) && !isComponentLine) return 140;
    return 0;
  }

  return 0;
};

const hasPopulatedValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return false;
};

const buildStatementGroups = (items) => (
  (items || []).reduce((acc, item) => {
    if (!item || typeof item !== 'object') {
      return acc;
    }
    const key = item.statement || 'Unassigned Statement';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {})
);

const getOrderedStatements = (groupedStatements) => {
  const dynamicStatements = Object.keys(groupedStatements || {}).filter(
    (name) => name && !PREFERRED_STATEMENT_ORDER.includes(name)
  );
  return [
    ...PREFERRED_STATEMENT_ORDER,
    ...dynamicStatements,
  ].filter((name, index, self) => name && self.indexOf(name) === index);
};

const buildStatementExport = ({
  lineItems,
  valueColumns,
  verifiedStatements,
  limitPerStatement,
  includePreviewNote,
}) => {
  const grouped = buildStatementGroups(lineItems);
  const orderedStatements = getOrderedStatements(grouped);
  const statements = orderedStatements.map((statementName) => {
    const rows = grouped[statementName] || [];
    const columns = Array.isArray(valueColumns)
      ? valueColumns.filter((column) => (
        rows.some((item) => hasPopulatedValue(item?.[column]))
      ))
      : [];
    const slicedRows = typeof limitPerStatement === 'number'
      ? rows.slice(0, limitPerStatement)
      : rows;
    const lineItemsPayload = slicedRows.map((item) => {
      const values = {};
      columns.forEach((column) => {
        values[column] = item?.[column] ?? '';
      });
      return {
        lineItem: item?.lineItem || item?.['Line Item'] || '',
        sopMetric: item?.classification || '',
        classificationSource: item?.classificationSource || '',
        aiConfidence: typeof item?.aiConfidence === 'number' ? item.aiConfidence : null,
        values,
      };
    });
    const payload = {
      statement: statementName,
      reviewed: Boolean(verifiedStatements?.[statementName]),
      lineItemCount: rows.length,
      columns,
      lineItems: lineItemsPayload,
    };
    if (includePreviewNote && typeof limitPerStatement === 'number' && rows.length > limitPerStatement) {
      payload.previewNote = `Showing ${limitPerStatement} of ${rows.length} line items.`;
    }
    return payload;
  });

  return { statements, orderedStatements };
};

const buildAnnualReportJson = ({
  pdfName,
  qcComplete,
  lineItems,
  valueColumns,
  verifiedStatements,
  sopSummary,
  manualSopEntries,
  statementUploadImagesByStatement,
  limitPerStatement,
  includePreviewNote,
  includeTimestamp,
}) => {
  const { statements, orderedStatements } = buildStatementExport({
    lineItems,
    valueColumns,
    verifiedStatements,
    limitPerStatement,
    includePreviewNote,
  });

  const manualAdjustments = {};
  Object.entries(manualSopEntries || {}).forEach(([metric, entries]) => {
    if (!Array.isArray(entries) || !entries.length) {
      return;
    }
    manualAdjustments[metric] = entries.map((entry) => ({
      id: entry?.id || '',
      statement: toTrimmed(entry?.statement),
      lineItem: toTrimmed(entry?.lineItem),
      column: toTrimmed(entry?.column),
      value: entry?.value ?? '',
      calculation: Array.isArray(entry?.calculation)
        ? entry.calculation.map((step) => ({
          operator: step?.operator || '+',
          metric: toTrimmed(step?.metric),
          statement: toTrimmed(step?.statement),
          lineItem: toTrimmed(step?.lineItem),
          column: toTrimmed(step?.column),
          constant: step?.constant ?? '',
        }))
        : [],
    }));
  });

  const normalizedStatementImages = normaliseStatementUploadImagesByStatement(
    statementUploadImagesByStatement
  );
  const statementImageCount = Object.values(normalizedStatementImages || {}).reduce(
    (total, images) => total + (Array.isArray(images) ? images.length : 0),
    0
  );

  return {
    metadata: {
      pdfName: pdfName || '',
      generatedAt: includeTimestamp ? new Date().toISOString() : '',
      qcComplete: Boolean(qcComplete),
      totalStatements: orderedStatements.length,
      totalLineItems: Array.isArray(lineItems) ? lineItems.length : 0,
      reviewedStatements: verifiedStatements || {},
      valueColumns: Array.isArray(valueColumns) ? valueColumns : [],
      statementUploadImagesByStatement: normalizedStatementImages,
      statementImageCount,
    },
    statements,
    statementUploadImagesByStatement: normalizedStatementImages,
    sopSummary: Array.isArray(sopSummary)
      ? sopSummary.map((entry) => ({
        metric: toTrimmed(entry?.metric),
        value: entry?.value ?? '',
        statement: toTrimmed(entry?.statement),
        column: toTrimmed(entry?.column),
        sourceLine: toTrimmed(entry?.sourceLine),
        cashFlowQuarterAdjustment: normaliseCashFlowQuarterAdjustment(entry?.cashFlowQuarterAdjustment),
        profitLossQuarterAdjustment: normaliseProfitLossQuarterAdjustment(entry?.profitLossQuarterAdjustment),
        manual: Boolean(entry?.manual),
      }))
      : [],
    manualSopAdjustments: manualAdjustments,
  };
};

const STATEMENTS_WITHOUT_SOP_METRICS = [
  'comprehensive income',
  'changes in equity',
];

const shouldDisplaySopMetricColumn = (statementName) => {
  const text = toTrimmed(statementName).toLowerCase();
  if (!text) {
    return true;
  }
  return !STATEMENTS_WITHOUT_SOP_METRICS.some((segment) => text.includes(segment));
};

const hasMeaningfulSopValue = (value) => {
  const text = toTrimmed(value);
  return Boolean(text && text !== '-');
};

const getSopVerificationStatus = (row) => {
  if (!hasMeaningfulSopValue(row?.value)) {
    return 'missing';
  }
  if (row?.manual) {
    return 'manual';
  }
  return 'auto';
};

const getSopStatusLabel = (status) => {
  if (status === 'missing') {
    return 'Missing';
  }
  if (status === 'manual') {
    return 'Manual';
  }
  return 'Auto';
};

const buildEmptySopEditDraft = () => ({
  value: '',
  statement: '',
  column: '',
  sourceLine: '',
});

const buildEmptySopSummary = () => SOP_METRICS.map((metric) => ({
  metric,
  value: isManualEntrySopMetric(metric) ? '' : '-',
  statement: '',
  column: '',
  sourceLine: '',
  cashFlowQuarterAdjustment: null,
  profitLossQuarterAdjustment: null,
  manual: false,
}));

const buildEmptyPausedSopStatementMap = () => ({});

const createEmptyCalculationStep = (overrides = {}) => ({
  operator: '+',
  metric: '',
  statement: '',
  lineItem: '',
  column: '',
  constant: '',
  ...overrides,
});

const normaliseSopSummaryEntries = (entries) => {
  const populated = new Map();

  if (Array.isArray(entries)) {
    entries.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const metric = resolveSopMetricName(entry.metric || entry.name);
      const metricKey = normaliseMetricKey(metric);
      if (!metricKey || populated.has(metricKey)) return;
      populated.set(metricKey, {
        metric: metric || '',
        value: normaliseSopValue(entry.value),
        statement: cleanSopText(entry.statement || ''),
        column: cleanSopText(entry.column || entry.sourceColumn || ''),
        sourceLine: cleanSopText(entry.sourceLine || entry.lineItem || ''),
        cashFlowQuarterAdjustment: normaliseCashFlowQuarterAdjustment(entry.cashFlowQuarterAdjustment),
        profitLossQuarterAdjustment: normaliseProfitLossQuarterAdjustment(entry.profitLossQuarterAdjustment),
        manual: Boolean(entry.manual),
      });
    });
  }

  return SOP_METRICS.map((metric) => {
    const metricKey = normaliseMetricKey(metric);
    if (metricKey && populated.has(metricKey)) {
      return populated.get(metricKey);
    }
    return {
      metric,
      value: isManualEntrySopMetric(metric) ? '' : '-',
      statement: '',
      column: '',
      sourceLine: '',
      cashFlowQuarterAdjustment: null,
      profitLossQuarterAdjustment: null,
      manual: false,
    };
  });
};

const normaliseImportedManualSopEntries = (manualSopAdjustments) => {
  const normalized = {};
  Object.entries(manualSopAdjustments || {}).forEach(([metric, entries]) => {
    if (!Array.isArray(entries) || !entries.length) {
      return;
    }
    const resolvedMetric = resolveSopMetricName(metric) || toTrimmed(metric);
    if (!resolvedMetric) {
      return;
    }
    const existing = Array.isArray(normalized[resolvedMetric]) ? normalized[resolvedMetric] : [];
    const mappedEntries = entries.map((entry, index) => ({
      id: entry?.id || `imported-${resolvedMetric}-${index}`,
      statement: toTrimmed(entry?.statement),
      lineItem: toTrimmed(entry?.lineItem),
      column: toTrimmed(entry?.column),
      value: entry?.value ?? "",
      calculation: Array.isArray(entry?.calculation)
        ? entry.calculation.map((step) => ({
          ...createEmptyCalculationStep(),
          ...step,
          operator: step?.operator || "+",
          metric: resolveSopMetricName(step?.metric) || toTrimmed(step?.metric),
          statement: toTrimmed(step?.statement),
          lineItem: toTrimmed(step?.lineItem),
          column: toTrimmed(step?.column),
          constant: step?.constant ?? "",
        }))
        : [],
    }));
    normalized[resolvedMetric] = [...existing, ...mappedEntries];
  });
  return normalized;
};

const rebuildLineItemsFromReviewSnapshot = (reviewSnapshot) => {
  const statements = Array.isArray(reviewSnapshot?.statements)
    ? reviewSnapshot.statements
    : [];
  const rows = [];
  let rowCounter = 1;

  statements.forEach((statementBlock) => {
    const statementName = toTrimmed(statementBlock?.statement) || "Statement";
    const columns = Array.isArray(statementBlock?.columns)
      ? statementBlock.columns.map((column) => toTrimmed(column)).filter(Boolean)
      : [];
    const lineItems = Array.isArray(statementBlock?.lineItems)
      ? statementBlock.lineItems
      : [];

    lineItems.forEach((lineItem) => {
      const values = lineItem?.values && typeof lineItem.values === "object"
        ? lineItem.values
        : {};
      const row = {
        rowId: `${normaliseKey(statementName)}-${rowCounter}`,
        statement: statementName,
        lineItem: toTrimmed(lineItem?.lineItem),
        "Line Item": toTrimmed(lineItem?.lineItem),
        classification: toTrimmed(lineItem?.sopMetric),
        classificationSource: toTrimmed(lineItem?.classificationSource) === 'manual'
          ? 'manual'
          : (toTrimmed(lineItem?.sopMetric) ? 'auto' : ''),
        aiConfidence:
          typeof lineItem?.aiConfidence === "number"
            ? lineItem.aiConfidence
            : null,
      };
      columns.forEach((column) => {
        row[column] = values?.[column] ?? "";
      });
      rows.push(row);
      rowCounter += 1;
    });
  });

  return rows;
};

function AnnualReportExtraction() {
  const { authUser } = useContext(StateContext);
  const [searchParams] = useSearchParams();
  const [uploadMode, setUploadMode] = useState('statement_upload');
  const [pdfName, setPdfName] = useState('');
  const [pdfBase64, setPdfBase64] = useState('');
  const [statementUploadImagesByStatement, setStatementUploadImagesByStatement] = useState(
    () => buildEmptyStatementUploadImages()
  );
  const [lineItems, setLineItems] = useState([]);
  const [activeStatement, setActiveStatement] = useState('');
  const [valueColumns, setValueColumns] = useState([]);
  const [sopSummary, setSopSummary] = useState(() => buildEmptySopSummary());
  const [pausedSopStatements, setPausedSopStatements] = useState(() => buildEmptyPausedSopStatementMap());
  const [candidateMetrics, setCandidateMetrics] = useState([]);
  const [manualSopEntries, setManualSopEntries] = useState({});
  const [sopMetadata, setSopMetadata] = useState(() => ({ latestColumns: {} }));
  const [expandedSopMetrics, setExpandedSopMetrics] = useState({});
  const [breakdownDrafts, setBreakdownDrafts] = useState({});
  const [linkedSourceDrafts, setLinkedSourceDrafts] = useState({});
  const [qcComplete, setQcComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualRow, setManualRow] = useState({ statement: '', lineItem: '', values: {} });
  const [verifiedStatements, setVerifiedStatements] = useState({});
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('overview');
  const [pdfZoom, setPdfZoom] = useState(1);
  const [statementMultiplierApplied, setStatementMultiplierApplied] = useState({});
  const [editingSopMetric, setEditingSopMetric] = useState(null);
  const [sopEditDraft, setSopEditDraft] = useState(() => buildEmptySopEditDraft());
  const [sopSearchTerm, setSopSearchTerm] = useState('');
  const [sopStatusFilter, setSopStatusFilter] = useState('all');
  const [sopStatementFilter, setSopStatementFilter] = useState('all');
  const [statementEditorFullscreen, setStatementEditorFullscreen] = useState(false);
  const [sopSummaryFullscreen, setSopSummaryFullscreen] = useState(false);
  const [cashFlowScaleKey, setCashFlowScaleKey] = useState('none');
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [stockOptions, setStockOptions] = useState([]);
  const [stockSearchTerm, setStockSearchTerm] = useState('');
  const [stockSearchModalOpen, setStockSearchModalOpen] = useState(false);
  const [selectedStockKeys, setSelectedStockKeys] = useState<string[]>([]);
  const setSelectedTicker = (val: string) => {
    const ticker = String(val || '').trim().toUpperCase();
    if (!ticker) {
      setSelectedStockKeys([]);
      return;
    }
    const matchedStock = stockOptions.find(
      (stock) => String(stock?.ticker_symbol || '').trim().toUpperCase() === ticker
    );
    setSelectedStockKeys([matchedStock ? getStockSelectionKey(matchedStock) : `ticker:${ticker}:`]);
  };
  const [fiscalYear, setFiscalYear] = useState('');
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [selectedQuarterDate, setSelectedQuarterDate] = useState('');
  const [applyProfitLossQ4CumulativeAdjustment, setApplyProfitLossQ4CumulativeAdjustment] = useState(false);
  const [convertCurrency, setConvertCurrency] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [targetCurrency, setTargetCurrency] = useState('AED');
  const [showQuarterSuggestions, setShowQuarterSuggestions] = useState(false);
  const [savedFalconQuarters, setSavedFalconQuarters] = useState([]);
  const [workflowHistoryRows, setWorkflowHistoryRows] = useState([]);
  const [loadingWorkflowHistory, setLoadingWorkflowHistory] = useState(false);
  const [workflowActionBusy, setWorkflowActionBusy] = useState(false);
  const [workflowActionComment, setWorkflowActionComment] = useState('');
  const [loadingQuarterOptions, setLoadingQuarterOptions] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [hydratingWorkflowVersion, setHydratingWorkflowVersion] = useState(false);
  const loadingIntervalRef = useRef(null);
  const printedLogsRef = useRef(new Set());
  const selectAllCheckboxRef = useRef(null);
  const statementTableWrapperRef = useRef(null);
  const statementTopScrollbarRef = useRef(null);
  const statementTopScrollbarInnerRef = useRef(null);
  const statementScrollSyncRef = useRef(false);
  const hydratedWorkflowRef = useRef('');
  const [showStatementTopScrollbar, setShowStatementTopScrollbar] = useState(false);

  const buildAuthHeaders = useCallback(() => {
    const headers = {};
    const authToken = (
      localStorage.getItem('token')
      || localStorage.getItem('authToken')
      || sessionStorage.getItem('token')
      || sessionStorage.getItem('authToken')
      || authUser?.token
      || authUser?.accessToken
      || ''
    );
    const authorization = toAuthorizationHeader(authToken);
    if (authorization) {
      headers.Authorization = authorization;
    }
    const userId = authUser?.id || authUser?.userId;
    if (userId) {
      headers.user_id = String(userId);
      headers["x-user-id"] = String(userId);
    }
    if (authUser?.role_id) {
      headers.role_id = String(authUser.role_id);
      headers["x-role-id"] = String(authUser.role_id);
    }
    if (authUser?.role_name) {
      headers.role_name = String(authUser.role_name);
      headers["x-role-name"] = String(authUser.role_name);
    }
    if (authUser?.full_name || authUser?.name || authUser?.fullName) {
      headers["x-full-name"] = String(authUser.full_name || authUser.name || authUser.fullName);
    }
    if (Array.isArray(authUser?.allowed)) {
      headers["x-allowed-keys"] = authUser.allowed.join(",");
    }
    if (authUser?.email) {
      headers["x-user-email"] = String(authUser.email);
    }
    if (authUser?.username) {
      headers["x-username"] = String(authUser.username);
    }
    return headers;
  }, [
    authUser?.accessToken,
    authUser?.allowed,
    authUser?.full_name,
    authUser?.id,
    authUser?.name,
    authUser?.role_id,
    authUser?.role_name,
    authUser?.token,
    authUser?.userId,
    authUser?.email,
    authUser?.username,
  ]);

  const authHeaders = useMemo(() => buildAuthHeaders(), [buildAuthHeaders]);
  const hasPausedSopRecalculation = useMemo(
    () => Object.keys(pausedSopStatements || {}).length > 0,
    [pausedSopStatements]
  );

  const resetExtractionWorkspace = useCallback(() => {
    setError('');
    setPdfBase64('');
    setPdfName('');
    setLineItems([]);
    setValueColumns([]);
    setSopSummary(buildEmptySopSummary());
    setPausedSopStatements(buildEmptyPausedSopStatementMap());
    setSopMetadata({ latestColumns: {} });
    setManualSopEntries({});
    setExpandedSopMetrics({});
    setBreakdownDrafts({});
    setEditingSopMetric(null);
    setSopEditDraft(buildEmptySopEditDraft());
    setVerifiedStatements({});
    setQcComplete(false);
    setJobId('');
    setJobStatus('');
    setActiveWorkspaceTab('overview');
    setStatementMultiplierApplied({});
    setCandidateMetrics([]);
    setShowManualEntry(false);
    setManualRow({ statement: '', lineItem: '', values: {} });
    setSelectedRowIds(new Set());
    setSopSearchTerm('');
    setSopStatusFilter('all');
    setSopStatementFilter('all');
    setCashFlowScaleKey('none');
    setStatus(null);
    setPdfZoom(1);
  }, []);

  const handleUploadModeChange = useCallback((nextMode) => {
    if (!nextMode || nextMode === uploadMode) {
      return;
    }
    resetExtractionWorkspace();
    setUploadMode(nextMode);
    setStatementUploadImagesByStatement(buildEmptyStatementUploadImages());
  }, [resetExtractionWorkspace, uploadMode]);

  const readFileAsDataUrl = useCallback((sourceFile) => (
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Failed to read file: ${sourceFile?.name || 'unknown file'}.`));
      reader.readAsDataURL(sourceFile);
    })
  ), []);

  const syncStatementScrollbarMetrics = useCallback(() => {
    const wrapper = statementTableWrapperRef.current;
    const topScrollbar = statementTopScrollbarRef.current;
    const topScrollbarInner = statementTopScrollbarInnerRef.current;

    if (!wrapper || !topScrollbar || !topScrollbarInner) {
      setShowStatementTopScrollbar(false);
      return;
    }

    const scrollWidth = Math.ceil(wrapper.scrollWidth || 0);
    const clientWidth = Math.ceil(wrapper.clientWidth || 0);
    topScrollbarInner.style.width = `${scrollWidth}px`;

    const hasOverflow = scrollWidth > clientWidth + 1;
    setShowStatementTopScrollbar(hasOverflow);

    if (!hasOverflow) {
      topScrollbar.scrollLeft = 0;
      wrapper.scrollLeft = 0;
      return;
    }

    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    const nextScrollLeft = Math.min(wrapper.scrollLeft, maxScrollLeft);
    if (wrapper.scrollLeft !== nextScrollLeft) {
      wrapper.scrollLeft = nextScrollLeft;
    }
    if (topScrollbar.scrollLeft !== nextScrollLeft) {
      topScrollbar.scrollLeft = nextScrollLeft;
    }
  }, []);

  const handleStatementTopScrollbarScroll = useCallback((event) => {
    const wrapper = statementTableWrapperRef.current;
    if (!wrapper || statementScrollSyncRef.current) {
      return;
    }

    statementScrollSyncRef.current = true;
    wrapper.scrollLeft = event.currentTarget.scrollLeft;
    statementScrollSyncRef.current = false;
  }, []);

  const handleStatementTableScroll = useCallback((event) => {
    const topScrollbar = statementTopScrollbarRef.current;
    if (!topScrollbar || statementScrollSyncRef.current) {
      return;
    }

    statementScrollSyncRef.current = true;
    topScrollbar.scrollLeft = event.currentTarget.scrollLeft;
    statementScrollSyncRef.current = false;
  }, []);

  useEffect(() => {
    const hasFullscreenPanel = statementEditorFullscreen || sopSummaryFullscreen;
    if (!hasFullscreenPanel || typeof document === 'undefined') {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setStatementEditorFullscreen(false);
        setSopSummaryFullscreen(false);
      }
    };

    document.body.classList.add('falcon-statement-editor-fullscreen-open');
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.classList.remove('falcon-statement-editor-fullscreen-open');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [statementEditorFullscreen, sopSummaryFullscreen]);

  useEffect(() => {
    if (!loading) {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const messages = [
      'Uploading PDF to the server...',
      'Extracting structured text from the document...',
      'Rebuilding financial tables...',
      'Preparing line items for review...',
    ];

    let index = 0;
    setStatus({
      type: 'info',
      message: `${messages[index]} This can take 2-3 minutes.`,
    });

    const intervalId = window.setInterval(() => {
      index = (index + 1) % messages.length;
      setStatus({
        type: 'info',
        message: `${messages[index]} Still working...`,
      });
    }, 20000);

    loadingIntervalRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
      loadingIntervalRef.current = null;
    };
  }, [loading]);

  useEffect(() => {
    if (!jobId) {
      printedLogsRef.current.clear();
      return;
    }

    let cancelled = false;

    const pollJob = async () => {
      try {
        const headers = buildAuthHeaders();

        const response = await axios.get(
          `${API_BASE_URL}/api/annual-report/status/${jobId}`,
          { headers }
        );
        if (cancelled) return;

        const status = response?.data?.status || '';
        const logs = response?.data?.logs || [];

        logs.forEach((logMsg) => {
          if (!printedLogsRef.current.has(logMsg)) {
            if (typeof logMsg === 'string' && logMsg.startsWith('[SUCCESS]')) {
              console.log(`%c[Falcon AI] ${logMsg}`, 'color:#16a34a;font-weight:600');
            } else if (typeof logMsg === 'string' && logMsg.startsWith('[WARNING]')) {
              console.warn(`[Falcon AI] ${logMsg}`);
            } else if (typeof logMsg === 'string' && logMsg.startsWith('[ERROR]')) {
              console.error(`[Falcon AI] ${logMsg}`);
            } else {
              console.log(`[Falcon AI] ${logMsg}`);
            }
            printedLogsRef.current.add(logMsg);
          }
        });

        setJobStatus(status);
        if (status === 'done') {
          const result = response?.data?.result;
          const statements = result?.extractionMeta?.statements || [];
          console.group('%c[Falcon AI] Background job completed', 'color:#16a34a;font-weight:bold');
          console.log('Job ID:', jobId);
          if (statements.length) {
            statements.forEach((item) => {
              if (item?.extracted) {
                console.log(
                  `%c✅ ${item.statement}: ${item.rows} rows x ${item.columns} columns`,
                  'color:#16a34a'
                );
              } else {
                console.warn(`❌ ${item?.statement}: rows=${item?.rows}, columns=${item?.columns}`);
              }
            });
          }
          console.log('Line items:', (result?.lineItems || []).length);
          console.log('Value columns:', result?.valueColumns || []);
          console.groupEnd();
          setJobId('');
          setJobStatus('');
          setLoading(false);
          applyExtractionResponse(result, { quarter: Number(selectedQuarter?.quarter || 0) });
        } else if (status === 'error') {
          const message =
            response?.data?.error || 'Background processing failed. Please try again.';
          setJobId('');
          setJobStatus('');
          setLoading(false);
          setError(message);
          setStatus({ type: 'error', message });
        }
      } catch (pollError) {
        if (cancelled) return;
        console.error('[annual-report] Polling failed', pollError);
      }
    };

    pollJob();
    const intervalId = window.setInterval(pollJob, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [jobId, buildAuthHeaders]);

  useEffect(() => {
    syncStatementScrollbarMetrics();

    const wrapper = statementTableWrapperRef.current;
    const table = wrapper?.querySelector('table');

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', syncStatementScrollbarMetrics);
    }

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        syncStatementScrollbarMetrics();
      });
      if (wrapper) {
        observer.observe(wrapper);
      }
      if (table) {
        observer.observe(table);
      }
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', syncStatementScrollbarMetrics);
      }
      if (observer) {
        observer.disconnect();
      }
    };
  }, [
    activeStatement,
    activeWorkspaceTab,
    lineItems,
    statementEditorFullscreen,
    valueColumns,
    showManualEntry,
    syncStatementScrollbarMetrics,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchStocks = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/all-stocks`, {
          headers: authHeaders,
        });
        if (cancelled) {
          return;
        }

        const sections = response?.data?.data || response?.data || [];
        const flattened = Array.isArray(sections)
          ? sections.flatMap((section) => section?.options || [])
          : [];
        const deduped = new Map();

        flattened.forEach((stock) => {
          const ticker = String(stock?.ticker_symbol || '').trim().toUpperCase();
          const stockKey = getStockSelectionKey(stock);
          if (!ticker || !stockKey || deduped.has(stockKey)) {
            return;
          }
          deduped.set(stockKey, {
            id: stock?.id ?? null,
            ticker_symbol: ticker,
            company_name: stock?.company_name || ticker,
            company_quarter_id: stock?.company_quarter_id ?? null,
            company_start_date: stock?.company_start_date ?? null,
            company_logo: stock?.company_logo || '',
            market_name: stock?.market_name || '',
            short_name: stock?.short_name || '',
            status: stock?.status ?? null,
          });
        });

        const options = Array.from(deduped.values())
          .filter((stock) => (
            stock?.ticker_symbol
            && stock?.id
          ))
          .sort((a, b) => (
            `${a.company_name} (${a.ticker_symbol})`.localeCompare(
              `${b.company_name} (${b.ticker_symbol})`
            )
          ));

        setStockOptions(options);
      } catch (fetchError) {
        console.error('[Falcon AI] Failed to load stock options', fetchError);
      }
    };

    fetchStocks();

    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  const totalRows = lineItems.length;
  const stockAutocompleteOptions = useMemo(
    () =>
      (stockOptions || []).map((stock) => ({
        ...stock,
        displayLabel: `${stock?.company_name || stock?.ticker_symbol} (${stock?.ticker_symbol})`,
      })),
    [stockOptions]
  );
  const filteredStockSearchResults = useMemo(() => {
    const term = String(stockSearchTerm || '').trim().toLowerCase();
    const all = Array.isArray(stockAutocompleteOptions) ? stockAutocompleteOptions : [];
    if (!term) {
      return all;
    }
    const startsWith = all.filter((stock) => {
      const ticker = String(stock?.ticker_symbol || '').toLowerCase();
      const company = String(stock?.company_name || '').toLowerCase();
      return ticker.startsWith(term) || company.startsWith(term);
    });
    const contains = all.filter((stock) => {
      const ticker = String(stock?.ticker_symbol || '').toLowerCase();
      const company = String(stock?.company_name || '').toLowerCase();
      const market = String(stock?.market_name || '').toLowerCase();
      return (ticker.includes(term) || company.includes(term) || market.includes(term))
        && !startsWith.some((entry) => getStockSelectionKey(entry) === getStockSelectionKey(stock));
    });
    return [...startsWith, ...contains];
  }, [stockAutocompleteOptions, stockSearchTerm]);

  const handleStockSuggestionSelect = useCallback((stock) => {
    if (!stock) {
      return;
    }
    const stockKey = getStockSelectionKey(stock);
    if (!stockKey) {
      return;
    }
    setSelectedStockKeys((prev) => {
      if (prev.includes(stockKey)) {
        return prev;
      }
      if (prev.length >= 2) {
        return prev;
      }
      return [...prev, stockKey];
    });
    setStockSearchTerm('');
    setStockSearchModalOpen(false);
  }, []);
  const selectedStocks = useMemo(
    () => {
      return selectedStockKeys
        .map((key) => {
          if (String(key || '').startsWith('ticker:')) {
            const ticker = String(key).split(':')[1] || '';
            return stockOptions.find(
              (stock) => String(stock?.ticker_symbol || '').trim().toUpperCase() === ticker
            );
          }
          return stockOptions.find((stock) => getStockSelectionKey(stock) === key);
        })
        .filter(Boolean);
    },
    [stockOptions, selectedStockKeys],
  );
  const selectedStock = selectedStocks[0] || null;
  const selectedTickers = useMemo(
    () => selectedStocks.map((stock) => stock?.ticker_symbol).filter(Boolean),
    [selectedStocks]
  );
  const selectedTicker = selectedTickers[0] || (
    String(selectedStockKeys[0] || '').startsWith('ticker:')
      ? String(selectedStockKeys[0]).split(':')[1] || ''
      : ''
  );
  useEffect(() => {
    if (!selectedTicker) {
      return;
    }
    const match = stockAutocompleteOptions.find(
      (stock) => String(stock?.ticker_symbol || '') === String(selectedTicker),
    );
    if (!match) {
      return;
    }
    const displayLabel = match.displayLabel;
    setStockSearchTerm((current) => {
      const currentText = String(current || '').trim();
      if (!currentText || currentText.toUpperCase() === String(selectedTicker).toUpperCase()) {
        return displayLabel;
      }
      return current;
    });
  }, [selectedTicker, stockAutocompleteOptions]);
  const validFiscalYear = isValidFiscalYear(fiscalYear) ? Number(fiscalYear) : null;
  const savedFalconQuarterMap = useMemo(() => {
    const map = new Map();
    (savedFalconQuarters || []).forEach((row) => {
      const quarterDate = String(row?.quarter_date || '').trim();
      if (!quarterDate) {
        return;
      }
      map.set(quarterDate, row);
    });
    return map;
  }, [savedFalconQuarters]);
  const selectedQuarter = useMemo(() => {
    const exactMatch = quarterOptions.find((option) => option.date === selectedQuarterDate) || null;
    if (exactMatch) {
      return exactMatch;
    }
    if (!isIsoDateString(selectedQuarterDate)) {
      return null;
    }

    const fallbackQuarterNumber = inferCalendarQuarterFromDate(selectedQuarterDate) || 1;
    const fallbackYear = Number(selectedQuarterDate.slice(0, 4)) || validFiscalYear || 0;
    const savedRow = savedFalconQuarterMap.get(selectedQuarterDate);

    return {
      year: fallbackYear || validFiscalYear || null,
      quarter: fallbackQuarterNumber,
      date: selectedQuarterDate,
      label: `Q${fallbackQuarterNumber} | ${selectedQuarterDate}`,
      saved: Boolean(savedRow),
      finalizedAt: savedRow?.finalized_at || null,
    };
  }, [quarterOptions, selectedQuarterDate, savedFalconQuarterMap, validFiscalYear]);
  const quarterSelectDisabled = loading || savingReview || !selectedStock?.id || !validFiscalYear || loadingQuarterOptions;
  const quarterPlaceholderLabel = loadingQuarterOptions
    ? 'Loading quarters...'
    : !selectedStock?.id || !validFiscalYear
      ? 'Select stock and fiscal year first'
      : 'Select target quarter';
  const showProfitLossQ4CumulativeAdjustment = Number(selectedQuarter?.quarter || 0) > 1;
  const isSeniorReviewer = useMemo(() => {
    const authId = Number(authUser?.id || authUser?.userId || 0);
    return authId === 1;
  }, [authUser?.id, authUser?.userId]);

  const [extractionExchangeRate, setExtractionExchangeRate] = useState(null);
  const [extractionExchangeRateLoading, setExtractionExchangeRateLoading] = useState(false);
  const [extractionExchangeRateDate, setExtractionExchangeRateDate] = useState('');

  useEffect(() => {
    if (!convertCurrency || !baseCurrency || !targetCurrency || baseCurrency === targetCurrency) {
      setExtractionExchangeRate(1);
      return;
    }
    const qDate = String(selectedQuarter?.date || selectedQuarterDate || '').trim();
    if (!qDate) {
      setExtractionExchangeRate(null);
      setExtractionExchangeRateDate('');
      return;
    }
    let isMounted = true;
    setExtractionExchangeRateLoading(true);
    axios
      .get(`${API_BASE_URL}/api/annual-report/exchange-rate`, {
        headers: authHeaders,
        params: {
          baseCurrency,
          targetCurrency,
          date: qDate,
        },
      })
      .then((res) => {
        if (isMounted) {
          const rateVal = Number(res?.data?.rate);
          setExtractionExchangeRate(Number.isFinite(rateVal) && rateVal > 0 ? rateVal : null);
          setExtractionExchangeRateDate(res?.data?.date || qDate);
        }
      })
      .catch((err) => {
        console.warn('[Falcon AI] Exchange rate fetch failed:', err);
        if (isMounted) {
          setExtractionExchangeRate(null);
          setExtractionExchangeRateDate(qDate);
        }
      })
      .finally(() => {
        if (isMounted) setExtractionExchangeRateLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [authHeaders, convertCurrency, baseCurrency, targetCurrency, selectedQuarter?.date, selectedQuarterDate]);

  useEffect(() => {
    if (selectedQuarter && !showProfitLossQ4CumulativeAdjustment && applyProfitLossQ4CumulativeAdjustment) {
      setApplyProfitLossQ4CumulativeAdjustment(false);
    }
  }, [selectedQuarter, showProfitLossQ4CumulativeAdjustment, applyProfitLossQ4CumulativeAdjustment]);

  const sopAdjustmentRequestId = useRef(0);
  const fetchSopAdjustments = useCallback(async (overrideAdjustment?: boolean) => {
    const requestId = ++sopAdjustmentRequestId.current;
    if (
      !selectedStock?.id ||
      !validFiscalYear ||
      !selectedQuarter?.quarter ||
      !sopSummary ||
      !sopSummary.length ||
      loading ||
      savingReview
    ) {
      return;
    }

    const activeAdjustment = overrideAdjustment !== undefined
      ? overrideAdjustment
      : applyProfitLossQ4CumulativeAdjustment;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/adjustments`,
        {
          stock_id: selectedStock.id,
          stock_ids: selectedStocks.map((s) => s.id),
          fiscal_year: validFiscalYear,
          selected_quarter: {
            quarter: selectedQuarter.quarter,
            date: selectedQuarter.date,
            year: selectedQuarter.year,
            label: selectedQuarter.label,
          },
          sop_summary: sopSummary,
          apply_profit_loss_q4_cumulative_adjustment: activeAdjustment,
          currency_conversion: {
            enabled: convertCurrency,
            baseCurrency: baseCurrency,
            targetCurrency: targetCurrency,
          },
        },
        {
          headers: authHeaders,
        }
      );

      if (requestId !== sopAdjustmentRequestId.current) return;
      const updatedSop = Array.isArray(response?.data?.sopSummary) ? response.data.sopSummary : [];
      if (updatedSop.length > 0) {
        setSopSummary((current) => {
          const currentRows = Array.isArray(current) ? current : [];
          return currentRows.map((entry) => {
            const match = updatedSop.find((u: any) => normaliseMetricKey(u.metric) === normaliseMetricKey(entry.metric));
            if (match) {
              return {
                ...entry,
                cashFlowQuarterAdjustment: match.cashFlowQuarterAdjustment || null,
                profitLossQuarterAdjustment: match.profitLossQuarterAdjustment || null,
              };
            }
            return entry;
          });
        });
      }
    } catch (err) {
      if (requestId !== sopAdjustmentRequestId.current) return;
      console.error("[Falcon AI] Failed to fetch dynamic SOP adjustments:", err);
      setStatus({
        type: 'error',
        message: err?.response?.data?.details || 'Unable to calculate quarterly adjustments. Refresh the calculation before saving.',
      });
    }
  }, [
    selectedStock?.id,
    validFiscalYear,
    selectedQuarter?.quarter,
    selectedQuarter?.date,
    selectedQuarter?.year,
    selectedQuarter?.label,
    sopSummary?.length,
    applyProfitLossQ4CumulativeAdjustment,
    convertCurrency,
    baseCurrency,
    targetCurrency,
    loading,
    savingReview,
    authHeaders,
  ]);

  useEffect(() => {
    fetchSopAdjustments();
    return () => { sopAdjustmentRequestId.current += 1; };
  }, [
    selectedStock?.id,
    validFiscalYear,
    selectedQuarter?.quarter,
    selectedQuarter?.date,
    sopSummary?.length,
    applyProfitLossQ4CumulativeAdjustment,
    convertCurrency,
    baseCurrency,
    targetCurrency,
    loading,
    savingReview,
    authHeaders,
  ]);

  const selectedQuarterWorkflow = useMemo(() => {
    if (!selectedQuarter?.date) {
      return null;
    }
    return (workflowHistoryRows || []).find(
      (row) => String(row?.quarter_date || '').trim() === String(selectedQuarter.date).trim(),
    ) || null;
  }, [selectedQuarter?.date, workflowHistoryRows]);
  const selectedQuarterWorkflowCurrentVersion = useMemo(() => {
    if (!selectedQuarterWorkflow) {
      return null;
    }
    const versions = Array.isArray(selectedQuarterWorkflow?.versions)
      ? selectedQuarterWorkflow.versions
      : [];
    return versions.find((version) => Number(version?.is_current || 0) === 1) || versions[0] || null;
  }, [selectedQuarterWorkflow]);
  const extractionPrerequisitesReady = Boolean(selectedStock?.id)
    && Boolean(selectedTicker)
    && Boolean(validFiscalYear)
    && Boolean(selectedQuarter);

  const loadQuarterContext = useCallback(async () => {
    if (!selectedStock?.id || !validFiscalYear) {
    setQuarterOptions([]);
    setSavedFalconQuarters([]);
    setWorkflowHistoryRows([]);
    setWorkflowActionComment('');
    setSelectedQuarterDate('');
    setShowQuarterSuggestions(false);
      return;
    }

    setLoadingQuarterOptions(true);
    let quarterOptionsData = buildFallbackQuarterOptions(validFiscalYear);
    let savedRows = [];

    try {
      try {
        const quarterResponse = await axios.get(
          `${API_BASE_URL}/api/annual-report/quarters`,
          {
            headers: authHeaders,
            params: {
              stock_id: selectedStock.id,
              fiscal_year: validFiscalYear,
            },
          },
        );

        const quarterRows = Array.isArray(quarterResponse?.data?.rows)
          ? quarterResponse.data.rows
          : [];
        const mappedOptions = quarterRows
          .map((row, index) => {
            const date = String(row?.date || row?.quarter_date || '').trim();
            if (!isIsoDateString(date)) {
              return null;
            }
            const quarterNumber = Number(row?.quarter || row?.quarter_number || index + 1);
            const safeQuarter = Number.isInteger(quarterNumber) && quarterNumber >= 1 && quarterNumber <= 4
              ? quarterNumber
              : index + 1;
            return {
              year: validFiscalYear,
              quarter: safeQuarter,
              date,
              label: `Q${safeQuarter} | ${date}`,
              saved: false,
              finalizedAt: null,
            };
          })
          .filter(Boolean);

        if (mappedOptions.length) {
          quarterOptionsData = mappedOptions;
        }
      } catch (quarterError) {
        console.warn('[Falcon AI] Quarter calendar lookup failed, using fallback calendar quarters.', quarterError);
      }

      try {
        const savedResponse = await axios.get(
          `${API_BASE_URL}/api/annual-report/finalized-quarters`,
          {
            headers: authHeaders,
            params: {
              stock_id: selectedStock.id,
              fiscal_year: validFiscalYear,
            },
          },
        );
        savedRows = Array.isArray(savedResponse?.data?.rows) ? savedResponse.data.rows : [];
      } catch (savedError) {
        console.warn('[Falcon AI] Failed to load saved Falcon quarter metadata.', savedError);
      }

      const savedMap = new Map(
        savedRows.map((row) => [String(row?.quarter_date || '').trim(), row]),
      );
      const hydratedOptions = quarterOptionsData.map((option) => {
        const savedRow = savedMap.get(option.date) || null;
        return {
          ...option,
          saved: Boolean(savedRow),
          finalizedAt: savedRow?.finalized_at || null,
        };
      });

      setQuarterOptions(hydratedOptions);
      setSavedFalconQuarters(savedRows);
      setSelectedQuarterDate((current) => {
        if (!current) {
          return '';
        }
        if (hydratedOptions.some((option) => option.date === current)) {
          return current;
        }
        return isIsoDateString(current) ? current : '';
      });
      if (!hydratedOptions.length) {
        setShowQuarterSuggestions(false);
      }
    } catch (quarterError) {
      console.error('[Falcon AI] Failed to load quarter options', quarterError);
      setQuarterOptions([]);
      setSavedFalconQuarters([]);
      setSelectedQuarterDate('');
      setShowQuarterSuggestions(false);
    } finally {
      setLoadingQuarterOptions(false);
    }
  }, [
    authHeaders,
    selectedStock?.id,
    validFiscalYear,
  ]);

  useEffect(() => {
    loadQuarterContext();
  }, [loadQuarterContext]);

  const loadWorkflowHistory = useCallback(async () => {
    if (!selectedStock?.id || !validFiscalYear || !selectedQuarter?.date) {
      setWorkflowHistoryRows([]);
      return;
    }
    setLoadingWorkflowHistory(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/annual-report/workflows/history`,
        {
          headers: authHeaders,
          params: {
            stock_id: selectedStock.id,
            fiscal_year: validFiscalYear,
            quarter_date: selectedQuarter.date,
            limit: 20,
          },
        },
      );
      const rows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      setWorkflowHistoryRows(rows);
    } catch (workflowHistoryError) {
      console.warn('[Falcon AI] Failed to load workflow history.', workflowHistoryError);
      setWorkflowHistoryRows([]);
    } finally {
      setLoadingWorkflowHistory(false);
    }
  }, [
    authHeaders,
    selectedStock?.id,
    selectedQuarter?.date,
    validFiscalYear,
  ]);

  useEffect(() => {
    loadWorkflowHistory();
  }, [loadWorkflowHistory]);
  const sopMetricOptions = useMemo(() => {
    const merged = new Set(SOP_METRICS);
    if (Array.isArray(candidateMetrics)) {
      candidateMetrics.forEach((metric) => {
        const name = typeof metric === 'string' ? metric.trim() : '';
        if (name) {
          merged.add(name);
        }
      });
    }
    lineItems.forEach((item) => {
      const name = typeof item?.classification === 'string' ? item.classification.trim() : '';
      if (name) {
        merged.add(name);
      }
    });
    return Array.from(merged);
  }, [candidateMetrics, lineItems]);

  const statements = useMemo(() => (
    Array.from(new Set(lineItems.map((item) => item.statement))).filter(Boolean)
  ), [lineItems]);

  const lineItemSuggestionsByStatement = useMemo(() => {
    const map = new Map();
    const addValue = (key, value) => {
      if (!value) {
        return;
      }
      if (!map.has(key)) {
        map.set(key, new Set());
      }
      map.get(key).add(value);
    };
    lineItems.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const statementName = (item.statement || '').toString().trim();
      const lineItemName = (item.lineItem || item['Line Item'] || '').toString().trim();
      if (!lineItemName) {
        return;
      }
      const statementKey = statementName || '__without_statement__';
      addValue('__all__', lineItemName);
      addValue(statementKey, lineItemName);
    });
    return map;
  }, [lineItems]);

  const getLineItemSuggestions = useCallback((statementName) => {
    if (!statementName) {
      return Array.from(lineItemSuggestionsByStatement.get('__all__') || []);
    }
    const key = statementName.toString().trim() || '__without_statement__';
    const forStatement = lineItemSuggestionsByStatement.get(key);
    if (forStatement && forStatement.size) {
      return Array.from(forStatement);
    }
    return Array.from(lineItemSuggestionsByStatement.get('__all__') || []);
  }, [lineItemSuggestionsByStatement]);

  const statementSuggestions = useMemo(() => {
    const collected = new Set(statements);
    Object.values(manualSopEntries || {}).forEach((entries) => {
      if (!Array.isArray(entries)) {
        return;
      }
      entries.forEach((entry) => {
        if (entry?.statement) {
          collected.add(entry.statement);
        }
        if (Array.isArray(entry?.calculation)) {
          entry.calculation.forEach((step) => {
            if (step?.statement) {
              collected.add(step.statement);
            }
          });
        }
      });
    });
    return Array.from(collected).filter(Boolean);
  }, [statements, manualSopEntries]);

  const latestColumnHints = useMemo(() => {
    const latestColumnsInput = (sopMetadata && typeof sopMetadata === 'object')
      ? sopMetadata.latestColumns || {}
      : {};
    const next = new Map();
    Object.entries(latestColumnsInput).forEach(([statementName, columnName]) => {
      const statementKey = normaliseStatementLookupKey(statementName);
      const trimmedColumn = toTrimmed(columnName);
      if (!statementKey || !trimmedColumn) {
        return;
      }
      if (!next.has(statementKey)) {
        next.set(statementKey, trimmedColumn);
      }
    });
    return next;
  }, [sopMetadata]);

  const strictPreferredValueColumns = useMemo(
    () => rankPreferredStatementColumns(valueColumns),
    [valueColumns]
  );

  const findMatchingColumnName = useCallback((row, columnName) => {
    const trimmed = toTrimmed(columnName);
    if (!trimmed) {
      return '';
    }
    const targetKey = normaliseKey(trimmed);
    const rowKeys = Object.keys(row || {});
    for (let idx = 0; idx < rowKeys.length; idx += 1) {
      const key = rowKeys[idx];
      if (normaliseKey(key) === targetKey) {
        return key;
      }
    }
    return '';
  }, []);

  const getLineItemValuePreview = useCallback((item, limit = 2) => {
    if (!item || typeof item !== 'object') {
      return '';
    }
    const statementKey = normaliseKey(item.statement);
    const candidates = [];
    const seen = new Set();

    const enqueue = (columnName) => {
      const resolved = findMatchingColumnName(item, columnName);
      if (!resolved || seen.has(resolved)) {
        return;
      }
      const rawValue = item?.[resolved];
      if (rawValue === null || typeof rawValue === 'undefined') {
        return;
      }
      const textValue = rawValue.toString().trim();
      if (!textValue) {
        return;
      }
      seen.add(resolved);
      candidates.push(`${resolved}: ${textValue}`);
    };

    enqueue(latestColumnHints.get(statementKey) || '');
    valueColumns.forEach((columnName) => enqueue(columnName));

    return candidates.slice(0, limit).join(' | ');
  }, [findMatchingColumnName, latestColumnHints, valueColumns]);

  const getAvailableSourceRows = useCallback((metricName, statementName) => {
    const targetMetricKey = normaliseMetricKey(metricName);
    const statementFilter = toTrimmed(statementName);
    return lineItems
      .filter((item) => {
        if (!item || !item.rowId) {
          return false;
        }
        const itemMetricKey = normaliseMetricKey(item.classification);
        if (itemMetricKey && itemMetricKey === targetMetricKey) {
          return false;
        }
        const itemStatement = toTrimmed(item.statement);
        if (statementFilter && itemStatement !== statementFilter) {
          return false;
        }
        return Boolean(toTrimmed(item.lineItem || item['Line Item']));
      })
      .sort((left, right) => {
        const leftStatement = toTrimmed(left.statement);
        const rightStatement = toTrimmed(right.statement);
        if (leftStatement !== rightStatement) {
          return leftStatement.localeCompare(rightStatement);
        }
        const leftLineItem = toTrimmed(left.lineItem || left['Line Item']);
        const rightLineItem = toTrimmed(right.lineItem || right['Line Item']);
        return leftLineItem.localeCompare(rightLineItem);
      });
  }, [lineItems]);

  useEffect(() => {
    if (!statements.length) {
      setActiveStatement('');
      return;
    }
    setActiveStatement((current) => (
      statements.includes(current) ? current : statements[0]
    ));
  }, [statements]);

  useEffect(() => {
    if (!statements.length) {
      setStatementMultiplierApplied({});
      return;
    }
    setStatementMultiplierApplied((prev) => {
      const filtered = {};
      statements.forEach((statement) => {
        if (prev[statement]) {
          filtered[statement] = prev[statement];
        }
      });
      const prevKeys = Object.keys(prev).filter((key) => statements.includes(key));
      if (prevKeys.length === Object.keys(filtered).length) {
        const unchanged = statements.every((statement) => prev[statement] === filtered[statement]);
        if (unchanged) {
          return prev;
        }
      }
      return filtered;
    });
  }, [statements]);

  useEffect(() => {
    if (!statements.length) {
      setVerifiedStatements({});
      return;
    }

    setVerifiedStatements((prev) => {
      const next = {};
      statements.forEach((statement) => {
        next[statement] = Object.prototype.hasOwnProperty.call(prev, statement) ? prev[statement] : false;
      });
      return next;
    });
  }, [statements]);

  useEffect(() => {
    // Keep the legacy "must have PDF to open tabs" behavior only for PDF mode.
    // In statement-image mode `pdfBase64` is intentionally empty.
    if (uploadMode === 'pdf_upload' && !pdfBase64 && activeWorkspaceTab !== 'overview') {
      setActiveWorkspaceTab('overview');
    }
  }, [pdfBase64, activeWorkspaceTab, uploadMode]);

  const visibleItems = useMemo(() => {
    if (!activeStatement) {
      return [];
    }
    return lineItems.filter((item) => item.statement === activeStatement);
  }, [activeStatement, lineItems]);

  useEffect(() => {
    setSelectedRowIds((prev) => {
      if (!prev.size) {
        return prev;
      }
      const visibleSet = new Set(visibleItems.map((item) => item.rowId));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (visibleSet.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      if (!changed && next.size === prev.size) {
        return prev;
      }
      return next;
    });
  }, [visibleItems]);

  useEffect(() => {
    if (!selectAllCheckboxRef.current) {
      return;
    }
    const checkbox = selectAllCheckboxRef.current;
    const total = visibleItems.length;
    const selected = selectedRowIds.size;
    checkbox.indeterminate = selected > 0 && selected < total;
    checkbox.checked = total > 0 && selected === total;
  }, [selectedRowIds, visibleItems]);

  const statementTotalsMap = useMemo(() => {
    const map = {};
    lineItems.forEach((item) => {
      if (!item.statement) return;
      if (!map[item.statement]) {
        map[item.statement] = 0;
      }
      map[item.statement] += 1;
    });
    return map;
  }, [lineItems]);

  const totalStatements = statements.length;
  const pendingStatements = statements.filter((statement) => !verifiedStatements[statement]).length;
  const activeStatementVerified = activeStatement ? !!verifiedStatements[activeStatement] : false;

  const filterColumnsForStatement = useCallback((columns, statementName) => {
    if (!Array.isArray(columns) || !columns.length) {
      return columns;
    }
    const nonReferenceColumns = columns.filter((column) => !isReferenceColumnHeader(column));
    const baseColumns = nonReferenceColumns.length ? nonReferenceColumns : columns;
    if (!statementName) {
      return baseColumns;
    }
    const lowerStatement = statementName.toString().toLowerCase();
    if (!lowerStatement.includes('profit') && !lowerStatement.includes('loss')) {
      return baseColumns;
    }
    return baseColumns.filter((column) => {
      const text = (column ?? '').toString().toLowerCase();
      if (!text) return true;
      const hasChange = text.includes('change');
      const hasPercent = text.includes('%') || text.includes('percent') || text.includes('pct');
      return !(hasChange && hasPercent);
    });
  }, []);

  const statementValueColumns = useMemo(() => {
    const baseColumns = filterColumnsForStatement(valueColumns, activeStatement);
    if (!visibleItems.length) {
      return baseColumns;
    }
    const populatedColumns = baseColumns.filter((column) => (
      visibleItems.some((item) => {
        const raw = item[column];
        if (typeof raw === 'number') return true;
        return typeof raw === 'string' && raw.trim() !== '';
      })
    ));
    return populatedColumns.length ? populatedColumns : baseColumns;
  }, [valueColumns, visibleItems, activeStatement, filterColumnsForStatement]);

  const manualEntryColumns = useMemo(() => (
    filterColumnsForStatement(valueColumns, manualRow.statement || activeStatement || '')
  ), [valueColumns, manualRow.statement, activeStatement, filterColumnsForStatement]);

  const lineItemBreakdown = useMemo(() => {
    const grouped = {};
    lineItems.forEach((item) => {
      if (!item) {
        return;
      }
      const rawMetric = typeof item.classification === 'string' ? item.classification.trim() : '';
      if (!rawMetric) {
        return;
      }
      const metric = resolveSopMetricName(rawMetric);
      const values = {};
      valueColumns.forEach((column) => {
        const raw = item[column];
        if (raw === null || typeof raw === 'undefined') {
          return;
        }
        const text = raw.toString().trim();
        if (text) {
          values[column] = text;
        }
      });
      if (!grouped[metric]) {
        grouped[metric] = [];
      }
      grouped[metric].push({
        type: 'lineItem',
        rowId: item.rowId,
        statement: item.statement || '',
        lineItem: item.lineItem || item['Line Item'] || '',
        values,
      });
    });
    return grouped;
  }, [lineItems, valueColumns]);

  const lineItemLookup = useMemo(() => {
    const lookup = new Map();
    lineItems.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const statementKey = normaliseStatementLookupKey(item.statement);
      const lineLabelKey = normaliseKey(item.lineItem || item['Line Item']);
      if (!statementKey || !lineLabelKey) {
        return;
      }
      const key = `${statementKey}||${lineLabelKey}`;
      if (!lookup.has(key)) {
        lookup.set(key, []);
      }
      lookup.get(key).push(item);
    });
    return lookup;
  }, [lineItems]);

  const classificationSopSummary = useMemo(() => {
    if (!lineItems.length) {
      return {};
    }
    const pausedStatementKeys = new Set(Object.keys(pausedSopStatements || {}));
    const latestColumnsInput = (sopMetadata && typeof sopMetadata === 'object')
      ? sopMetadata.latestColumns || {}
      : {};
    const latestColumnByStatement = new Map();
    Object.entries(latestColumnsInput).forEach(([statementName, columnName]) => {
      const statementKey = normaliseStatementLookupKey(statementName);
      const trimmedColumn = toTrimmed(columnName);
      if (!statementKey || !trimmedColumn) {
        return;
      }
      if (!latestColumnByStatement.has(statementKey)) {
        latestColumnByStatement.set(statementKey, trimmedColumn);
      }
    });

    const metricIndex = new Map();
    SOP_METRICS.forEach((metric) => {
      metricIndex.set(normaliseMetricKey(metric), metric);
    });

    const resolveMetricName = (metric) => {
      const resolved = resolveSopMetricName(metric);
      const key = normaliseMetricKey(resolved);
      if (!key) {
        return '';
      }
      if (metricIndex.has(key)) {
        return metricIndex.get(key);
      }
      return resolved;
    };

    const findMatchingColumnName = (row, columnName) => {
      const trimmed = toTrimmed(columnName);
      if (!trimmed) {
        return '';
      }
      const targetKey = normaliseKey(trimmed);
      const rowKeys = Object.keys(row || {});
      for (let idx = 0; idx < rowKeys.length; idx += 1) {
        const key = rowKeys[idx];
        if (normaliseKey(key) === targetKey) {
          return key;
        }
      }
      return '';
    };

    const resolveRowValue = (row, columnHint) => {
      if (!row) {
        return null;
      }
      const strictColumns = strictPreferredValueColumns.length
        ? strictPreferredValueColumns
        : valueColumns;
      const isManualClassificationRow = normaliseKey(row?.classificationSource) === 'manual';

      // Always use one selected column (latest quarter/group column).
      // Do not fall back to another period when selected cell is "-" or non-numeric.
      let selectedColumn = '';
      if (columnHint) {
        selectedColumn = findMatchingColumnName(row, columnHint);
      }
      if (!selectedColumn) {
        for (let idx = 0; idx < strictColumns.length; idx += 1) {
          const matched = findMatchingColumnName(row, strictColumns[idx]);
          if (matched) {
            selectedColumn = matched;
            break;
          }
        }
      }

      if (!selectedColumn) {
        return null;
      }

      const raw = row[selectedColumn];
      if (raw === null || typeof raw === 'undefined') {
        return null;
      }
      const numeric = parseNumericValue(raw);
      if (numeric !== null) {
        return {
          numericValue: numeric,
          columnName: selectedColumn,
          rawValue: raw,
        };
      }

      // For operator-manual SOP assignments, allow fallback to another numeric
      // column on the same row when selected latest column is empty/non-numeric.
      if (isManualClassificationRow) {
        const seenColumns = new Set([selectedColumn]);
        const fallbackColumns = [];

      strictColumns.forEach((columnName) => {
        if (isReferenceColumnHeader(columnName)) {
          return;
        }
        const matched = findMatchingColumnName(row, columnName);
        if (!matched || seenColumns.has(matched)) {
          return;
        }
        seenColumns.add(matched);
          fallbackColumns.push(matched);
        });

        Object.keys(row || {}).forEach((columnName) => {
          if (isReferenceColumnHeader(columnName)) {
            return;
          }
          if (seenColumns.has(columnName)) {
            return;
          }
          seenColumns.add(columnName);
          fallbackColumns.push(columnName);
        });

        for (let idx = 0; idx < fallbackColumns.length; idx += 1) {
          const candidateColumn = fallbackColumns[idx];
          const candidateRaw = row[candidateColumn];
          if (candidateRaw === null || typeof candidateRaw === 'undefined') {
            continue;
          }
          const candidateNumeric = parseNumericValue(candidateRaw);
          if (candidateNumeric === null) {
            continue;
          }
          return {
            numericValue: candidateNumeric,
            columnName: candidateColumn,
            rawValue: candidateRaw,
          };
        }
      }

      return null;
    };

    const summary = {};

    lineItems.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const metricName = resolveMetricName(item.classification);
      if (!metricName) {
        return;
      }
      const statementName = item.statement || '';
      const statementKey = normaliseStatementLookupKey(statementName);
      if (statementKey && pausedStatementKeys.has(statementKey)) {
        return;
      }
      const columnHint = latestColumnByStatement.get(statementKey)
        || strictPreferredValueColumns[0]
        || '';
      const resolved = resolveRowValue(item, columnHint);
      if (!resolved) {
        return;
      }

      if (!summary[metricName]) {
        summary[metricName] = {
          total: 0,
          count: 0,
          columns: new Set(),
          statements: new Set(),
          sources: [],
          rows: [],
        };
      }

      summary[metricName].total += resolved.numericValue;
      summary[metricName].count += 1;
      if (resolved.columnName) {
        summary[metricName].columns.add(resolved.columnName);
      }
      if (statementName) {
        summary[metricName].statements.add(statementName);
      }
      const sourceLabel = item.lineItem || item['Line Item'] || 'Line item';
      summary[metricName].sources.push(sourceLabel);
      summary[metricName].rows.push({
        statement: statementName,
        column: resolved.columnName || '',
        sourceLine: sourceLabel,
        numericValue: resolved.numericValue,
        classificationSource: normaliseKey(item.classificationSource) === 'manual' ? 'manual' : 'auto',
      });
    });

    const result = {};
    Object.entries(summary).forEach(([metric, info]) => {
      if (!info || !info.count) {
        return;
      }
      const metricKey = normaliseMetricKey(metric);
      const hasManualContribution = Array.isArray(info.rows)
        && info.rows.some((row) => normaliseKey(row?.classificationSource) === 'manual');
      if (
        DIRECT_TOTAL_METRIC_KEYS.has(metricKey)
        && !hasManualContribution
        && Array.isArray(info.rows)
        && info.rows.length
      ) {
        let directBest = null;
        info.rows.forEach((row) => {
          const score = resolveDirectTotalScore(metric, row?.sourceLine);
          if (!score) {
            return;
          }
          if (!directBest || score > directBest.score) {
            directBest = {
              ...row,
              score,
            };
          }
        });
        if (directBest) {
          result[metric] = {
            value: formatSopDisplayValue(directBest.numericValue),
            statement: directBest.statement || '',
            column: directBest.column || '',
            sourceLine: directBest.sourceLine || '',
          };
          return;
        }
      }

      const columns = Array.from(info.columns);
      const statements = Array.from(info.statements);
      const column = columns.length === 1 ? columns[0] : columns.length ? 'Multiple' : '';
      const statement = statements.length === 1 ? statements[0] : statements.length ? 'Multiple' : '';
      const sourceLine = info.sources.length === 1
        ? info.sources[0]
        : `Sum of ${info.sources.join(', ')}`;
      result[metric] = {
        value: formatSopDisplayValue(info.total),
        statement,
        column,
        sourceLine,
      };
    });

    return result;
  }, [lineItems, valueColumns, sopMetadata, strictPreferredValueColumns, pausedSopStatements]);

  const manualSopOverrides = useMemo(() => {
    if (!manualSopEntries || !Object.keys(manualSopEntries).length) {
      return {};
    }

    const metricValueLookup = new Map();
    Object.entries(classificationSopSummary || {}).forEach(([metricName, entry]) => {
      const numeric = parseNumericValue(entry?.value);
      if (numeric === null) {
        return;
      }
      const resolvedMetricName = resolveSopMetricName(metricName) || toTrimmed(metricName);
      const metricPayload = {
        metricName: resolvedMetricName || metricName,
        numericValue: numeric,
        displayValue: entry?.value ?? '',
        columnName: toTrimmed(entry?.column),
      };
      const keys = [
        normaliseMetricKey(metricName),
        normaliseMetricKey(resolvedMetricName),
      ].filter(Boolean);
      keys.forEach((key) => {
        if (!metricValueLookup.has(key)) {
          metricValueLookup.set(key, metricPayload);
        }
      });
    });

    const latestColumnsInput = (sopMetadata && typeof sopMetadata === 'object')
      ? sopMetadata.latestColumns || {}
      : {};
    const latestColumnByStatement = new Map();
    Object.entries(latestColumnsInput).forEach(([statementName, columnName]) => {
      const normalisedStatement = normaliseStatementLookupKey(statementName);
      const trimmedColumn = toTrimmed(columnName);
      if (!normalisedStatement || !trimmedColumn) {
        return;
      }
      if (!latestColumnByStatement.has(normalisedStatement)) {
        latestColumnByStatement.set(normalisedStatement, trimmedColumn);
      }
    });

    const findMatchingColumnName = (row, columnName) => {
      const trimmed = toTrimmed(columnName);
      if (!trimmed) {
        return '';
      }
      const targetKey = normaliseKey(trimmed);
      const rowKeys = Object.keys(row || {});
      for (let idx = 0; idx < rowKeys.length; idx += 1) {
        const key = rowKeys[idx];
        if (normaliseKey(key) === targetKey) {
          return key;
        }
      }
      return '';
    };

    const resolveLineItemValue = (statementName, lineItemName, columnHint) => {
      const statementKey = normaliseStatementLookupKey(statementName);
      const lineItemKey = normaliseKey(lineItemName);
      if (!lineItemKey) {
        return null;
      }
      const lookupKey = statementKey ? `${statementKey}||${lineItemKey}` : '';
      let rows = statementKey ? lineItemLookup.get(lookupKey) : null;
      if (!rows || !rows.length) {
        const fallbackMatches = [];
        const suffix = `||${lineItemKey}`;
        lineItemLookup.forEach((candidateRows, candidateKey) => {
          if (!candidateKey.endsWith(suffix)) {
            return;
          }
          fallbackMatches.push({ key: candidateKey, rows: candidateRows });
        });

        if (statementKey) {
          const sameStatementMatch = fallbackMatches.find((entry) => (
            entry.key.startsWith(`${statementKey}||`)
          ));
          if (sameStatementMatch?.rows?.length) {
            rows = sameStatementMatch.rows;
          }
        } else if (fallbackMatches.length === 1) {
          rows = fallbackMatches[0].rows;
        }
      }
      if (!rows || !rows.length) {
        return null;
      }

      const candidates = [];
      const seen = new Set();
      const strictColumns = strictPreferredValueColumns.length
        ? strictPreferredValueColumns
        : valueColumns;

      const enqueue = (row, candidateColumn) => {
        if (!row) {
          return;
        }
        const resolvedColumn = findMatchingColumnName(row, candidateColumn);
        if (!resolvedColumn) {
          return;
        }
        const identifier = `${row.rowId || `${normaliseKey(row.statement)}||${normaliseKey(row.lineItem || row['Line Item'])}`}||${resolvedColumn}`;
        if (seen.has(identifier)) {
          return;
        }
        seen.add(identifier);
        candidates.push({ row, column: resolvedColumn });
      };

      rows.forEach((row) => {
        if (columnHint) {
          enqueue(row, columnHint);
        }
      });

      const metaColumn = latestColumnByStatement.get(statementKey);
      rows.forEach((row) => {
        if (metaColumn) {
          enqueue(row, metaColumn);
        }
      });

      strictColumns.forEach((columnName) => {
        if (isReferenceColumnHeader(columnName)) {
          return;
        }
        rows.forEach((row) => enqueue(row, columnName));
      });

      for (let idx = 0; idx < candidates.length; idx += 1) {
        const candidate = candidates[idx];
        const raw = candidate.row[candidate.column];
        if (raw === null || typeof raw === 'undefined') {
          continue;
        }
        const numeric = parseNumericValue(raw);
        if (numeric === null) {
          continue;
        }
        return {
          numericValue: numeric,
          columnName: candidate.column,
          statementName: candidate.row.statement || statementName,
          lineItemName: candidate.row.lineItem || candidate.row['Line Item'] || lineItemName,
          displayValue: raw,
        };
      }

      return null;
    };

    const evaluateEntry = (rawEntry) => {
      if (!rawEntry || typeof rawEntry !== 'object') {
        return null;
      }
      const entryStatement = toTrimmed(rawEntry.statement);
      const entryLineItem = toTrimmed(rawEntry.lineItem);
      const entryColumn = toTrimmed(rawEntry.column);
      const entryValueText = toTrimmed(rawEntry.value);
      const steps = Array.isArray(rawEntry.calculation) ? rawEntry.calculation : [];

      const columnsUsed = new Set();
      const formulaParts = [];

      const addColumn = (columnName) => {
        if (!columnName) {
          return;
        }
        columnsUsed.add(columnName);
      };

      let runningTotal = null;
      let baseContext = null;
      let hasMeaningfulSeed = false;
      let appliedStepCount = 0;

      if (entryValueText) {
        const numeric = parseNumericValue(entryValueText);
        if (numeric === null) {
          return null;
        }
        runningTotal = numeric;
        hasMeaningfulSeed = true;
        const formatted = formatNumericValue(numeric) ?? numeric.toString();
        formulaParts.push(`Manual value ${formatted}`);
        addColumn('Manual Input');
      } else if (entryStatement && entryLineItem) {
        const resolved = resolveLineItemValue(entryStatement, entryLineItem, entryColumn);
        if (!resolved) {
          runningTotal = 0;
          baseContext = {
            statementName: entryStatement,
            columnName: '',
            lineItemName: entryLineItem,
          };
          formulaParts.push(`Start at 0 for ${entryLineItem} [${entryStatement}]`);
        } else {
          runningTotal = resolved.numericValue;
          hasMeaningfulSeed = true;
          baseContext = resolved;
          const valueDisplay = resolved.displayValue
            ? normaliseSopValue(resolved.displayValue)
            : formatNumericValue(resolved.numericValue) ?? resolved.numericValue.toString();
          const columnDescriptor = resolved.columnName ? ` -> ${resolved.columnName}` : '';
          formulaParts.push(`${resolved.lineItemName || entryLineItem} [${resolved.statementName || entryStatement}${columnDescriptor}] (${valueDisplay})`);
          addColumn(resolved.columnName || '');
        }
      } else {
        runningTotal = 0;
        formulaParts.push('Start at 0');
      }

      if (runningTotal === null) {
        return null;
      }

      for (let idx = 0; idx < steps.length; idx += 1) {
        const step = steps[idx];
        if (!step || typeof step !== 'object') {
          continue;
        }
        const operator = ['+', '-', '*', '/'].includes(step.operator) ? step.operator : '+';
        const stepMetric = toTrimmed(step.metric);
        const stepConstant = toTrimmed(step.constant);
        const stepStatement = toTrimmed(step.statement) || baseContext?.statementName || entryStatement;
        const stepLineItem = toTrimmed(step.lineItem);
        const stepColumnHint = toTrimmed(step.column) || baseContext?.columnName || entryColumn;
        const isEmptyStep = !stepMetric && !stepConstant && !stepStatement && !stepLineItem && !stepColumnHint;
        if (isEmptyStep) {
          continue;
        }
        let operandValue = null;
        let operandDescription = '';
        let operandColumn = '';

        if (stepMetric) {
          const resolvedStepMetric = resolveSopMetricName(stepMetric) || stepMetric;
          const metricRef = metricValueLookup.get(normaliseMetricKey(resolvedStepMetric));
          if (!metricRef) {
            continue;
          }
          operandValue = metricRef.numericValue;
          operandColumn = metricRef.columnName || 'Derived';
          operandDescription = `metric ${metricRef.metricName} (${metricRef.displayValue || normaliseSopValue(metricRef.numericValue)})`;
        } else if (stepConstant) {
          const numeric = parseNumericValue(stepConstant);
          if (numeric === null) {
            continue;
          }
          operandValue = numeric;
          const formatted = formatNumericValue(numeric) ?? numeric.toString();
          operandDescription = `manual constant ${formatted}`;
          operandColumn = 'Manual Input';
        } else {
          const operandStatement = stepStatement;
          const operandLineItem = stepLineItem;
          if (!operandLineItem) {
            continue;
          }
          const resolved = resolveLineItemValue(operandStatement, operandLineItem, stepColumnHint);
          if (!resolved) {
            continue;
          }
          operandValue = resolved.numericValue;
          operandColumn = resolved.columnName || '';
          const valueDisplay = resolved.displayValue
            ? normaliseSopValue(resolved.displayValue)
            : formatNumericValue(resolved.numericValue) ?? resolved.numericValue.toString();
          const columnDescriptor = resolved.columnName ? ` -> ${resolved.columnName}` : '';
          operandDescription = `${resolved.lineItemName || operandLineItem} [${resolved.statementName || operandStatement}${columnDescriptor}] (${valueDisplay})`;
        }

        if (operandValue === null || typeof operandValue === 'undefined' || !Number.isFinite(operandValue)) {
          continue;
        }
        const effectiveOperandValue = (operator === '+' || operator === '-')
          ? Math.abs(operandValue)
          : operandValue;

        if (!hasMeaningfulSeed && appliedStepCount === 0) {
          switch (operator) {
            case '-':
              runningTotal = -effectiveOperandValue;
              break;
            case '/':
              if (effectiveOperandValue === 0) {
                return null;
              }
              runningTotal = effectiveOperandValue;
              break;
            case '*':
              runningTotal = effectiveOperandValue;
              break;
            case '+':
            default:
              runningTotal = effectiveOperandValue;
              break;
          }
          if (!Number.isFinite(runningTotal)) {
            return null;
          }
          hasMeaningfulSeed = true;
          appliedStepCount += 1;
          formulaParts.push(`${operator} ${operandDescription}`);
          addColumn(operandColumn);
          continue;
        }

        switch (operator) {
          case '+':
            runningTotal += effectiveOperandValue;
            break;
          case '-':
            runningTotal -= effectiveOperandValue;
            break;
          case '*':
            runningTotal *= effectiveOperandValue;
            break;
          case '/':
            if (effectiveOperandValue === 0) {
              return null;
            }
            runningTotal /= effectiveOperandValue;
            break;
          default:
            runningTotal += effectiveOperandValue;
            break;
        }

        if (!Number.isFinite(runningTotal)) {
          return null;
        }

        appliedStepCount += 1;
        formulaParts.push(`${operator} ${operandDescription}`);
        addColumn(operandColumn);
      }

      if (!hasMeaningfulSeed && appliedStepCount === 0) {
        return null;
      }

      const formula = formulaParts
        .map((part) => part.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join(' ');

      return {
        total: runningTotal,
        columns: Array.from(columnsUsed).filter(Boolean),
        formula,
      };
    };

    const overrides = {};
    const manualEntriesByMetric = new Map();
    Object.entries(manualSopEntries || {}).forEach(([metric, entries]) => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      const resolvedMetric = resolveSopMetricName(metric) || toTrimmed(metric);
      if (!resolvedMetric) {
        return;
      }
      if (!manualEntriesByMetric.has(resolvedMetric)) {
        manualEntriesByMetric.set(resolvedMetric, []);
      }
      manualEntriesByMetric.get(resolvedMetric).push(...entries);
    });

    manualEntriesByMetric.forEach((entries, metric) => {
      let aggregate = 0;
      let hasValue = false;
      const formulas = [];
      const columns = new Set();

      entries.forEach((entry) => {
        const result = evaluateEntry(entry);
        if (!result) {
          return;
        }
        aggregate += result.total;
        hasValue = true;
        if (result.formula) {
          formulas.push(result.formula);
        }
        result.columns.forEach((columnName) => columns.add(columnName));
      });

      if (!hasValue) {
        return;
      }

      const formattedValue = formatNumericValue(aggregate);
      const valueText = normaliseSopValue(formattedValue ?? aggregate);
      const columnValues = Array.from(columns).filter(Boolean);
      const columnText = columnValues.length === 0
        ? ''
        : columnValues.length === 1
          ? columnValues[0]
          : 'Multiple';
      const sourceLine = formulas.length
        ? formulas.join('; ')
        : 'Derived from manual calculation';

      overrides[metric] = {
        value: valueText,
        statement: 'Derived',
        column: columnText,
        sourceLine,
      };
    });

    return overrides;
  }, [manualSopEntries, lineItemLookup, sopMetadata, valueColumns, classificationSopSummary, strictPreferredValueColumns]);

  const displayedSopSummary = useMemo(() => {
    const orderedMetrics = [...SOP_METRICS];
    const applyDefaultZeroMetric = (row) => {
      if (isManualEntrySopMetric(row?.metric)) {
        return row;
      }
      const valueText = toTrimmed(row?.value);
      if (valueText && !isDashPlaceholder(valueText)) {
        return row;
      }
      return {
        ...row,
        value: '0',
      };
    };

    const summaryByMetric = new Map();
    (sopSummary || []).forEach((entry) => {
      const key = normaliseMetricKey(entry?.metric);
      if (!key || summaryByMetric.has(key)) {
        return;
      }
      summaryByMetric.set(key, entry);
    });

    const linkedSummaryByMetricKey = new Map();
    Object.entries(classificationSopSummary || {}).forEach(([metricName, entry]) => {
      const key = normaliseMetricKey(metricName);
      if (!key || linkedSummaryByMetricKey.has(key)) {
        return;
      }
      linkedSummaryByMetricKey.set(key, entry);
    });

    const manualOverrideByMetricKey = new Map();
    Object.entries(manualSopOverrides || {}).forEach(([metricName, entry]) => {
      const key = normaliseMetricKey(metricName);
      if (!key || manualOverrideByMetricKey.has(key)) {
        return;
      }
      manualOverrideByMetricKey.set(key, entry);
    });

    const rows = orderedMetrics.map((metricName) => {
      const metricKey = normaliseMetricKey(metricName);
      const baseEntry = summaryByMetric.get(metricKey) || {
        metric: metricName,
        value: isManualEntrySopMetric(metricName) ? '' : '-',
        statement: '',
        column: '',
        sourceLine: '',
        cashFlowQuarterAdjustment: null,
        profitLossQuarterAdjustment: null,
        manual: false,
      };
      const entry = {
        ...baseEntry,
        metric: metricName,
      };
      if (!showProfitLossQ4CumulativeAdjustment) {
        entry.profitLossQuarterAdjustment = null;
      }
      const isCashFlowQuarterMetric = CASH_FLOW_QUARTER_METRIC_KEYS.has(
        metricKey
      );
      const isManualOnlyMetric = isManualEntrySopMetric(metricName);
      const linkedSummary = linkedSummaryByMetricKey.get(metricKey);
      const override = manualOverrideByMetricKey.get(metricKey);
      const effectiveOverride = override;

      // Priority Resolution logic
      let rawValue = '-';
      let statement = '';
      let column = '';
      let sourceLine = '';
      let isManual = false;
      let hasOverride = false;
      let hasLinked = false;
      let hasManualDirect = false;

      if (effectiveOverride) {
        rawValue = effectiveOverride.value;
        statement = effectiveOverride.statement || entry.statement;
        column = effectiveOverride.column || entry.column;
        sourceLine = effectiveOverride.sourceLine || entry.sourceLine;
        isManual = true;
        hasOverride = true;
      } else if (linkedSummary) {
        rawValue = linkedSummary.value;
        statement = linkedSummary.statement || entry.statement;
        column = linkedSummary.column || entry.column;
        sourceLine = linkedSummary.sourceLine || entry.sourceLine;
        isManual = false;
        hasLinked = true;
      } else if (entry.manual) {
        rawValue = entry.value;
        statement = entry.statement;
        column = entry.column;
        sourceLine = entry.sourceLine;
        isManual = true;
        hasManualDirect = true;
      } else {
        // If not manual, and not linked, and not overridden:
        // Do NOT fall back to the auto-extracted value, because if it's not linked,
        // it means either it was never classified, or it was manually unassigned!
        rawValue = '-';
        statement = '';
        column = '';
        sourceLine = '';
        isManual = false;
      }

      if (isManualOnlyMetric) {
        return {
          ...entry,
          value: hasOverride ? formatSopDisplayValue(rawValue) : (hasManualDirect ? formatSopDisplayValue(rawValue) : ''),
          statement: hasOverride || hasManualDirect ? statement : '',
          column: hasOverride || hasManualDirect ? column : '',
          sourceLine: hasOverride || hasManualDirect ? sourceLine : '',
          cashFlowQuarterAdjustment: null,
          profitLossQuarterAdjustment: null,
          manual: isManual,
        };
      }

      // Handle Cash Flow Adjustment Priority
      if (isCashFlowQuarterMetric) {
        if (hasOverride || hasManualDirect) {
          const cashFlowQuarterAdjustment = normaliseCashFlowQuarterAdjustment(entry?.cashFlowQuarterAdjustment);
          const cashFlowBaseEntry = {
            ...entry,
            value: rawValue,
            statement,
            column,
            sourceLine,
            cashFlowQuarterAdjustment,
          };
          const scaledAdjustment = scaleCashFlowQuarterAdjustment(
            cashFlowBaseEntry,
            cashFlowScaleKey,
            statementMultiplierApplied
          );
          if (scaledAdjustment) {
            return {
              ...cashFlowBaseEntry,
              value: scaledAdjustment.value,
              sourceLine: scaledAdjustment.sourceLine,
              cashFlowQuarterAdjustment: {
                selectedQuarter: scaledAdjustment.selectedQuarter,
                extractedCumulative: scaledAdjustment.extractedCumulative,
                previousCumulative: scaledAdjustment.previousCumulative,
                quarterValue: scaledAdjustment.quarterValue,
              },
              manual: true,
            };
          }
          return {
            ...cashFlowBaseEntry,
            value: formatSopDisplayValue(rawValue),
            cashFlowQuarterAdjustment: null,
            manual: true,
          };
        }

        if (hasLinked) {
          const linkedNumericValue = parseNumericValue(linkedSummary.value);
          const linkedValue = isDashPlaceholder(linkedSummary.value)
            ? '0'
            : linkedSummary.value;
          const shouldUseLinkedSummary = linkedNumericValue !== null
            || isDashPlaceholder(linkedSummary.value);
          if (shouldUseLinkedSummary) {
            const cashFlowBaseEntry = {
              ...entry,
              value: linkedValue,
              statement,
              column,
              sourceLine,
              cashFlowQuarterAdjustment: entry?.cashFlowQuarterAdjustment
                ? {
                  ...entry.cashFlowQuarterAdjustment,
                  extractedCumulative: formatSopDisplayValue(linkedValue),
                }
                : entry?.cashFlowQuarterAdjustment,
            };
            const scaledAdjustment = scaleCashFlowQuarterAdjustment(
              cashFlowBaseEntry,
              cashFlowScaleKey,
              statementMultiplierApplied
            );
            if (scaledAdjustment) {
              return {
                ...cashFlowBaseEntry,
                value: scaledAdjustment.value,
                sourceLine: scaledAdjustment.sourceLine,
                cashFlowQuarterAdjustment: {
                  selectedQuarter: scaledAdjustment.selectedQuarter,
                  extractedCumulative: scaledAdjustment.extractedCumulative,
                  previousCumulative: scaledAdjustment.previousCumulative,
                  quarterValue: scaledAdjustment.quarterValue,
                },
                manual: false,
              };
            }
            return {
              ...cashFlowBaseEntry,
              value: linkedValue,
              manual: false,
            };
          }
        }

        // Cash Flow fallback using base entry if present
        const baseCashFlowQuarterAdjustment = normaliseCashFlowQuarterAdjustment(
          entry?.cashFlowQuarterAdjustment
        );
        if (baseCashFlowQuarterAdjustment) {
          const cashFlowBaseEntry = {
            ...entry,
            value: baseCashFlowQuarterAdjustment.extractedCumulative,
            statement: entry.statement || '',
            column: entry.column || '',
            sourceLine: entry.sourceLine || '',
            cashFlowQuarterAdjustment: baseCashFlowQuarterAdjustment,
          };
          const scaledAdjustment = scaleCashFlowQuarterAdjustment(
            cashFlowBaseEntry,
            cashFlowScaleKey,
            statementMultiplierApplied
          );
          if (scaledAdjustment) {
            return {
              ...cashFlowBaseEntry,
              value: scaledAdjustment.value,
              sourceLine: scaledAdjustment.sourceLine,
              cashFlowQuarterAdjustment: {
                selectedQuarter: scaledAdjustment.selectedQuarter,
                extractedCumulative: scaledAdjustment.extractedCumulative,
                previousCumulative: scaledAdjustment.previousCumulative,
                quarterValue: scaledAdjustment.quarterValue,
              },
              manual: entry.manual,
            };
          }
        }

        const hasBaseCFValue = entry.value !== null && typeof entry.value !== 'undefined' && toTrimmed(entry.value) !== '-' && toTrimmed(entry.value) !== '';
        if (hasBaseCFValue && entry.manual) {
          const cashFlowQuarterAdjustment = normaliseCashFlowQuarterAdjustment(entry?.cashFlowQuarterAdjustment);
          const cashFlowBaseEntry = {
            ...entry,
            value: entry.value,
            statement: entry.statement || '',
            column: entry.column || '',
            sourceLine: entry.sourceLine || '',
            cashFlowQuarterAdjustment,
          };
          const scaledAdjustment = scaleCashFlowQuarterAdjustment(
            cashFlowBaseEntry,
            cashFlowScaleKey,
            statementMultiplierApplied
          );
          if (scaledAdjustment) {
            return {
              ...cashFlowBaseEntry,
              value: scaledAdjustment.value,
              sourceLine: scaledAdjustment.sourceLine,
              cashFlowQuarterAdjustment: {
                selectedQuarter: scaledAdjustment.selectedQuarter,
                extractedCumulative: scaledAdjustment.extractedCumulative,
                previousCumulative: scaledAdjustment.previousCumulative,
                quarterValue: scaledAdjustment.quarterValue,
              },
              manual: entry.manual,
            };
          }
          return {
            ...cashFlowBaseEntry,
            value: entry.value,
            manual: entry.manual,
          };
        }

        // Cash Flow fallback (neither override/manual nor valid linked summary nor valid base value)
        return {
          ...entry,
          value: '-',
          statement: '',
          column: '',
          sourceLine: '',
          cashFlowQuarterAdjustment: null,
          profitLossQuarterAdjustment: null,
          manual: false,
        };
      }

      // Handle Profit or Loss Adjustment Priority
      const hasPLAdjustment = showProfitLossQ4CumulativeAdjustment && PROFIT_LOSS_Q4_METRIC_KEYS.has(metricKey);
      if (hasPLAdjustment) {
        const profitLossQuarterAdjustment = normaliseProfitLossQuarterAdjustment(entry?.profitLossQuarterAdjustment) || {
          selectedQuarter: selectedQuarter?.quarter ? `Q${selectedQuarter.quarter} (${selectedQuarter.date || ''})` : '',
          extractedCumulative: '0',
          previousCumulative: '0',
          quarterValue: '0',
        };

        const recalculatedProfitLossAdjustment = recomputeProfitLossQuarterAdjustment(
          {
            ...entry,
            value: rawValue,
            statement,
            column,
            sourceLine,
            manual: isManual,
          },
          hasOverride || hasManualDirect ? null : (linkedSummary || null),
          statementMultiplierApplied
        );

        const finalAdjustment = recalculatedProfitLossAdjustment || profitLossQuarterAdjustment;

        // When toggle is ON: use the subtracted quarter value as the displayed/saved value.
        // When toggle is OFF: use the raw extracted value; do not attach the adjustment breakdown.
        if (applyProfitLossQ4CumulativeAdjustment) {
          return {
            ...entry,
            value: finalAdjustment?.quarterValue || rawValue,
            statement,
            column,
            sourceLine: finalAdjustment?.sourceLine || sourceLine,
            profitLossQuarterAdjustment: finalAdjustment,
            cashFlowQuarterAdjustment: null,
            manual: isManual,
          };
        }

        // Toggle is OFF — show raw extracted value, no adjustment breakdown
        return {
          ...entry,
          value: hasOverride || hasManualDirect ? formatSopDisplayValue(rawValue) : rawValue,
          statement,
          column,
          sourceLine: hasOverride || hasManualDirect
            ? stripProfitLossQuarterCalcSuffix(finalAdjustment?.sourceLine || sourceLine)
            : (sourceLine || stripProfitLossQuarterCalcSuffix(finalAdjustment?.sourceLine || sourceLine)),
          // Keep the adjustment data for the info panel even when disabled,
          // so users can see what WOULD be subtracted without it affecting the saved value.
          profitLossQuarterAdjustment: finalAdjustment
            ? { ...finalAdjustment, _disabled: true }
            : null,
          cashFlowQuarterAdjustment: null,
          manual: isManual,
        };
      }

      // Standard metric (non-CF, non-PL)
      if (hasOverride || hasLinked || hasManualDirect) {
        return {
          ...entry,
          value: hasOverride || hasManualDirect ? formatSopDisplayValue(rawValue) : rawValue,
          statement,
          column,
          sourceLine,
          profitLossQuarterAdjustment: null,
          cashFlowQuarterAdjustment: null,
          manual: isManual,
        };
      }

      const hasBaseValue = entry.value !== null && typeof entry.value !== 'undefined' && toTrimmed(entry.value) !== '-' && toTrimmed(entry.value) !== '';
      if (hasBaseValue && entry.manual) {
        return {
          ...entry,
          value: entry.value,
          statement: entry.statement || '',
          column: entry.column || '',
          sourceLine: entry.sourceLine || '',
          profitLossQuarterAdjustment: null,
          cashFlowQuarterAdjustment: null,
          manual: entry.manual,
        };
      }

      // Fallback
      return {
        ...entry,
        value: '-',
        statement: '',
        column: '',
        sourceLine: '',
        profitLossQuarterAdjustment: null,
        cashFlowQuarterAdjustment: null,
        manual: false,
      };
    });
    return rows.map(applyDefaultZeroMetric);
  }, [
    sopSummary,
    manualSopOverrides,
    classificationSopSummary,
    cashFlowScaleKey,
    statementMultiplierApplied,
    applyProfitLossQ4CumulativeAdjustment,
    showProfitLossQ4CumulativeAdjustment,
  ]);

  useEffect(() => {
    if (!lineItems.length) {
      return;
    }

    const bestDirectRowByMetricKey = new Map();
    lineItems.forEach((item, index) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const metricName = resolveSopMetricName(item.classification);
      const metricKey = normaliseMetricKey(metricName);
      if (!metricKey || !DIRECT_TOTAL_METRIC_KEYS.has(metricKey)) {
        return;
      }
      const score = resolveDirectTotalScore(metricName, item.lineItem || item['Line Item'] || '');
      if (!score) {
        return;
      }
      const current = bestDirectRowByMetricKey.get(metricKey);
      if (!current || score > current.score) {
        bestDirectRowByMetricKey.set(metricKey, { index, score });
      }
    });

    if (!bestDirectRowByMetricKey.size) {
      return;
    }

    let nextItems = lineItems;
    let changed = false;

    lineItems.forEach((item, index) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const metricName = resolveSopMetricName(item.classification);
      const metricKey = normaliseMetricKey(metricName);
      if (!metricKey || !DIRECT_TOTAL_METRIC_KEYS.has(metricKey)) {
        return;
      }

      const bestDirectRow = bestDirectRowByMetricKey.get(metricKey);
      if (!bestDirectRow || bestDirectRow.index === index) {
        return;
      }

      const isManualRow = normaliseKey(item.classificationSource) === 'manual';
      if (isManualRow || !toTrimmed(item.classification)) {
        return;
      }

      if (!changed) {
        nextItems = [...lineItems];
        changed = true;
      }
      const nextRow = { ...item };
      delete nextRow.classification;
      delete nextRow.classificationSource;
      nextItems[index] = nextRow;
    });

    if (changed) {
      setLineItems(nextItems);
    }
  }, [lineItems]);

const jsonPreviewPayload = useMemo(() => (
    buildAnnualReportJson({
      pdfName,
      qcComplete,
      lineItems,
      valueColumns,
      verifiedStatements,
      sopSummary: displayedSopSummary,
      manualSopEntries,
      statementUploadImagesByStatement,
      limitPerStatement: 5,
      includePreviewNote: true,
      includeTimestamp: false,
    })
  ), [
    pdfName,
    qcComplete,
    lineItems,
    valueColumns,
    verifiedStatements,
    displayedSopSummary,
    manualSopEntries,
    statementUploadImagesByStatement,
  ]);

const finalizedReviewSnapshot = useMemo(() => (
    buildAnnualReportJson({
      pdfName,
      qcComplete,
      lineItems,
      valueColumns,
      verifiedStatements,
      sopSummary: displayedSopSummary,
      manualSopEntries,
      statementUploadImagesByStatement,
      includePreviewNote: false,
      includeTimestamp: true,
    })
  ), [
    pdfName,
    qcComplete,
    lineItems,
    valueColumns,
    verifiedStatements,
    displayedSopSummary,
    manualSopEntries,
    statementUploadImagesByStatement,
  ]);

  const jsonPreviewText = useMemo(() => (
    JSON.stringify(jsonPreviewPayload, null, 2)
  ), [jsonPreviewPayload]);

  const iframeSrc = useMemo(() => {
    if (!pdfBase64) return '';
    return `data:application/pdf;base64,${pdfBase64}`;
  }, [pdfBase64]);

  const handleValueChange = (rowId, columnName, value, options = {}) => {
    const safeValue = value === null || typeof value === 'undefined' ? '' : value.toString();
    let affectedStatement = null;
    let affectedLineItem = '';
    let valueChanged = false;

    setLineItems((items) => items.map((item) => {
      if (item.rowId !== rowId) {
        return item;
      }
      affectedStatement = item.statement;
      affectedLineItem = item.lineItem || item['Line Item'] || '';
      if (item[columnName] === safeValue) {
        return item;
      }
      valueChanged = true;
      return { ...item, [columnName]: safeValue };
    }));

    if (!valueChanged) {
      if (!options?.preserveStatus) {
        setStatus(null);
      }
      return;
    }

    if (affectedStatement) {
      setVerifiedStatements((prev) => ({
        ...prev,
        [affectedStatement]: false,
      }));
    }

    if (affectedStatement && affectedLineItem) {
      const statementKey = affectedStatement.toString().toLowerCase();
      const lineItemKey = affectedLineItem.toString().toLowerCase();
      const columnKey = columnName.toString().toLowerCase();

      setSopSummary((current) => current.map((entry) => {
        if (!entry.statement || !entry.sourceLine || !entry.column) {
          return entry;
        }
        if (entry.manual) {
          return entry;
        }
        const entryStatement = entry.statement.toString().toLowerCase();
        const entryLine = entry.sourceLine.toString().toLowerCase();
        const entryColumn = entry.column.toString().toLowerCase();

        if (entryStatement === statementKey
          && entryLine === lineItemKey
          && entryColumn === columnKey) {
          return {
            ...entry,
            value: normaliseSopValue(safeValue),
          };
        }
        return entry;
      }));
    }

    setQcComplete(false);
    if (!options?.preserveStatus) {
      setStatus(null);
    }
  };

  const handleSopEditStart = (metric) => {
    const currentEntry = displayedSopSummary.find((entry) => entry.metric === metric);
    if (!currentEntry) {
      return;
    }
    setEditingSopMetric(metric);
    setSopEditDraft({
      value: currentEntry.value === '-' ? '' : currentEntry.value || '',
      statement: currentEntry.statement || '',
      column: currentEntry.column || '',
      sourceLine: currentEntry.sourceLine || '',
    });
  };

  const handleSopEditCancel = () => {
    setEditingSopMetric(null);
    setSopEditDraft(buildEmptySopEditDraft());
  };

  const handleSopEditFieldChange = (field, value) => {
    setSopEditDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSopEditSave = () => {
    if (!editingSopMetric) {
      return;
    }
    setSopSummary((current) => {
      const rows = Array.isArray(current) ? [...current] : [];
      const nextEntry = {
        metric: editingSopMetric,
        value: normaliseSopValue(sopEditDraft.value),
        statement: cleanSopText(sopEditDraft.statement),
        column: cleanSopText(sopEditDraft.column),
        sourceLine: cleanSopText(sopEditDraft.sourceLine),
        manual: true,
      };
      const existingIndex = rows.findIndex((entry) => entry.metric === editingSopMetric);
      if (existingIndex === -1) {
        return [...rows, nextEntry];
      }
      rows[existingIndex] = {
        ...rows[existingIndex],
        ...nextEntry,
      };
      return rows;
    });
    setStatus({ type: 'success', message: `${editingSopMetric} updated in SOP summary.` });
    setQcComplete(false);
    handleSopEditCancel();
  };

  const buildEmptyValueMap = () => valueColumns.reduce((acc, column) => {
    acc[column] = '';
    return acc;
  }, {});

  const openManualEntry = () => {
    const defaultStatement = activeStatement || statements[0] || '';
    setManualRow({
      statement: defaultStatement,
      lineItem: '',
      values: buildEmptyValueMap(),
    });
    setShowManualEntry(true);
    setStatus(null);
  };

  const handleManualFieldChange = (field, value) => {
    setManualRow((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleManualValueInput = (column, value) => {
    setManualRow((prev) => ({
      ...prev,
      values: {
        ...(prev.values || {}),
        [column]: value,
      },
    }));
  };

  const handleManualRowCancel = () => {
    setShowManualEntry(false);
    setManualRow({ statement: activeStatement || '', lineItem: '', values: buildEmptyValueMap() });
    setStatus(null);
  };

  const handleManualRowSubmit = () => {
    const statement = (manualRow.statement || '').trim();
    const lineItem = (manualRow.lineItem || '').trim();

    if (!statement || !lineItem) {
      setStatus({ type: 'error', message: 'Enter both a statement name and line item before adding a row.' });
      return;
    }

    const newRowId = `manual-${Date.now()}`;
    const newRow = {
      rowId: newRowId,
      statement,
      lineItem,
    };

    valueColumns.forEach((column) => {
      newRow[column] = (manualRow.values?.[column] || '').trim();
    });

    setLineItems((prev) => [...prev, newRow]);
    setVerifiedStatements((prev) => ({
      ...prev,
      [statement]: false,
    }));
    setActiveStatement(statement);
    setShowManualEntry(false);
    setManualRow({
      statement,
      lineItem: '',
      values: buildEmptyValueMap(),
    });
    setQcComplete(false);
    setActiveWorkspaceTab('statements');
    setStatus({ type: 'info', message: 'Manual line item added. Review the values and mark the statement when ready.' });
  };

  const handleClassificationChange = (rowIds, metricName, options = {}) => {
    const trimmedMetric = typeof metricName === 'string' ? metricName.trim() : '';
    const targets = Array.isArray(rowIds) ? rowIds : [rowIds];
    const targetSet = new Set(
      targets
        .map((value) => toTrimmed(value))
        .filter(Boolean)
    );

    if (!targetSet.size) {
      if (!options?.preserveStatus) {
        setStatus({ type: 'warning', message: 'Select at least one row before updating the SOP metric.' });
      }
      return;
    }

    const affectedStatements = new Set();
    let changedCount = 0;

    setLineItems((items) => items.map((item) => {
      const rowIdKey = toTrimmed(item?.rowId);
      if (!targetSet.has(rowIdKey)) {
        return item;
      }
      if (item.statement) {
        affectedStatements.add(item.statement);
      }
      const current = typeof item.classification === 'string' ? item.classification.trim() : '';
      if (current === trimmedMetric) {
        return item;
      }
      changedCount += 1;
      if (!trimmedMetric) {
        const next = { ...item };
        delete next.classification;
        delete next.classificationSource;
        return next;
      }
      return { ...item, classification: trimmedMetric, classificationSource: 'manual' };
    }));

    if (!changedCount) {
      if (!options?.preserveStatus) {
        setStatus({
          type: 'info',
          message: trimmedMetric
            ? `Selected row${targetSet.size === 1 ? '' : 's'} already linked to "${trimmedMetric}".`
            : 'Selected row(s) already had no SOP metric assigned.',
        });
      }
      return;
    }

    if (affectedStatements.size) {
      setVerifiedStatements((prev) => {
        const next = { ...prev };
        affectedStatements.forEach((statement) => {
          if (statement) {
            next[statement] = false;
          }
        });
        return next;
      });
    }
    setQcComplete(false);
    if (!options?.preserveStatus) {
      setStatus({
        type: 'success',
        message: trimmedMetric
          ? `Linked ${changedCount} row${changedCount === 1 ? '' : 's'} to "${trimmedMetric}".`
          : `Cleared SOP metric for ${changedCount} row${changedCount === 1 ? '' : 's'}.`,
      });
    }
  };

  const handleRowSelectionToggle = (rowId, checked) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  };

  const handleSelectAllVisibleRows = (checked) => {
    if (checked) {
      setSelectedRowIds(new Set(visibleItems.map((item) => item.rowId)));
    } else {
      setSelectedRowIds(new Set());
    }
  };

  const toggleSopMetricExpansion = (metric) => {
    setExpandedSopMetrics((prev) => ({
      ...prev,
      [metric]: !prev?.[metric],
    }));
  };

  const handleLinkedSourceDraftChange = (metric, field, value) => {
    setLinkedSourceDrafts((prev) => {
      const current = prev?.[metric] || {};
      const next = {
        statement: current.statement || '',
        rowId: current.rowId || '',
        ...current,
      };

      if (field === 'statement') {
        next.statement = value;
        next.rowId = '';
      } else if (field === 'rowId') {
        next.rowId = value;
        const selectedRow = lineItems.find((item) => String(item?.rowId) === String(value));
        if (selectedRow?.statement) {
          next.statement = selectedRow.statement;
        }
      } else {
        next[field] = value;
      }

      return {
        ...prev,
        [metric]: next,
      };
    });
  };

  const handleRemoveLinkedSource = (metric, rowId) => {
    const sourceRow = lineItems.find((item) => String(item?.rowId) === String(rowId));
    if (!sourceRow) {
      setStatus({ type: 'error', message: 'Could not find the linked row to remove.' });
      return;
    }

    handleClassificationChange(rowId, '', { preserveStatus: true });
    setStatus({
      type: 'success',
      message: `Removed "${sourceRow.lineItem || sourceRow['Line Item'] || 'Line item'}" from "${metric}".`,
    });
  };

  const handleAddLinkedSource = (metric) => {
    const draft = linkedSourceDrafts?.[metric] || {};
    const selectedRowId = toTrimmed(draft.rowId);
    if (!selectedRowId) {
      setStatus({ type: 'warning', message: `Choose a source row before adding it to "${metric}".` });
      return;
    }

    const sourceRow = lineItems.find((item) => String(item?.rowId) === String(selectedRowId));
    if (!sourceRow) {
      setStatus({ type: 'error', message: 'Could not find the selected source row.' });
      return;
    }

    const currentMetric = toTrimmed(sourceRow.classification);
    if (normaliseMetricKey(currentMetric) === normaliseMetricKey(metric)) {
      setStatus({
        type: 'info',
        message: `"${sourceRow.lineItem || sourceRow['Line Item'] || 'Line item'}" is already linked to "${metric}".`,
      });
      return;
    }

    handleClassificationChange(selectedRowId, metric, { preserveStatus: true });
    setLinkedSourceDrafts((prev) => ({
      ...prev,
      [metric]: {
        statement: sourceRow.statement || draft.statement || '',
        rowId: '',
      },
    }));
    setStatus({
      type: 'success',
      message: currentMetric
        ? `Moved "${sourceRow.lineItem || sourceRow['Line Item'] || 'Line item'}" from "${currentMetric}" to "${metric}".`
        : `Added "${sourceRow.lineItem || sourceRow['Line Item'] || 'Line item'}" to "${metric}".`,
    });
  };

  const handleManualBreakdownDraftChange = (metric, field, value) => {
    setBreakdownDrafts((prev) => {
      const current = prev?.[metric] || {};
      const next = {
        statement: current.statement || '',
        lineItem: current.lineItem || '',
        calculation: Array.isArray(current.calculation) && current.calculation.length
          ? current.calculation
          : [createEmptyCalculationStep()],
      };
      if (field === 'statement' || field === 'lineItem') {
        next[field] = value;
      }
      return {
        ...prev,
        [metric]: next,
      };
    });
  };

  const handleManualBreakdownDraftCalculationChange = (metric, index, field, value) => {
    setBreakdownDrafts((prev) => {
      const current = prev?.[metric] || {};
      const steps = Array.isArray(current.calculation)
        ? current.calculation.map((step) => ({ ...createEmptyCalculationStep(), ...step }))
        : [];
      while (steps.length <= index) {
        steps.push(createEmptyCalculationStep());
      }
      steps[index] = {
        ...steps[index],
        [field]: value,
      };
      return {
        ...prev,
        [metric]: {
          statement: current.statement || '',
          lineItem: current.lineItem || '',
          column: current.column || '',
          value: current.value || '',
          calculation: steps,
        },
      };
    });
  };

  const handleAddManualBreakdownDraftStep = (metric) => {
    setBreakdownDrafts((prev) => {
      const current = prev?.[metric] || {};
      const steps = Array.isArray(current.calculation)
        ? [...current.calculation.map((step) => ({ ...createEmptyCalculationStep(), ...step }))]
        : [];
      steps.push(createEmptyCalculationStep({ operator: steps.length ? '+' : '+' }));
      return {
        ...prev,
        [metric]: {
          statement: current.statement || '',
          lineItem: current.lineItem || '',
          column: current.column || '',
          value: current.value || '',
          calculation: steps,
        },
      };
    });
  };

  const handleRemoveManualBreakdownDraftStep = (metric, index) => {
    setBreakdownDrafts((prev) => {
      const current = prev?.[metric];
      if (!current) {
        return prev;
      }
      const steps = Array.isArray(current.calculation)
        ? current.calculation.filter((_, stepIndex) => stepIndex !== index)
        : [];
      return {
        ...prev,
        [metric]: {
          statement: current.statement || '',
          lineItem: current.lineItem || '',
          column: current.column || '',
          value: current.value || '',
          calculation: steps,
        },
      };
    });
  };

  const handleAddManualBreakdownEntry = (metric) => {
    const draft = breakdownDrafts?.[metric] || {};
    const statement = (draft.statement || '').trim();
    const lineItem = (draft.lineItem || '').trim();
    const calculationSteps = Array.isArray(draft.calculation)
      ? draft.calculation
        .map((step) => ({
          ...createEmptyCalculationStep(),
          ...step,
          operator: step?.operator || '+',
          metric: (step?.metric || '').trim(),
          statement: (step?.statement || '').trim(),
          lineItem: (step?.lineItem || '').trim(),
          column: (step?.column || '').trim(),
          constant: (step?.constant || '').trim(),
        }))
        .filter((step) => (
          step.metric
          || step.statement
          || step.lineItem
          || step.column
          || step.constant
        ))
      : [];

    if (!statement && !lineItem && !calculationSteps.length) {
      setStatus({ type: 'error', message: 'Provide a source line item or add at least one calculation step.' });
      return;
    }

    const newEntry = {
      id: `manual-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      statement,
      lineItem,
      column: '',
      value: '',
      calculation: calculationSteps,
    };

    setManualSopEntries((prev) => {
      const next = { ...(prev || {}) };
      const existing = Array.isArray(next[metric]) ? next[metric] : [];
      next[metric] = [...existing, newEntry];
      return next;
    });

    setBreakdownDrafts((prev) => ({
      ...prev,
      [metric]: {
        statement: '',
        lineItem: '',
        calculation: [createEmptyCalculationStep()],
      },
    }));

    setQcComplete(false);
    setStatus({ type: 'success', message: `Added manual breakdown entry to "${metric}".` });
  };

  const handleManualBreakdownValueChange = (metric, entryId, field, value) => {
    setManualSopEntries((prev) => {
      const existing = Array.isArray(prev?.[metric]) ? prev[metric] : [];
      const updated = existing.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }
        return {
          ...entry,
          [field]: value,
        };
      });
      return {
        ...(prev || {}),
        [metric]: updated,
      };
    });
    setQcComplete(false);
  };

  const handleManualBreakdownCalculationChange = (metric, entryId, index, field, value) => {
    setManualSopEntries((prev) => {
      const existing = Array.isArray(prev?.[metric]) ? prev[metric] : [];
      const updated = existing.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }
        const steps = Array.isArray(entry.calculation)
          ? entry.calculation.map((step) => ({ ...createEmptyCalculationStep(), ...step }))
          : [];
        while (steps.length <= index) {
          steps.push(createEmptyCalculationStep());
        }
        steps[index] = {
          ...steps[index],
          [field]: value,
        };
        return {
          ...entry,
          calculation: steps,
        };
      });
      return {
        ...(prev || {}),
        [metric]: updated,
      };
    });
    setQcComplete(false);
  };

  const handleAddManualBreakdownCalculationStep = (metric, entryId) => {
    setManualSopEntries((prev) => {
      const existing = Array.isArray(prev?.[metric]) ? prev[metric] : [];
      const updated = existing.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }
        const steps = Array.isArray(entry.calculation)
          ? entry.calculation.map((step) => ({ ...createEmptyCalculationStep(), ...step }))
          : [];
        steps.push(createEmptyCalculationStep({ operator: steps.length ? '+' : '+' }));
        return {
          ...entry,
          calculation: steps,
        };
      });
      return {
        ...(prev || {}),
        [metric]: updated,
      };
    });
    setQcComplete(false);
  };

  const handleRemoveManualBreakdownCalculationStep = (metric, entryId, index) => {
    setManualSopEntries((prev) => {
      const existing = Array.isArray(prev?.[metric]) ? prev[metric] : [];
      const updated = existing.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }
        const steps = Array.isArray(entry.calculation)
          ? entry.calculation.filter((_, stepIndex) => stepIndex !== index)
          : [];
        return {
          ...entry,
          calculation: steps,
        };
      });
      return {
        ...(prev || {}),
        [metric]: updated,
      };
    });
    setQcComplete(false);
  };

  const handleRemoveManualBreakdownEntry = (metric, entryId) => {
    setManualSopEntries((prev) => {
      const existing = Array.isArray(prev?.[metric]) ? prev[metric] : [];
      const filtered = existing.filter((entry) => entry.id !== entryId);
      const next = { ...(prev || {}) };
      if (filtered.length) {
        next[metric] = filtered;
      } else {
        delete next[metric];
      }
      return next;
    });
    setQcComplete(false);
    setStatus({ type: 'info', message: `Removed a manual breakdown entry from "${metric}".` });
  };

  const getAppliedMultiplierLabel = (multiplierKey) => (
    multiplierKey === 'x1000000' ? 'x1,000,000' : 'x1,000'
  );

  const isMultiplierProtectedRow = (row) => {
    const metricCandidates = [
      row?.classification,
      row?.lineItem,
      row?.['Line Item'],
    ];

    const protectedMetricKeys = new Set([
      normaliseMetricKey('EPS'),
      normaliseMetricKey('Book Value'),
    ]);

    for (let index = 0; index < metricCandidates.length; index += 1) {
      const candidate = metricCandidates[index];
      const resolvedMetric = resolveSopMetricName(candidate);
      const resolvedKey = normaliseMetricKey(resolvedMetric);
      if (resolvedKey && protectedMetricKeys.has(resolvedKey)) {
        return true;
      }
    }

    const lineText = toTrimmed(row?.lineItem || row?.['Line Item']).toLowerCase();
    if (!lineText) {
      return false;
    }
    const normalizedLine = normaliseMetricKey(lineText);
    const hasNetAssetPhrase = /\bnet assets?\b/.test(lineText) || /\bnav\b/.test(lineText);
    const hasPerSharePhrase = /\bper\s+share\b|\bordinary\s+share\b/.test(lineText);
    const bookValuePatternMatch =
      lineText.includes('book value')
      || lineText.includes('book value per share')
      || lineText.includes('net asset per share')
      || lineText.includes('net asset value per share')
      || lineText.includes('net assets per share')
      || lineText.includes('nav per share')
      || lineText.includes('nav per ordinary share')
      || (hasNetAssetPhrase && hasPerSharePhrase)
      || normalizedLine.includes('netassetvalueperordinaryshare')
      || normalizedLine.includes('netassetsperordinaryshare')
      || normalizedLine.includes('netassetsattributabletoordinaryshareholderspershare')
      || normalizedLine.includes('netassetsattributabletoequityholderspershare')
      || normalizedLine.includes('netassetsattributabletoownerspershare')
      || normalizedLine.includes('netassetspershareattributabletoequityholders')
      || normalizedLine.includes('netassetvalueattributabletoequityholderspershare')
      || normalizedLine.includes('totalequitytotalshares')
      || normalizedLine.includes('totalequitytotshares');

    return (
      /\beps\b/.test(lineText)
      || lineText.includes('earnings per share')
      || bookValuePatternMatch
    );
  };

  const handleStatementAddZeros = () => {
    if (!activeStatement) {
      setStatus({ type: 'warning', message: 'Select a statement before applying the multiplier.' });
      return;
    }
    const existingMultiplier = statementMultiplierApplied?.[activeStatement];
    if (existingMultiplier) {
      setStatus({
        type: 'info',
        message: `A multiplier (${getAppliedMultiplierLabel(existingMultiplier)}) has already been applied to ${activeStatement}.`,
      });
      return;
    }
    if (!statementValueColumns.length) {
      setStatus({ type: 'info', message: 'No numeric columns available in this statement.' });
      return;
    }
    let updatedCells = 0;
    let protectedRows = 0;
    lineItems.forEach((row) => {
      if (row.statement !== activeStatement) {
        return;
      }
      if (isMultiplierProtectedRow(row)) {
        protectedRows += 1;
        return;
      }
      statementValueColumns.forEach((column) => {
        const nextValue = appendThreeZeros(row[column]);
        if (nextValue === null) {
          return;
        }
        const original = row[column] === null || typeof row[column] === 'undefined'
          ? ''
          : row[column].toString().trim();
        if (nextValue !== original) {
          updatedCells += 1;
          handleValueChange(row.rowId, column, nextValue, { preserveStatus: true });
        }
      });
    });
    if (updatedCells) {
      setStatementMultiplierApplied((prev) => ({
        ...prev,
        [activeStatement]: 'x1000',
      }));
      setStatus({
        type: 'success',
        message: `Added three zeros to ${updatedCells} cell${updatedCells === 1 ? '' : 's'} in ${activeStatement}.${protectedRows ? ` Skipped EPS/Book Value rows: ${protectedRows}.` : ''}`,
      });
    } else {
      setStatus({
        type: 'info',
        message: protectedRows
          ? `No numeric values were updated. EPS/Book Value rows were skipped (${protectedRows}).`
          : 'No numeric values were updated.',
      });
    }
  };

  const handleStatementAddMillionZeros = () => {
    if (!activeStatement) {
      setStatus({ type: 'warning', message: 'Select a statement before applying the multiplier.' });
      return;
    }
    const existingMultiplier = statementMultiplierApplied?.[activeStatement];
    if (existingMultiplier) {
      setStatus({
        type: 'info',
        message: `A multiplier (${getAppliedMultiplierLabel(existingMultiplier)}) has already been applied to ${activeStatement}.`,
      });
      return;
    }
    if (!statementValueColumns.length) {
      setStatus({ type: 'info', message: 'No numeric columns available in this statement.' });
      return;
    }
    let updatedCells = 0;
    let protectedRows = 0;
    lineItems.forEach((row) => {
      if (row.statement !== activeStatement) {
        return;
      }
      if (isMultiplierProtectedRow(row)) {
        protectedRows += 1;
        return;
      }
      statementValueColumns.forEach((column) => {
        const nextValue = appendSixZeros(row[column]);
        if (nextValue === null) {
          return;
        }
        const original = row[column] === null || typeof row[column] === 'undefined'
          ? ''
          : row[column].toString().trim();
        if (nextValue !== original) {
          updatedCells += 1;
          handleValueChange(row.rowId, column, nextValue, { preserveStatus: true });
        }
      });
    });
    if (updatedCells) {
      setStatementMultiplierApplied((prev) => ({
        ...prev,
        [activeStatement]: 'x1000000',
      }));
      setStatus({
        type: 'success',
        message: `Added six zeros to ${updatedCells} cell${updatedCells === 1 ? '' : 's'} in ${activeStatement}.${protectedRows ? ` Skipped EPS/Book Value rows: ${protectedRows}.` : ''}`,
      });
    } else {
      setStatus({
        type: 'info',
        message: protectedRows
          ? `No numeric values were updated. EPS/Book Value rows were skipped (${protectedRows}).`
          : 'No numeric values were updated.',
      });
    }
  };

  const handleStatementRemoveZeros = () => {
    if (!activeStatement) {
      setStatus({ type: 'warning', message: 'Select a statement before removing the multiplier.' });
      return;
    }
    const existingMultiplier = statementMultiplierApplied?.[activeStatement];
    if (!existingMultiplier) {
      setStatus({
        type: 'info',
        message: `No multiplier has been applied to ${activeStatement}.`,
      });
      return;
    }
    if (!statementValueColumns.length) {
      setStatus({ type: 'info', message: 'No numeric columns available in this statement.' });
      return;
    }
    const factor = existingMultiplier === 'x1000000' ? 1000000 : 1000;
    let updatedCells = 0;
    let protectedRows = 0;
    lineItems.forEach((row) => {
      if (row.statement !== activeStatement) {
        return;
      }
      if (isMultiplierProtectedRow(row)) {
        protectedRows += 1;
        return;
      }
      statementValueColumns.forEach((column) => {
        const raw = row[column] === null || typeof row[column] === 'undefined' ? '' : row[column].toString().trim();
        if (!raw || /%|percent|pct/i.test(raw)) {
          return;
        }
        const numeric = parseNumericValue(raw);
        if (numeric === null) {
          return;
        }
        const nextValue = formatNumericValue(numeric / factor);
        if (nextValue !== null && nextValue !== raw) {
          updatedCells += 1;
          handleValueChange(row.rowId, column, nextValue, { preserveStatus: true });
        }
      });
    });

    setStatementMultiplierApplied((prev) => {
      const next = { ...prev };
      delete next[activeStatement];
      return next;
    });

    setStatus({
      type: 'success',
      message: `Reverted the ${getAppliedMultiplierLabel(existingMultiplier)} multiplier. Restored ${updatedCells} cell${updatedCells === 1 ? '' : 's'} in ${activeStatement}.${protectedRows ? ` Skipped EPS/Book Value rows: ${protectedRows}.` : ''}`,
    });
  };

  const handleStatementMakePositive = () => {
    if (!activeStatement) {
      setStatus({ type: 'warning', message: 'Select a statement before converting values.' });
      return;
    }
    if (!statementValueColumns.length) {
      setStatus({ type: 'info', message: 'No numeric columns available in this statement.' });
      return;
    }
    let updatedCells = 0;
    lineItems.forEach((row) => {
      if (row.statement !== activeStatement) {
        return;
      }
      statementValueColumns.forEach((column) => {
        const nextValue = convertValueToPositive(row[column]);
        if (nextValue === null) {
          return;
        }
        const original = row[column] === null || typeof row[column] === 'undefined'
          ? ''
          : row[column].toString().trim();
        if (nextValue !== original) {
          updatedCells += 1;
          handleValueChange(row.rowId, column, nextValue, { preserveStatus: true });
        }
      });
    });
    if (updatedCells) {
      setStatus({
        type: 'success',
        message: `Converted ${updatedCells} cell${updatedCells === 1 ? '' : 's'} to positive in ${activeStatement}.`,
      });
    } else {
      setStatus({ type: 'info', message: 'No negative values were found to update.' });
    }
  };

  const handleRowSignConversion = (rowId, targetSign = 'positive') => {
    if (!statementValueColumns.length) {
      setStatus({ type: 'info', message: 'No numeric columns available in this statement.' });
      return;
    }
    const targetRow = lineItems.find((item) => item.rowId === rowId);
    if (!targetRow) {
      return;
    }
    let updatedCells = 0;
    statementValueColumns.forEach((column) => {
      const nextValue = targetSign === 'negative'
        ? convertValueToNegative(targetRow[column])
        : convertValueToPositive(targetRow[column]);
      if (nextValue === null) {
        return;
      }
      const original = targetRow[column] === null || typeof targetRow[column] === 'undefined'
        ? ''
        : targetRow[column].toString().trim();
      if (nextValue !== original) {
        updatedCells += 1;
        handleValueChange(rowId, column, nextValue, { preserveStatus: true });
      }
    });
    const rowLabel = targetRow.lineItem || targetRow['Line Item'] || 'row';
    if (updatedCells) {
      setStatus({
        type: 'success',
        message: `Converted ${updatedCells} cell${updatedCells === 1 ? '' : 's'} to ${targetSign} for ${rowLabel}.`,
      });
    } else {
      setStatus({ type: 'info', message: `No values were found to convert to ${targetSign}.` });
    }
  };

  const handleDeleteRow = (rowId) => {
    const targetRow = lineItems.find((item) => item.rowId === rowId);
    if (!targetRow) {
      return;
    }
    const statementName = targetRow.statement || '';
    const lineItemName = targetRow.lineItem || targetRow['Line Item'] || '';
    const statementKey = statementName.toString().toLowerCase();
    const lineItemKey = lineItemName.toString().toLowerCase();

    setLineItems((prev) => prev.filter((item) => item.rowId !== rowId));

    setSelectedRowIds((prev) => {
      if (!prev.has(rowId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });

    if (statementName) {
      setVerifiedStatements((prev) => ({
        ...prev,
        [statementName]: false,
      }));
    }

    if (statementKey && lineItemKey) {
      setSopSummary((current) => current.map((entry) => {
        if (!entry || !entry.statement || !entry.sourceLine || !entry.column) {
          return entry;
        }
        if (entry.manual) {
          return entry;
        }
        const entryStatement = entry.statement.toString().toLowerCase();
        const entryLine = entry.sourceLine.toString().toLowerCase();
        if (entryStatement === statementKey && entryLine === lineItemKey) {
          return {
            ...entry,
            value: '-',
          };
        }
        return entry;
      }));
    }

    setQcComplete(false);
    setStatus({ type: 'info', message: 'Line item removed from the dataset.' });
  };

  const handleRemoveColumn = (columnName, statementName = activeStatement) => {
    if (!columnName || !valueColumns.includes(columnName)) {
      return;
    }
    const columnKey = columnName.toString().toLowerCase();
    const normalizedColumnKey = normaliseKey(columnName);
    const affectedStatementName = toTrimmed(statementName);
    const affectedStatementKey = normaliseStatementLookupKey(affectedStatementName);
    const currentStatementColumns = filterColumnsForStatement(valueColumns, affectedStatementName)
      .filter((column) => (
        lineItems.some((item) => (
          item
          && typeof item === 'object'
          && (!affectedStatementKey || normaliseStatementLookupKey(item.statement) === affectedStatementKey)
          && hasPopulatedValue(item?.[column])
        ))
      ));
    const currentPreferredColumns = rankPreferredStatementColumns(currentStatementColumns);
    const nextLineItems = lineItems.map((item) => {
      if (!item || typeof item !== 'object') {
        return item;
      }
      if (
        affectedStatementKey
        && normaliseStatementLookupKey(item.statement) !== affectedStatementKey
      ) {
        return item;
      }
      if (!Object.prototype.hasOwnProperty.call(item, columnName)) {
        return item;
      }
      const next = { ...item };
      delete next[columnName];
      return next;
    });
    const shouldKeepColumn = nextLineItems.some((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      return hasPopulatedValue(item?.[columnName]);
    });
    const nextValueColumns = shouldKeepColumn
      ? valueColumns
      : valueColumns.filter((column) => column !== columnName);
    const affectedStatementRows = nextLineItems.filter((item) => (
      item
      && typeof item === 'object'
      && (!affectedStatementKey || normaliseStatementLookupKey(item.statement) === affectedStatementKey)
    ));
    const nextStatementColumns = filterColumnsForStatement(nextValueColumns, affectedStatementName)
      .filter((column) => (
        affectedStatementRows.some((item) => hasPopulatedValue(item?.[column]))
      ));
    const rankedStatementColumns = rankPreferredStatementColumns(nextStatementColumns);
    const nextSopColumn = rankedStatementColumns[0] || nextStatementColumns[0] || '';
    const latestColumnsInput = (sopMetadata && typeof sopMetadata === 'object')
      ? sopMetadata.latestColumns || {}
      : {};
    const removedLatestHint = affectedStatementKey
      ? Object.entries(latestColumnsInput || {}).some(([latestStatementName, latestColumn]) => (
        normaliseStatementLookupKey(latestStatementName) === affectedStatementKey
        && normaliseKey(latestColumn) === normalizedColumnKey
      ))
      : Object.values(latestColumnsInput || {}).some(
        (latestColumn) => normaliseKey(latestColumn) === normalizedColumnKey
      );
    const preferredColumns = currentPreferredColumns.length
      ? currentPreferredColumns
      : currentStatementColumns;
    const removedPreferredColumn = preferredColumns.some(
      (preferredColumn) => normaliseKey(preferredColumn) === normalizedColumnKey
    );
    const removedFirstValueColumn = normaliseKey(preferredColumns[0]) === normalizedColumnKey;
    const removedActiveSopColumn =
      removedFirstValueColumn || removedPreferredColumn || removedLatestHint;
    let shouldRecalculateSops = true;

    if (removedActiveSopColumn && nextSopColumn && affectedStatementName) {
      shouldRecalculateSops = window.confirm(
        `Column "${columnName}" was removed from ${affectedStatementName}. Recalculate SOPs for this statement using "${nextSopColumn}"?`
      );
    }
    const pauseSopRecalculation = removedActiveSopColumn && !shouldRecalculateSops;

    setValueColumns(nextValueColumns);
    setPausedSopStatements((current) => {
      if (!affectedStatementKey || !removedActiveSopColumn) {
        return current;
      }
      const next = { ...(current || {}) };
      if (pauseSopRecalculation) {
        next[affectedStatementKey] = affectedStatementName || affectedStatementKey;
      } else {
        delete next[affectedStatementKey];
      }
      return next;
    });

    setLineItems(nextLineItems);

    setManualRow((prev) => {
      if (
        affectedStatementKey
        && normaliseStatementLookupKey(prev.statement) !== affectedStatementKey
      ) {
        return prev;
      }
      const nextValues = Object.entries(prev.values || {}).reduce((acc, [name, val]) => {
        if (name !== columnName) {
          acc[name] = val;
        }
        return acc;
      }, {});
      return {
        ...prev,
        values: nextValues,
      };
    });

    setManualSopEntries((prev) => {
      if (!prev || !Object.keys(prev).length) {
        return prev;
      }
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([metric, entries]) => {
        if (!Array.isArray(entries)) {
          next[metric] = entries;
          return;
        }
        const updated = entries.map((entry) => {
          if (!entry || typeof entry !== 'object') {
            return entry;
          }
          const entryColumn = entry.column ? entry.column.toString().toLowerCase() : '';
          const entryStatementKey = normaliseStatementLookupKey(entry.statement);
          if (
            entryColumn === columnKey
            && (!affectedStatementKey || entryStatementKey === affectedStatementKey)
          ) {
            changed = true;
            return {
              ...entry,
              column: '',
              value: '',
            };
          }
          return entry;
        });
        next[metric] = updated;
      });
      return changed ? next : prev;
    });

    setSopMetadata((prev) => {
      const currentLatestColumns = prev?.latestColumns;
      if (!currentLatestColumns || typeof currentLatestColumns !== 'object') {
        return prev;
      }
      let changed = false;
      const nextLatestColumns = {};
      Object.entries(currentLatestColumns).forEach(([statementName, latestColumn]) => {
        if (
          normaliseKey(latestColumn) === normalizedColumnKey
          && (!affectedStatementKey || normaliseStatementLookupKey(statementName) === affectedStatementKey)
        ) {
          changed = true;
          if (shouldRecalculateSops && nextSopColumn) {
            nextLatestColumns[statementName] = nextSopColumn;
          }
          return;
        }
        nextLatestColumns[statementName] = latestColumn;
      });
      return changed ? { ...prev, latestColumns: nextLatestColumns } : prev;
    });

    setSopSummary((current) => current.map((entry) => {
      if (!entry || !entry.column) {
        return entry;
      }
      if (entry.column.toString().toLowerCase() !== columnKey) {
        return entry;
      }
      if (
        affectedStatementKey
        && normaliseStatementLookupKey(entry.statement) !== affectedStatementKey
      ) {
        return entry;
      }
      return {
        ...entry,
        column: '',
        value: '-',
      };
    }));

    setVerifiedStatements((current) => {
      if (!affectedStatementName) {
        return current;
      }
      const next = {
        ...(current || {}),
      };
      next[affectedStatementName] = false;
      return next;
    });

    setQcComplete(false);
    setStatus({
      type: 'info',
      message: pauseSopRecalculation
        ? `Column "${columnName}" removed from ${affectedStatementName || 'this statement'}. SOP recalculation is paused for that statement until you choose the next target column.`
        : shouldRecalculateSops && removedActiveSopColumn && nextSopColumn
          ? `Column "${columnName}" removed from ${affectedStatementName || 'this statement'}. SOPs recalculated only for that statement using "${nextSopColumn}".`
          : `Column "${columnName}" removed from ${affectedStatementName || 'this statement'}.`,
    });
  };

  const handleResumeSopRecalculation = () => {
    const pausedEntries = Object.entries(pausedSopStatements || {});
    if (!pausedEntries.length) {
      return;
    }

    const nextLatestColumnsByStatement = {};
    pausedEntries.forEach(([statementKey, statementLabel]) => {
      const label = toTrimmed(statementLabel);
      const statementColumns = filterColumnsForStatement(valueColumns, label).filter((column) => (
        lineItems.some((item) => (
          item
          && typeof item === 'object'
          && normaliseStatementLookupKey(item.statement) === statementKey
          && hasPopulatedValue(item?.[column])
        ))
      ));
      const rankedColumns = rankPreferredStatementColumns(statementColumns);
      const nextColumn = rankedColumns[0] || statementColumns[0] || '';
      if (nextColumn) {
        nextLatestColumnsByStatement[label] = nextColumn;
      }
    });

    if (!Object.keys(nextLatestColumnsByStatement).length) {
      setStatus({
        type: 'warning',
        message: 'No value columns are available for the paused statements.',
      });
      return;
    }

    setSopMetadata((prev) => {
      const nextLatestColumns = {
        ...((prev && typeof prev === 'object' && prev.latestColumns) || {}),
      };
      Object.entries(nextLatestColumnsByStatement).forEach(([statementName, nextColumn]) => {
        nextLatestColumns[statementName] = nextColumn;
      });
      return {
        ...(prev || {}),
        latestColumns: nextLatestColumns,
      };
    });
    setPausedSopStatements(buildEmptyPausedSopStatementMap());
    setQcComplete(false);
    setStatus({
      type: 'info',
      message: `SOPs recalculated only for the paused statements.`,
    });
  };

  const handleRenameColumn = (columnName) => {
    const currentName = toTrimmed(columnName);
    if (!currentName || !valueColumns.includes(columnName)) {
      return;
    }

    const nextNameRaw = window.prompt('Rename column', currentName);
    if (nextNameRaw === null) {
      return;
    }

    const nextName = toTrimmed(nextNameRaw);
    if (!nextName) {
      setStatus({ type: 'error', message: 'Column name cannot be empty.' });
      return;
    }
    if (nextName === currentName) {
      return;
    }

    const nextKey = normaliseKey(nextName);
    const duplicate = valueColumns.some((column) => (
      column !== currentName && normaliseKey(column) === nextKey
    ));
    if (duplicate) {
      setStatus({ type: 'error', message: `Column "${nextName}" already exists.` });
      return;
    }

    setValueColumns((cols) => cols.map((column) => (
      column === currentName ? nextName : column
    )));

    setLineItems((prev) => prev.map((item) => {
      if (!Object.prototype.hasOwnProperty.call(item, currentName)) {
        return item;
      }
      const next = { ...item };
      next[nextName] = next[currentName];
      delete next[currentName];
      return next;
    }));

    setManualRow((prev) => {
      const nextValues = { ...(prev.values || {}) };
      if (!Object.prototype.hasOwnProperty.call(nextValues, currentName)) {
        return prev;
      }
      nextValues[nextName] = nextValues[currentName];
      delete nextValues[currentName];
      return {
        ...prev,
        values: nextValues,
      };
    });

    setManualSopEntries((prev) => {
      if (!prev || !Object.keys(prev).length) {
        return prev;
      }
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([metric, entries]) => {
        if (!Array.isArray(entries)) {
          next[metric] = entries;
          return;
        }
        next[metric] = entries.map((entry) => {
          if (!entry || typeof entry !== 'object') {
            return entry;
          }
          let entryChanged = false;
          const updatedCalculation = Array.isArray(entry.calculation)
            ? entry.calculation.map((step) => {
              if (!step || typeof step !== 'object') {
                return step;
              }
              if (normaliseKey(step.column) !== normaliseKey(currentName)) {
                return step;
              }
              entryChanged = true;
              return {
                ...step,
                column: nextName,
              };
            })
            : entry.calculation;

          const nextEntry = {
            ...entry,
            calculation: updatedCalculation,
          };

          if (normaliseKey(entry.column) === normaliseKey(currentName)) {
            nextEntry.column = nextName;
            entryChanged = true;
          }

          if (entryChanged) {
            changed = true;
          }
          return nextEntry;
        });
      });
      return changed ? next : prev;
    });

    setBreakdownDrafts((prev) => {
      if (!prev || !Object.keys(prev).length) {
        return prev;
      }
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([metric, draft]) => {
        if (!draft || typeof draft !== 'object') {
          next[metric] = draft;
          return;
        }
        const updatedDraft = { ...draft };
        if (normaliseKey(updatedDraft.column) === normaliseKey(currentName)) {
          updatedDraft.column = nextName;
          changed = true;
        }
        if (Array.isArray(updatedDraft.calculation)) {
          updatedDraft.calculation = updatedDraft.calculation.map((step) => {
            if (!step || typeof step !== 'object') {
              return step;
            }
            if (normaliseKey(step.column) !== normaliseKey(currentName)) {
              return step;
            }
            changed = true;
            return {
              ...step,
              column: nextName,
            };
          });
        }
        next[metric] = updatedDraft;
      });
      return changed ? next : prev;
    });

    setSopSummary((current) => current.map((entry) => {
      if (!entry || normaliseKey(entry.column) !== normaliseKey(currentName)) {
        return entry;
      }
      return {
        ...entry,
        column: nextName,
      };
    }));

    setSopMetadata((prev) => {
      const currentLatestColumns = prev?.latestColumns;
      if (!currentLatestColumns || typeof currentLatestColumns !== 'object') {
        return prev;
      }
      let changed = false;
      const nextLatestColumns = {};
      Object.entries(currentLatestColumns).forEach(([statementName, latestColumn]) => {
        if (normaliseKey(latestColumn) === normaliseKey(currentName)) {
          nextLatestColumns[statementName] = nextName;
          changed = true;
        } else {
          nextLatestColumns[statementName] = latestColumn;
        }
      });
      return changed ? { ...prev, latestColumns: nextLatestColumns } : prev;
    });

    setSopEditDraft((prev) => {
      if (!prev || normaliseKey(prev.column) !== normaliseKey(currentName)) {
        return prev;
      }
      return {
        ...prev,
        column: nextName,
      };
    });

    setVerifiedStatements(() => {
      const next = {};
      statements.forEach((statement) => {
        next[statement] = false;
      });
      return next;
    });

    setQcComplete(false);
    setStatus({ type: 'success', message: `Column renamed from "${currentName}" to "${nextName}".` });
  };

  function applyExtractionResponse(responseData, options = {}) {
    if (!responseData) {
      return;
    }

    const sourceType = responseData?.sourceType || 'pdf_upload';
    const extractionMeta = responseData?.extractionMeta || {};
    const extractedCount = Number(extractionMeta?.extractedCount || 0);
    const expectedCount = Number(extractionMeta?.expectedCount || 5);
    const missingStatements = Array.isArray(extractionMeta?.missingStatements)
      ? extractionMeta.missingStatements.filter(Boolean)
      : [];
    const hasServerPdf = Boolean(responseData?.pdfBase64);
    const pdfReady = typeof extractionMeta?.pdfReady === 'boolean'
      ? extractionMeta.pdfReady
      : hasServerPdf;
    const sourceReady = typeof extractionMeta?.sourceReady === 'boolean'
      ? extractionMeta.sourceReady
      : pdfReady || Boolean(responseData?.lineItems?.length);

    const nextValueColumns = responseData.valueColumns || [];
    const preferSubmittedSopValues = Boolean(options?.preferSubmittedSopValues);
    const sopEntries = normaliseSopSummaryEntries(responseData.sopSummary).map((entry) => (
      preferSubmittedSopValues
        ? { ...entry, manual: true }
        : entry
    ));
    const candidateMetricList = Array.isArray(responseData.candidateMetrics)
      ? responseData.candidateMetrics
      : [];

    const incomingLineItems = responseData.lineItems || [];
    const sanitizedLineItems = incomingLineItems.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const { verified: _discardVerified, Verified: _discardVerifiedUpper, ...rest } = item;
      const normalisedClassification = resolveSopMetricName(rest.classification || rest.sopMetric || '');
      const classification = toTrimmed(normalisedClassification);
      const classificationSource = normaliseKey(rest.classificationSource) === 'manual'
        ? 'manual'
        : (classification ? 'auto' : '');
      return {
        ...rest,
        classification,
        classificationSource,
      };
    });

    const lineLookup = new Map();
    const assignedMetricKeys = new Set();
    sanitizedLineItems.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const statementKey = (item.statement || '').toString().toLowerCase().trim();
      const labelKey = (item.lineItem || item['Line Item'] || '').toString().toLowerCase().trim();
      const metricKey = normaliseMetricKey(item.classification);
      if (metricKey) {
        assignedMetricKeys.add(metricKey);
      }
      if (!statementKey || !labelKey) {
        return;
      }
      const key = `${statementKey}||${labelKey}`;
      if (!lineLookup.has(key)) {
        lineLookup.set(key, item);
      }
    });

    const initialManualEntries = {};
    sopEntries.forEach((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const statementKey = entry.statement ? entry.statement.toString().toLowerCase().trim() : '';
      const lineKey = entry.sourceLine ? entry.sourceLine.toString().toLowerCase().trim() : '';
      const lookupKey = statementKey && lineKey ? `${statementKey}||${lineKey}` : '';
      if (lookupKey && lineLookup.has(lookupKey)) {
        const targetRow = lineLookup.get(lookupKey);
        const metricKey = normaliseMetricKey(entry.metric);
        if (targetRow && !targetRow.classification && entry.metric && !assignedMetricKeys.has(metricKey)) {
          targetRow.classification = entry.metric;
          targetRow.classificationSource = 'auto';
          if (metricKey) {
            assignedMetricKeys.add(metricKey);
          }
        }
        return;
      }
      const hasDetails = [entry.statement, entry.column, entry.sourceLine, entry.value].some((field) => {
        if (field === null || typeof field === 'undefined') {
          return false;
        }
        const text = field.toString().trim();
        return Boolean(text && text !== '-');
      });
      if (!hasDetails || !entry.manual) {
        return;
      }
      if (!initialManualEntries[entry.metric]) {
        initialManualEntries[entry.metric] = [];
      }
      initialManualEntries[entry.metric].push({
        id: `seed-${index}`,
        statement: entry.statement || '',
        lineItem: entry.sourceLine || '',
        column: entry.column || '',
        value: entry.value === '-' ? '' : (entry.value || ''),
        calculation: Array.isArray(entry.calculation)
          ? entry.calculation.map((step) => ({
            ...createEmptyCalculationStep(),
            ...step,
            operator: step?.operator || '+',
            metric: (step?.metric || '').trim(),
            statement: (step?.statement || '').trim(),
            lineItem: (step?.lineItem || '').trim(),
            column: (step?.column || '').trim(),
            constant: (step?.constant || '').trim(),
          }))
          : [],
      });
    });

    const statementsFromResponse = Array.from(new Set(sanitizedLineItems.map((item) => item?.statement))).filter(Boolean);
    const emptyValues = nextValueColumns.reduce((acc, column) => ({ ...acc, [column]: '' }), {});

    setUploadMode(sourceType);
    setPdfName(responseData.pdfName || '');
    setPdfBase64(sourceType === 'pdf_upload' && pdfReady ? (responseData.pdfBase64 || '') : '');
    setLineItems(sanitizedLineItems);
    setCandidateMetrics(candidateMetricList);
    setValueColumns(nextValueColumns);
    setStatementMultiplierApplied({});
    setSopSummary(sopEntries);
    setPausedSopStatements(buildEmptyPausedSopStatementMap());
    const initialMetadata = responseData.sopMetadata || { latestColumns: {} };
    const savedAdjustmentOption = initialMetadata.applyProfitLossQ4CumulativeAdjustment;
    // Use isTrue so we only force-restore when the server explicitly saved it as true.
    // When saved value is false/undefined we keep the user's current live state (they may have
    // toggled it ON since the last server load — never overwrite ON → OFF on a background refresh).
    const savedAsTrue = isTrue(savedAdjustmentOption);
    setApplyProfitLossQ4CumulativeAdjustment((current) => savedAsTrue ? true : current);
    setSopMetadata({
      ...initialMetadata,
      applyProfitLossQ4CumulativeAdjustment: savedAsTrue,
    });
    setManualSopEntries(initialManualEntries);
    setExpandedSopMetrics({});
    setBreakdownDrafts({});
    setEditingSopMetric(null);
    setSopEditDraft(buildEmptySopEditDraft());
    setSopSearchTerm('');
    setSopStatusFilter('all');
    setSopStatementFilter('all');
    setCashFlowScaleKey('none');
    setVerifiedStatements(() => {
      const map = {};
      statementsFromResponse.forEach((statement) => {
        map[statement] = false;
      });
      return map;
    });
    setShowManualEntry(false);
    setManualRow({
      statement: statementsFromResponse[0] || '',
      lineItem: '',
      values: emptyValues,
    });
    setPdfZoom(1);
    setActiveWorkspaceTab(sanitizedLineItems.length ? 'statements' : 'overview');

    const savedReport = responseData?.savedReport;
    let successMessage = '';
    if (!sourceReady) {
      const missingLabel = missingStatements.length
        ? ` Missing: ${missingStatements.join(', ')}.`
        : '';
      successMessage = sourceType === 'statement_upload'
        ? `Statement extraction incomplete (${extractedCount}/${expectedCount} statements).${missingLabel}`
        : `Extraction incomplete (${extractedCount}/${expectedCount} statements). PDF preview unlocks only after all ${expectedCount} statements are extracted.${missingLabel}`;
    } else {
      successMessage =
        savedReport?.ticker_symbol && savedReport?.fiscal_year
          ? `Extraction complete. ${savedReport.ticker_symbol} FY ${savedReport.fiscal_year} is ready for statement review.`
          : 'Extraction complete. Review each statement and mark it as reviewed when done.';
    }
    setStatus({ type: sourceReady ? 'success' : 'warning', message: successMessage });
  }

  useEffect(() => {
    const workflowId = Number(searchParams.get("workflow_id") || 0);
    const versionId = Number(searchParams.get("version_id") || 0);
    if (!workflowId) {
      return;
    }

    const hydrateKey = `${workflowId}-${versionId || "current"}`;
    if (hydratedWorkflowRef.current === hydrateKey) {
      return;
    }

    let cancelled = false;
    const hydrateWorkflowVersion = async () => {
      setHydratingWorkflowVersion(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/annual-report/workflows/version`,
          {
            headers: authHeaders,
            params: {
              workflow_id: workflowId,
              version_id: versionId > 0 ? versionId : undefined,
            },
          }
        );
        if (cancelled) {
          return;
        }

        const workflow = response?.data?.workflow || {};
        const payload = response?.data?.payload || {};
        const reviewSnapshot =
          payload?.reviewSnapshot && typeof payload.reviewSnapshot === "object"
            ? payload.reviewSnapshot
            : {};

        const lineItemsFromSnapshot = rebuildLineItemsFromReviewSnapshot(reviewSnapshot);
        const snapshotValueColumns = Array.isArray(reviewSnapshot?.metadata?.valueColumns)
          ? reviewSnapshot.metadata.valueColumns.map((column) => toTrimmed(column)).filter(Boolean)
          : [];
        const derivedValueColumns = snapshotValueColumns.length
          ? snapshotValueColumns
          : Array.from(new Set(
            lineItemsFromSnapshot.flatMap((row) =>
              Object.keys(row || {}).filter((key) => (
                key !== "rowId"
                && key !== "statement"
                && key !== "lineItem"
                && key !== "Line Item"
                && key !== "classification"
                && key !== "classificationSource"
                && key !== "aiConfidence"
              ))
            )
          ));

        const sopSummary = Array.isArray(payload?.sopSummary) && payload.sopSummary.length
          ? payload.sopSummary
          : Array.isArray(reviewSnapshot?.sopSummary)
            ? reviewSnapshot.sopSummary
            : [];
        const reviewedStatements =
          (reviewSnapshot?.metadata?.reviewedStatements &&
            typeof reviewSnapshot.metadata.reviewedStatements === "object")
            ? reviewSnapshot.metadata.reviewedStatements
            : payload?.reviewedStatements && typeof payload.reviewedStatements === "object"
              ? payload.reviewedStatements
              : {};
        const statementImageSources = [
          reviewSnapshot?.statementUploadImagesByStatement,
          reviewSnapshot?.metadata?.statementUploadImagesByStatement,
          payload?.statementUploadImagesByStatement,
          payload?.metadata?.statementUploadImagesByStatement,
        ].filter((source) => source && typeof source === "object");
        const countStatementImages = (imagesByStatement) => Object.values(imagesByStatement || {}).reduce(
          (total, images) => total + (Array.isArray(images) ? images.length : 0),
          0
        );
        let restoredStatementImages = buildEmptyStatementUploadImages();
        let restoredStatementImageCount = 0;
        for (const sourceImages of statementImageSources) {
          const normalizedImages = normaliseStatementUploadImagesByStatement(sourceImages);
          const normalizedImageCount = countStatementImages(normalizedImages);
          if (normalizedImageCount > 0) {
            restoredStatementImages = normalizedImages;
            restoredStatementImageCount = normalizedImageCount;
            break;
          }
        }

        applyExtractionResponse({
          sourceType: "statement_upload",
          pdfName: payload?.pdfName || reviewSnapshot?.metadata?.pdfName || "",
          lineItems: lineItemsFromSnapshot,
          valueColumns: derivedValueColumns,
          sopSummary,
          sopMetadata:
            payload?.sopMetadata && typeof payload.sopMetadata === "object"
              ? payload.sopMetadata
              : {},
          candidateMetrics: SOP_METRICS,
          extractionMeta: {
            sourceReady: true,
            pdfReady: false,
            expectedCount: 5,
            extractedCount: 5,
            missingStatements: [],
          },
        }, {
          quarter: Number(workflow?.quarter_number || 0)
        });
        setStatementUploadImagesByStatement(restoredStatementImages);
        if (restoredStatementImageCount > 0) {
          setUploadMode("statement_upload");
        }

        const importedManualEntries = normaliseImportedManualSopEntries(
          payload?.manualSopAdjustments ||
          reviewSnapshot?.manualSopAdjustments ||
          {}
        );
        setManualSopEntries(importedManualEntries);
        setVerifiedStatements(reviewedStatements || {});
        setQcComplete(Boolean(reviewSnapshot?.metadata?.qcComplete));

        const rawConv =
          payload?.currencyConversion ||
          payload?.currency_conversion ||
          payload?.sopMetadata?.currencyConversion ||
          payload?.sopMetadata?.currency_conversion ||
          reviewSnapshot?.currencyConversion ||
          reviewSnapshot?.currency_conversion;
        if (rawConv) {
          setConvertCurrency(Boolean(rawConv.enabled || rawConv.enable_currency_conversion));
          if (rawConv.baseCurrency || rawConv.base_currency || rawConv.sourceCurrency || rawConv.source_currency) {
            setBaseCurrency(String(rawConv.baseCurrency || rawConv.base_currency || rawConv.sourceCurrency || rawConv.source_currency).toUpperCase());
          }
          if (rawConv.targetCurrency || rawConv.target_currency) {
            setTargetCurrency(String(rawConv.targetCurrency || rawConv.target_currency).toUpperCase());
          }
        }

        if (workflow?.ticker_symbol) {
          setSelectedTicker(String(workflow.ticker_symbol).trim().toUpperCase());
        }
        if (workflow?.fiscal_year) {
          setFiscalYear(String(workflow.fiscal_year));
        }
        if (workflow?.quarter_date) {
          setSelectedQuarterDate(String(workflow.quarter_date));
        }

        hydratedWorkflowRef.current = hydrateKey;
        setStatus({
          type: "success",
          message: `Loaded workflow #${workflowId} version ${versionId > 0 ? versionId : "current"} for recheck and edit.`,
        });
      } catch (error) {
        if (!cancelled) {
          setStatus({
            type: "error",
            message: "Failed to load selected workflow version into editor.",
          });
        }
      } finally {
        if (!cancelled) {
          setHydratingWorkflowVersion(false);
        }
      }
    };

    hydrateWorkflowVersion();
    return () => {
      cancelled = true;
    };
  }, [authHeaders, searchParams]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!extractionPrerequisitesReady) {
      setStatus({
        type: 'warning',
        message: 'Select stock company, fiscal year, and target quarter before extracting data.',
      });
      if (event.target) {
        event.target.value = '';
      }
      return;
    }
    let keepLoadingForPolling = false;

    setLoading(true);
    resetExtractionWorkspace();
    setStatus({
      type: 'info',
      message: 'Uploading PDF to the server... This can take 2–3 minutes.',
    });

    console.group('%c[Falcon AI] Annual Report Extraction', 'color:#7c3aed;font-weight:bold;font-size:14px');
    console.log('%c[Step 1] File selected', 'color:#2563eb;font-weight:bold', {
      name: file.name,
      size: `${(file.size / 1024).toFixed(1)} KB`,
      type: file.type,
    });

    try {
      const pdfBase64 = await readFileAsDataUrl(file);
      console.log('%c[Step 2] PDF read as base64', 'color:#2563eb;font-weight:bold', {
        base64Length: pdfBase64.length,
        preview: pdfBase64.slice(0, 50) + '...',
      });

      const requestPayload = {
        input_mode: 'pdf_upload',
        pdf_base64: pdfBase64,
        pdf_name: file.name,
        async: true,
        apply_profit_loss_q4_cumulative_adjustment: Boolean(
          showProfitLossQ4CumulativeAdjustment && applyProfitLossQ4CumulativeAdjustment
        ),
        currency_conversion: {
          enabled: convertCurrency,
          base_currency: baseCurrency,
          target_currency: targetCurrency,
        },
      };
      if (selectedStock?.id) {
        requestPayload.stock_id = selectedStock.id;
        requestPayload.stock_ids = selectedStocks.map((s) => s.id);
      }
      if (selectedTicker && validFiscalYear) {
        requestPayload.ticker_symbol = selectedTicker;
        requestPayload.ticker_symbols = selectedTickers;
        requestPayload.fiscal_year = validFiscalYear;
      }
      if (selectedQuarter) {
        requestPayload.selected_quarter_date = selectedQuarter.date;
        requestPayload.selected_quarter_year = selectedQuarter.year;
        requestPayload.selected_quarter_number = selectedQuarter.quarter;
        requestPayload.selected_quarter_label = selectedQuarter.label;
      }

      console.log('%c[Step 3] Sending PDF to server for LLM extraction...', 'color:#2563eb;font-weight:bold', {
        endpoint: `${API_BASE_URL}/api/process`,
        hasAuthToken: Boolean(authHeaders.Authorization),
        asyncMode: true,
        ticker: requestPayload.ticker_symbol || '',
        fiscalYear: requestPayload.fiscal_year || '',
        selectedQuarter: requestPayload.selected_quarter_date || '',
        profitLossQ4Adjustment: requestPayload.apply_profit_loss_q4_cumulative_adjustment,
      });

      const response = await axios.post(
        `${API_BASE_URL}/api/process`,
        requestPayload,
        { headers: authHeaders },
      );

      console.log('%c[Step 4] Server response received', 'color:#16a34a;font-weight:bold', {
        status: response.status,
        hasData: Boolean(response.data),
      });

      const data = response.data;
      if (data?.jobId) {
        console.log('%c[Step 5] Async job accepted by server', 'color:#16a34a;font-weight:bold', {
          jobId: data.jobId,
          status: data.status,
        });
        console.log('%c[Step 6] Polling background progress logs...', 'color:#2563eb;font-weight:bold');
        setJobId(data.jobId);
        setJobStatus(data.status || 'processing');
        keepLoadingForPolling = true;
        setStatus({
          type: 'info',
          message: 'PDF uploaded. Falcon AI is extracting text and statements in the background...',
        });
        console.groupEnd();
        return;
      }

      // Log PDF extraction result
      console.log('%c[Step 5] PDF Text Extraction (Configured Provider)', 'color:#2563eb;font-weight:bold', {
        pdfName: data?.pdfName,
        hasPdfBase64: Boolean(data?.pdfBase64),
      });

      // Log each statement extraction
      const statementsStatus = data?.extractionMeta?.statements || [];
      if (statementsStatus.length) {
        console.group('%c[Step 6] Financial Statement Extraction Results', 'color:#2563eb;font-weight:bold');
        statementsStatus.forEach((s) => {
          if (s.extracted) {
            console.log(`%c  ✅ ${s.statement}`, 'color:#16a34a;font-weight:bold', `${s.rows} rows × ${s.columns} columns`);
          } else {
            console.warn(`  ❌ ${s.statement}`, `rows=${s.rows}, cols=${s.columns} — NOT extracted`);
          }
        });
        console.groupEnd();
      } else {
        console.warn('%c[Step 6] No extractionMeta.statements in response', 'color:#dc2626');
      }

      // Log dataframes
      const dataframes = data?.dataframes || {};
      const dfKeys = Object.keys(dataframes);
      console.log('%c[Step 7] Parsed Dataframes', 'color:#2563eb;font-weight:bold', {
        count: dfKeys.length,
        statements: dfKeys,
        detail: dfKeys.reduce((acc, k) => {
          acc[k] = `${dataframes[k]?.rows?.length ?? 0} rows, ${dataframes[k]?.headers?.length ?? 0} headers`;
          return acc;
        }, {}),
      });

      // Log lineItems
      const lineItemsFromServer = data?.lineItems || [];
      console.log('%c[Step 8] Line Items (from backend)', 'color:#2563eb;font-weight:bold', {
        count: lineItemsFromServer.length,
        byStatement: lineItemsFromServer.reduce((acc, item) => {
          acc[item.statement] = (acc[item.statement] || 0) + 1;
          return acc;
        }, {}),
        sample: lineItemsFromServer.slice(0, 3),
      });

      // Log valueColumns
      const valueColumnsData = data?.valueColumns || [];
      console.log('%c[Step 9] Value Columns', 'color:#2563eb;font-weight:bold', valueColumnsData);

      // Log SOP summary
      const sopSummaryData = data?.sopSummary || [];
      const populatedSopMetrics = sopSummaryData.filter((e) => e?.value && e.value !== '-');
      console.log('%c[Step 10] SOP Summary', 'color:#2563eb;font-weight:bold', {
        total: sopSummaryData.length,
        populated: populatedSopMetrics.length,
        populated_details: populatedSopMetrics,
      });

      // Log AI classifications
      const aiSuggestions = data?.aiSuggestions || {};
      console.log('%c[Step 11] AI Classifications', 'color:#2563eb;font-weight:bold', {
        classifiedRows: Object.keys(aiSuggestions).length,
      });

      // Final diagnosis
      if (!lineItemsFromServer.length) {
        console.error(
          '%c[DIAGNOSIS] ❌ NO LINE ITEMS extracted.\n' +
          '  Possible causes:\n' +
          '  1. PDF extractor failed to extract text (check server terminal logs)\n' +
          '  2. LLM returned "No table found" for all 5 statements\n' +
          '  3. The extracted markdown table failed validation (< 100 chars)\n' +
          '  4. dataframes was empty after parsing\n' +
          '  5. All rows were filtered out (no label in first column)',
          'color:#dc2626;font-weight:bold'
        );
      } else if (!valueColumnsData.length) {
        console.error(
          '%c[DIAGNOSIS] ❌ No value columns. Rows exist but no numeric columns were found.',
          'color:#dc2626;font-weight:bold'
        );
      } else {
        console.log(
          `%c[DIAGNOSIS] ✅ Extraction successful! ${lineItemsFromServer.length} line items, ${valueColumnsData.length} columns.`,
          'color:#16a34a;font-weight:bold'
        );
      }

      // Everything is calculated server-side before the response is sent.
      // Apply the full result (PDF + statements + SOPs) all at once.
      applyExtractionResponse(data, { quarter: Number(selectedQuarter?.quarter || 0) });
      console.log('%c[Step 12] State applied to UI. Switch to "Statements" tab to view.', 'color:#16a34a;font-weight:bold');
      console.groupEnd();
    } catch (err) {
      console.error('%c[Falcon AI] ❌ EXTRACTION FAILED', 'color:#dc2626;font-weight:bold', err);
      console.groupEnd();
      const serverError = (err as any)?.response?.data;
      if (serverError) {
        console.error('[Falcon AI] Server error payload:', serverError);
      }
      const baseMessage = serverError?.error || 'Failed to process PDF. Please try again.';
      const details = serverError?.details;
      const errorMessage = details ? `${baseMessage} (${details})` : baseMessage;
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      setError(errorMessage);
      setStatus({ type: 'error', message: errorMessage });
      setSopSummary(buildEmptySopSummary());
      setPausedSopStatements(buildEmptyPausedSopStatementMap());
      setSopMetadata({ latestColumns: {} });
      setEditingSopMetric(null);
      setSopEditDraft(buildEmptySopEditDraft());
    } finally {
      if (!keepLoadingForPolling) {
        setLoading(false);
      }
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const appendStatementImages = useCallback(async (statementName, files, source = 'upload') => {
    const statementLabel = getStatementUploadLabel(statementName);
    const currentImages = Array.isArray(statementUploadImagesByStatement?.[statementName])
      ? statementUploadImagesByStatement[statementName]
      : [];

    const imageFiles = Array.from(files || []).filter((file) => {
      const mimeType = String(file?.type || '').toLowerCase();
      return mimeType.startsWith('image/');
    });

    if (!imageFiles.length) {
      setStatus({
        type: 'warning',
        message: `${statementLabel}: no image found. Upload or paste a screenshot image.`,
      });
      return;
    }

    const availableSlots = Math.max(
      0,
      STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION - currentImages.length
    );
    const filesToRead = imageFiles.slice(0, availableSlots);
    if (!filesToRead.length) {
      setStatus({
        type: 'warning',
        message: `You can upload a maximum of ${STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION} images for ${statementLabel}.`,
      });
      return;
    }

    try {
      const now = Date.now();
      const nextImages = await Promise.all(filesToRead.map(async (file, fileIndex) => {
        const mimeType = file.type || 'image/png';
        const extension = mimeType.includes('/')
          ? mimeType.split('/')[1]
          : 'png';
        const fallbackName = `${source}-${statementName}-${now}-${fileIndex + 1}.${extension}`;
        const safeName = String(file?.name || '').trim() || fallbackName;
        return {
          id: `${safeName}-${file.size}-${now}-${Math.random().toString(36).slice(2, 7)}`,
          name: safeName,
          mimeType,
          size: file.size,
          dataUrl: await readFileAsDataUrl(file),
        };
      }));

      setStatementUploadImagesByStatement((prev) => ({
        ...(prev || {}),
        [statementName]: [...currentImages, ...nextImages],
      }));

      if (imageFiles.length > filesToRead.length) {
        setStatus({
          type: 'warning',
          message: `Only the first ${STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION} images were kept for ${statementLabel}.`,
        });
      } else {
        const actionLabel = source === 'pasted' ? 'pasted' : 'added';
        setStatus({
          type: 'info',
          message: `${statementLabel}: ${nextImages.length} image${nextImages.length === 1 ? '' : 's'} ${actionLabel}.`,
        });
      }
    } catch (imageError) {
      setStatus({ type: 'error', message: imageError?.message || 'Failed to read one or more statement images.' });
    }
  }, [readFileAsDataUrl, statementUploadImagesByStatement]);

  const handleStatementImageSelection = async (statementName, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      return;
    }
    try {
      await appendStatementImages(statementName, selectedFiles, 'upload');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleStatementImagePaste = useCallback(async (statementName, event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    const clipboardItems = Array.from(event?.clipboardData?.items || []);
    const pastedFiles = clipboardItems
      .filter((item) => item?.kind === 'file' && String(item?.type || '').toLowerCase().startsWith('image/'))
      .map((item, index) => {
        const file = item.getAsFile();
        if (!file) {
          return null;
        }
        if (file.name) {
          return file;
        }
        const mimeType = file.type || 'image/png';
        const extension = mimeType.includes('/') ? mimeType.split('/')[1] : 'png';
        return new File([file], `pasted-image-${Date.now()}-${index + 1}.${extension}`, { type: mimeType });
      })
      .filter(Boolean);

    await appendStatementImages(statementName, pastedFiles, 'pasted');

    if (event?.target && typeof event.target.value === 'string') {
      event.target.value = '';
    }
  }, [appendStatementImages]);

  const handleRemoveStatementImage = (statementName, imageId) => {
    const statementLabel = getStatementUploadLabel(statementName);
    setStatementUploadImagesByStatement((prev) => {
      const currentImages = Array.isArray(prev?.[statementName]) ? prev[statementName] : [];
      return {
        ...(prev || {}),
        [statementName]: currentImages.filter((image) => image.id !== imageId),
      };
    });
    setStatus({ type: 'info', message: `${statementLabel}: image removed.` });
  };

  const handleStatementUploadSubmit = async () => {
    if (!extractionPrerequisitesReady) {
      setStatus({
        type: 'warning',
        message: 'Select stock company, fiscal year, and target quarter before extracting data.',
      });
      return;
    }

    const statementPayloads = STATEMENT_UPLOAD_OPTIONS.map((option) => ({
      statement: option.value,
      label: option.label,
      images: Array.isArray(statementUploadImagesByStatement?.[option.value])
        ? statementUploadImagesByStatement[option.value]
        : [],
    }));
    const missingStatements = statementPayloads
      .filter((entry) => !entry.images.length)
      .map((entry) => entry.label);
    if (missingStatements.length) {
      setStatus({
        type: 'warning',
        message: `Upload at least one image for: ${missingStatements.join(', ')}.`,
      });
      return;
    }
    const totalImageCount = statementPayloads.reduce((total, entry) => total + entry.images.length, 0);

    let keepLoadingForPolling = false;
    setLoading(true);
    resetExtractionWorkspace();
    setStatus({
      type: 'info',
      message: `Uploading ${totalImageCount} statement image${totalImageCount === 1 ? '' : 's'} across ${statementPayloads.length} statements...`,
    });

    console.group('%c[Falcon AI] Multi-Statement Image Extraction', 'color:#7c3aed;font-weight:bold;font-size:14px');
    console.log('%c[Step 1] Statement upload prepared', 'color:#2563eb;font-weight:bold', {
      statementCount: statementPayloads.length,
      totalImageCount,
      statements: statementPayloads.map((entry) => ({
        statement: entry.statement,
        imageCount: entry.images.length,
        images: entry.images.map((image) => ({
          name: image.name,
          mimeType: image.mimeType,
          sizeKb: `${(image.size / 1024).toFixed(1)} KB`,
        })),
      })),
    });

    try {
      const requestPayload = {
        input_mode: 'statement_upload',
        statement_payloads: statementPayloads.map((entry) => ({
          statement_name: entry.statement,
          images: entry.images.map((image) => ({
            name: image.name,
            mimeType: image.mimeType,
            base64: image.dataUrl,
          })),
        })),
        async: true,
        apply_profit_loss_q4_cumulative_adjustment: Boolean(
          showProfitLossQ4CumulativeAdjustment && applyProfitLossQ4CumulativeAdjustment
        ),
        currency_conversion: {
          enabled: convertCurrency,
          base_currency: baseCurrency,
          target_currency: targetCurrency,
        },
      };
      if (selectedStock?.id) {
        requestPayload.stock_id = selectedStock.id;
        requestPayload.stock_ids = selectedStocks.map((s) => s.id);
      }
      if (selectedTicker && validFiscalYear) {
        requestPayload.ticker_symbol = selectedTicker;
        requestPayload.ticker_symbols = selectedTickers;
        requestPayload.fiscal_year = validFiscalYear;
      }
      if (selectedQuarter) {
        requestPayload.selected_quarter_date = selectedQuarter.date;
        requestPayload.selected_quarter_year = selectedQuarter.year;
        requestPayload.selected_quarter_number = selectedQuarter.quarter;
        requestPayload.selected_quarter_label = selectedQuarter.label;
      }

      console.log('%c[Step 2] Sending statement images to server...', 'color:#2563eb;font-weight:bold', {
        endpoint: `${API_BASE_URL}/api/process`,
        statementCount: requestPayload.statement_payloads.length,
        imageCount: totalImageCount,
        ticker: requestPayload.ticker_symbol || '',
        fiscalYear: requestPayload.fiscal_year || '',
        selectedQuarter: requestPayload.selected_quarter_date || '',
        profitLossQ4Adjustment: requestPayload.apply_profit_loss_q4_cumulative_adjustment,
      });

      const response = await axios.post(
        `${API_BASE_URL}/api/process`,
        requestPayload,
        { headers: authHeaders },
      );

      const data = response.data;
      if (data?.jobId) {
        setJobId(data.jobId);
        setJobStatus(data.status || 'processing');
        keepLoadingForPolling = true;
        setStatus({
          type: 'info',
          message: `Statement images uploaded. Falcon AI is extracting all uploaded statements...`,
        });
        console.groupEnd();
        return;
      }

      applyExtractionResponse(data, { quarter: Number(selectedQuarter?.quarter || 0) });
      console.log('%c[Step 3] Statement image extraction applied to UI.', 'color:#16a34a;font-weight:bold');
      console.groupEnd();
    } catch (err) {
      console.error('%c[Falcon AI] Statement image extraction failed', 'color:#dc2626;font-weight:bold', err);
      console.groupEnd();
      const serverError = err?.response?.data;
      const baseMessage = serverError?.error || 'Failed to process statement images. Please try again.';
      const details = serverError?.details;
      const errorMessage = details ? `${baseMessage} (${details})` : baseMessage;
      setError(errorMessage);
      setStatus({ type: 'error', message: errorMessage });
      setSopSummary(buildEmptySopSummary());
      setPausedSopStatements(buildEmptyPausedSopStatementMap());
      setSopMetadata({ latestColumns: {} });
      setEditingSopMetric(null);
      setSopEditDraft(buildEmptySopEditDraft());
    } finally {
      if (!keepLoadingForPolling) {
        setLoading(false);
      }
    }
  };

  const handleFinalize = () => {
    if (!totalRows) {
      setStatus({ type: 'warning', message: 'No line items available to finalise.' });
      return;
    }
    if (pendingStatements > 0) {
      setStatus({ type: 'error', message: 'Please mark every statement as reviewed before finalising.' });
      return;
    }
    setQcComplete(true);
    setStatus({ type: 'success', message: 'QC complete. You may now export the statement-wise JSON.' });
  };

  const handleSaveFinalizedMetrics = async () => {
    if (!qcComplete) {
      setStatus({ type: 'error', message: 'Finalize QC before saving to the database.' });
      return;
    }
    if (!selectedTickers.length) {
      setStatus({ type: 'error', message: 'Select at least one stock ticker before saving.' });
      return;
    }
    if (!validFiscalYear) {
      setStatus({ type: 'error', message: 'Enter a valid fiscal year (YYYY) before saving.' });
      return;
    }
    if (!selectedQuarter) {
      setStatus({ type: 'error', message: 'Select the exact target quarter before saving Falcon data.' });
      return;
    }
    if (!displayedSopSummary.length) {
      setStatus({ type: 'warning', message: 'No SOP summary rows are available to save.' });
      return;
    }
    const saveableSopSummary = displayedSopSummary
      .map((entry) => {
        // When P/L cumulative is ON, override value with quarterValue so the DB stores the subtracted result
        const plAdj = entry?.profitLossQuarterAdjustment;
        if (
          applyProfitLossQ4CumulativeAdjustment &&
          plAdj &&
          !plAdj._disabled &&
          plAdj.quarterValue
        ) {
          return { ...entry, value: plAdj.quarterValue };
        }
        return entry;
      })
      .filter((entry) => parseNumericValue(entry?.value) !== null);
    if (!saveableSopSummary.length) {
      setStatus({
        type: 'warning',
        message: 'No numeric SOP values found. Only metrics with values can be saved.',
      });
      return;
    }

    const sourceVersionId = Number(searchParams.get('version_id') || 0);
    setSavingReview(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/finalize`,
        {
          stock_id: selectedStock?.id || undefined,
          ticker_symbol: selectedTicker,
          stock_ids: selectedStocks.map((s) => s.id),
          ticker_symbols: selectedTickers,
          fiscal_year: validFiscalYear,
          selected_quarter_date: selectedQuarter.date,
          selected_quarter_year: selectedQuarter.year,
          selected_quarter_number: selectedQuarter.quarter,
          selected_quarter_label: selectedQuarter.label,
          pdf_name: pdfName || null,
          qc_complete: true,
          reviewed_statements: verifiedStatements,
          sop_summary: saveableSopSummary,
          manual_sop_adjustments: manualSopEntries,
          sop_metadata: {
            ...sopMetadata,
            currency_conversion: {
              enabled: convertCurrency,
              base_currency: baseCurrency,
              target_currency: targetCurrency,
            },
          },
          currency_conversion: {
            enabled: convertCurrency,
            base_currency: baseCurrency,
            target_currency: targetCurrency,
          },
          review_snapshot: finalizedReviewSnapshot,
          from_version_id: sourceVersionId > 0 ? sourceVersionId : undefined,
          authUser: {
            id: authUser?.id || authUser?.userId || null,
            userId: authUser?.userId || authUser?.id || null,
            role_id: authUser?.role_id || null,
            role_name: authUser?.role_name || null,
            full_name: authUser?.full_name || authUser?.fullName || authUser?.name || null,
            name: authUser?.name || authUser?.fullName || authUser?.full_name || null,
          },
        },
        {
          headers: authHeaders,
        },
      );

      await loadQuarterContext();
      await loadWorkflowHistory();

      const workflowId = Number(response?.data?.workflow?.id || 0);
      const versionNo = Number(response?.data?.version?.version_no || 0);
      const serverMessage = response?.data?.message;
      const partialFailure = response?.data?.partialFailure === true;
      const failedWorkflows: Array<{ tickerSymbol: string; details: string }> =
        Array.isArray(response?.data?.failedWorkflows) ? response.data.failedWorkflows : [];

      if (partialFailure && failedWorkflows.length > 0) {
        // Some stocks succeeded, some failed — show a warning with per-stock details.
        const failedLines = failedWorkflows
          .map((f) => `• ${f.tickerSymbol}: ${f.details}`)
          .join('\n');
        setStatus({
          type: 'warning',
          message: serverMessage
            ? `${serverMessage}\n\nFailed stocks:\n${failedLines}`
            : `Submitted for some stocks, but the following failed:\n${failedLines}`,
        });
      } else {
        setStatus({
          type: 'success',
          message: serverMessage || (workflowId
            ? `Submitted Falcon workflow #${workflowId} (version ${versionNo || '-'}) for senior QC approval.`
            : 'Submitted finalized Falcon metrics for senior QC approval.'),
        });
      }
    } catch (saveError: any) {
      const serverData = saveError?.response?.data || {};
      const failedWorkflows: Array<{ tickerSymbol: string; details: string }> =
        Array.isArray(serverData.failedWorkflows) ? serverData.failedWorkflows : [];
      let errorMessage = serverData.error || serverData.details || 'Failed to submit finalized Falcon metrics for QC approval.';
      if (failedWorkflows.length > 0) {
        const failedLines = failedWorkflows
          .map((f: any) => `• ${f.tickerSymbol}: ${f.details}`)
          .join('\n');
        errorMessage = `${errorMessage}\n\nFailed stocks:\n${failedLines}`;
      }
      setStatus({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setSavingReview(false);
    }
  };

  const handleApproveSelectedWorkflow = async () => {
    if (!selectedQuarterWorkflow?.id) {
      setStatus({ type: 'warning', message: 'No workflow found for the selected quarter.' });
      return;
    }
    setWorkflowActionBusy(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/approve`,
        {
          workflow_id: selectedQuarterWorkflow.id,
          version_id: selectedQuarterWorkflowCurrentVersion?.id || null,
          comment: workflowActionComment || null,
        },
        { headers: authHeaders },
      );

      await loadQuarterContext();
      await loadWorkflowHistory();

      const counts = response?.data?.counts || {};
      const savedCount = Number(counts.saved || 0);
      setStatus({
        type: 'success',
        message: `Workflow approved and committed. Saved ${savedCount} metric${savedCount === 1 ? '' : 's'} to production tables.`,
      });
      setWorkflowActionComment('');
    } catch (approveError) {
      const serverMessage = approveError?.response?.data?.error || approveError?.response?.data?.details;
      setStatus({
        type: 'error',
        message: serverMessage || 'Failed to approve and commit this workflow version.',
      });
    } finally {
      setWorkflowActionBusy(false);
    }
  };

  const handleRejectSelectedWorkflow = async () => {
    if (!selectedQuarterWorkflow?.id) {
      setStatus({ type: 'warning', message: 'No workflow found for the selected quarter.' });
      return;
    }
    setWorkflowActionBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/reject`,
        {
          workflow_id: selectedQuarterWorkflow.id,
          version_id: selectedQuarterWorkflowCurrentVersion?.id || null,
          comment: workflowActionComment || null,
        },
        { headers: authHeaders },
      );

      await loadWorkflowHistory();

      setStatus({
        type: 'success',
        message: 'Workflow rejected. Staff can resubmit from this version.',
      });
    } catch (rejectError) {
      const serverMessage = rejectError?.response?.data?.error || rejectError?.response?.data?.details;
      setStatus({
        type: 'error',
        message: serverMessage || 'Failed to reject this workflow version.',
      });
    } finally {
      setWorkflowActionBusy(false);
    }
  };

  const handleResubmitSelectedWorkflow = async () => {
    if (!selectedQuarterWorkflow?.id || !selectedQuarterWorkflowCurrentVersion?.id) {
      setStatus({ type: 'warning', message: 'No workflow version is available to resubmit.' });
      return;
    }
    setWorkflowActionBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/annual-report/workflows/resubmit`,
        {
          workflow_id: selectedQuarterWorkflow.id,
          from_version_id: selectedQuarterWorkflowCurrentVersion.id,
          comment: workflowActionComment || null,
        },
        { headers: authHeaders },
      );

      await loadWorkflowHistory();

      setStatus({
        type: 'success',
        message: 'Workflow version resubmitted for senior QC approval.',
      });
      setWorkflowActionComment('');
    } catch (resubmitError) {
      const serverMessage = resubmitError?.response?.data?.error || resubmitError?.response?.data?.details;
      setStatus({
        type: 'error',
        message: serverMessage || 'Failed to resubmit this workflow version.',
      });
    } finally {
      setWorkflowActionBusy(false);
    }
  };

  const handleStatementVerify = () => {
    if (!activeStatement) {
      return;
    }
    setVerifiedStatements((prev) => ({
      ...prev,
      [activeStatement]: true,
    }));
    setStatus({ type: 'success', message: `${activeStatement} marked as reviewed.` });
    setQcComplete(false);
  };

  const handleDownloadXlsx = () => {
    if (!qcComplete) return;

    const workbook = XLSX.utils.book_new();
    const preferredOrder = PREFERRED_STATEMENT_ORDER;

    const grouped = lineItems.reduce((acc, item) => {
      const key = item.statement || 'Unassigned Statement';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    const orderedStatements = [
      ...preferredOrder,
      ...Object.keys(grouped).filter((name) => !preferredOrder.includes(name)),
    ].filter((name, index, self) => name && self.indexOf(name) === index);

    const sanitizeSheetName = (name) => {
      const base = (name || 'Sheet');
      const withoutCommonInvalids = base.replace(/[\/?*]/g, '');
      const cleaned = withoutCommonInvalids.replace(/\[|\]/g, '').slice(0, 31);
      return cleaned || 'Sheet';
    };
    if (!orderedStatements.length) {
      const emptySheet = XLSX.utils.json_to_sheet([{
        'Line Item': '',
      }]);
      emptySheet['!cols'] = [{ wch: 35 }];
      const headerCell = XLSX.utils.encode_cell({ c: 0, r: 0 });
      if (emptySheet[headerCell]) {
        emptySheet[headerCell].s = {
          fill: { patternType: 'solid', fgColor: { rgb: 'FFFF00' } },
          font: { bold: true },
        };
      }
      XLSX.utils.book_append_sheet(workbook, emptySheet, 'Line_Items');
    } else {
      orderedStatements.forEach((statementName) => {
        const rows = grouped[statementName] || [];
        const relevantColumns = valueColumns.filter((column) => (
          rows.some((item) => {
            const raw = item[column];
            if (typeof raw === 'number') return true;
            return typeof raw === 'string' && raw.trim() !== '';
          })
        ));

        const headerOrder = [
          'Line Item',
          ...relevantColumns,
        ];

        const sheetRows = rows.map((item) => {
          const record = {
            'Line Item': item.lineItem,
          };

          relevantColumns.forEach((column) => {
            record[column] = item[column] ?? '';
          });

          return record;
        });

        const headerTemplate = headerOrder.reduce((acc, key) => {
          acc[key] = '';
          return acc;
        }, {});

        const worksheet = XLSX.utils.json_to_sheet(
          sheetRows.length ? sheetRows : [headerTemplate],
          { header: headerOrder },
        );

        if (!worksheet['!cols']) {
          worksheet['!cols'] = [];
        }
        headerOrder.forEach((_, idx) => {
          worksheet['!cols'][idx] = { wch: idx === 0 ? 35 : 18 };
          const cellAddress = XLSX.utils.encode_cell({ c: idx, r: 0 });
          if (!worksheet[cellAddress]) {
            worksheet[cellAddress] = { v: headerOrder[idx], t: 's' };
          }
          worksheet[cellAddress].s = {
            fill: { patternType: 'solid', fgColor: { rgb: 'FFFF00' } },
            font: { bold: true },
          };
        });

        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          sanitizeSheetName(statementName || 'Statement'),
        );
      });
    }

    const sopSheetRows = displayedSopSummary.map((row) => ({
      Metric: row.metric,
      'Latest Quarter': row.value ?? '-',
      Statement: row.statement || '',
      'Source Column': row.column || '',
      'Source Line Item': row.sourceLine || '',
    }));
    const sopSheet = XLSX.utils.json_to_sheet(
      sopSheetRows.length ? sopSheetRows : [{ Metric: 'No SOP metrics available', 'Latest Quarter': '-' }],
    );
    sopSheet['!cols'] = [
      { wch: 35 },
      { wch: 18 },
      { wch: 24 },
      { wch: 24 },
      { wch: 28 },
    ];
    sopSheet['!cols'].forEach((_, idx) => {
      const cellAddress = XLSX.utils.encode_cell({ c: idx, r: 0 });
      if (sopSheet[cellAddress]) {
        sopSheet[cellAddress].s = {
          fill: { patternType: 'solid', fgColor: { rgb: 'FFFF00' } },
          font: { bold: true },
        };
      }
    });
    XLSX.utils.book_append_sheet(workbook, sopSheet, 'SOP_Summary');

    const suggestedName = pdfName
      ? `${pdfName.replace(/\.pdf$/i, '')}_verified.xlsx`
      : 'verified_line_items.xlsx';

    XLSX.writeFile(workbook, suggestedName, { cellStyles: true });
  };

  const statementUploadSections = STATEMENT_UPLOAD_OPTIONS.map((option) => {
    const images = Array.isArray(statementUploadImagesByStatement?.[option.value])
      ? statementUploadImagesByStatement[option.value]
      : [];
    return {
      statement: option.value,
      label: option.label,
      images,
    };
  });
  const statementUploadTotalImageCount = statementUploadSections.reduce(
    (total, section) => total + section.images.length,
    0
  );
  const statementUploadSourceImages = statementUploadSections.flatMap((section) => (
    section.images.map((image) => ({
      ...image,
      statement: section.statement,
      statementLabel: section.label,
    }))
  ));
  const hasSourcePreview = uploadMode === 'statement_upload'
    ? statementUploadTotalImageCount > 0
    : Boolean(pdfBase64);
  const reviewedCount = Object.values(verifiedStatements).filter(Boolean).length;
  const reviewProgressPercent = totalStatements ? Math.round((reviewedCount / totalStatements) * 100) : 0;

  useEffect(() => {
    if (!totalStatements) {
      return;
    }
    if (reviewedCount === totalStatements && !qcComplete) {
      setQcComplete(true);
    }
  }, [reviewedCount, totalStatements, qcComplete]);
  const workflowSteps = [
    {
      title: uploadMode === 'statement_upload' ? 'Upload Statement Images' : 'Upload PDF',
      description: uploadMode === 'statement_upload'
        ? 'Upload statement screenshots for Profit or Loss, Balance Sheet, and Cash Flow, then run one extraction.'
        : 'Select a quarterly Group/Consolidated report to begin.',
      status: hasSourcePreview ? 'Complete' : loading ? 'In Progress' : 'Pending',
    },
    {
      title: 'Review Statements',
      description: 'Compare each line item with the source preview and mark statements as reviewed.',
      status: reviewedCount === totalStatements && totalStatements > 0 ? 'Complete'
        : lineItems.length ? 'In Progress' : 'Locked',
    },
    {
      title: 'Finalize & Export',
      description: 'Finalize QC to unlock the JSON export and download results.',
      status: qcComplete ? 'Complete'
        : reviewedCount === totalStatements && totalStatements > 0 ? 'Ready'
          : 'Locked',
    },
  ];

  const tabAvailability = {
    overview: true,
    statements: uploadMode === 'statement_upload' ? true : lineItems.length > 0,
    sop: uploadMode === 'statement_upload' ? true : lineItems.length > 0,
    exports: lineItems.length > 0,
  };

  const renderOverviewTab = () => (
    <div className="tab-panel-body overview-tab">
      <div className="overview-tab-grid">
        <div className="overview-card">
          <h3>Workflow Overview</h3>
          <ol>
            <li>{uploadMode === 'statement_upload' ? 'Upload statement screenshots and run AI extraction.' : 'Upload a financial PDF and run AI extraction.'}</li>
            <li>Review every line item against the source preview.</li>
            <li>Adjust values where needed and mark each statement as reviewed.</li>
            <li>Finalize QC to unlock the statement-wise JSON export.</li>
          </ol>
        </div>
        <div className="overview-card metrics">
          <h3>QC Snapshot</h3>
          <div className="overview-metrics">
            <div>
              <span className="metric-label">Line Items</span>
              <span className="metric-value">{totalRows}</span>
            </div>
            <div>
              <span className="metric-label">Statements</span>
              <span className="metric-value">{totalStatements}</span>
            </div>
            <div>
              <span className="metric-label">Reviewed</span>
              <span className="metric-value">{reviewedCount}</span>
            </div>
            <div>
              <span className="metric-label">Pending</span>
              <span className="metric-value">{pendingStatements}</span>
            </div>
          </div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{ width: `${reviewProgressPercent}%` }}
            />
          </div>
          <p className="progress-caption">
            {totalStatements
              ? `${reviewProgressPercent}% of statements reviewed`
              : uploadMode === 'statement_upload'
                ? 'Upload statement screenshots to begin the workflow.'
                : 'Upload a PDF to begin the workflow.'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderStatementsTab = () => {
    if (!lineItems.length) {
      const helperText = uploadMode === 'statement_upload'
        ? (loading
          ? 'Falcon AI is still extracting values from your statement images. Please wait for completion.'
          : 'No extracted line items yet. Click "Extract All Uploaded Statements" after uploading images, then open this tab again.')
        : 'Upload a PDF to populate the statements workspace.';
      return (
        <div className="tab-placeholder">
          {helperText}
        </div>
      );
    }

    const appliedMultiplierKey = statementMultiplierApplied?.[activeStatement] || '';
    const multiplierApplied = Boolean(appliedMultiplierKey);
    const multiplierLabel = appliedMultiplierKey === 'x1000000' ? 'x1,000,000' : 'x1,000';
    const showSopMetricColumn = shouldDisplaySopMetricColumn(activeStatement);

    return (
      <div className="tab-panel-body statements-tab">
        <div className="tab-header">
          <div>
            <h3>Statement Review</h3>
            <p>
              Compare each line item with the source preview on the left, adjust values where needed, and mark the statement as reviewed.
            </p>
          </div>
          <div className="tab-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={openManualEntry}
              disabled={showManualEntry}
            >
              Add Manual Line Item
            </button>
          </div>
        </div>
        <div className="panel-card statement-hint">
          <p>
            If a row is missing from the extraction, use <strong>Add Manual Line Item</strong> to capture it here.
          </p>
          <p>
            Remember to review each statement and click <strong>Mark Statement Reviewed</strong> once the values match the source preview.
          </p>
          {showSopMetricColumn && (
            <p>
              Use the <strong>SOP Metric</strong> column to map each row to your SOP categories so the summary breakdown stays in sync.
            </p>
          )}
        </div>
        {showManualEntry && (
          <div className="manual-entry-card panel-card">
            <div className="manual-entry-grid">
              <label>
                <span>Statement</span>
                <input
                  type="text"
                  value={manualRow.statement}
                  onChange={(event) => handleManualFieldChange('statement', event.target.value)}
                  placeholder="e.g. Statement of Profit or Loss"
                />
              </label>
              <label>
                <span>Line Item</span>
                <input
                  type="text"
                  value={manualRow.lineItem}
                  onChange={(event) => handleManualFieldChange('lineItem', event.target.value)}
                  placeholder="Enter line item description"
                />
              </label>
            </div>
            <div className="manual-entry-values">
              {manualEntryColumns.length ? (
                manualEntryColumns.map((column) => (
                  <label key={column}>
                    <span>{column}</span>
                    <input
                      type="text"
                      value={manualRow.values?.[column] ?? ''}
                      onChange={(event) => handleManualValueInput(column, event.target.value)}
                      placeholder={`Value for ${column}`}
                    />
                  </label>
                ))
              ) : (
                <p className="manual-entry-empty">No numeric columns detected. You can still add the line item now and edit values later.</p>
              )}
            </div>
            <div className="manual-entry-actions">
              <button type="button" className="secondary-button" onClick={handleManualRowCancel}>Cancel</button>
              <button type="button" className="finalize-button" onClick={handleManualRowSubmit}>Insert Row</button>
            </div>
          </div>
        )}
        <div className="statement-nav panel-card">
          {statements.length > 0 ? (
            <>
              <div className="statement-tabs">
                {statements.map((statement) => {
                  const totalForStatement = statementTotalsMap[statement] || 0;
                  const statementReviewed = !!verifiedStatements[statement];
                  return (
                    <button
                      key={statement}
                      type="button"
                      className={`statement-tab${statement === activeStatement ? ' active' : ''}${statementReviewed ? ' reviewed' : ''}`}
                      onClick={() => setActiveStatement(statement)}
                    >
                      <span>{statement}</span>
                      <span className="statement-tab-count">
                        {totalForStatement}{statementReviewed ? ' \u2713' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="statement-summary">
                {activeStatement ? (
                  <>
                    <span className="summary-label">Active Statement:</span>
                    <span className="summary-value">{activeStatement}</span>
                  </>
                ) : (
                  <span className="summary-label">Select a statement tab to review line items.</span>
                )}
              </div>
              {activeStatement && (
                <div className="statement-controls">
                  <button
                    type="button"
                    className={activeStatementVerified ? 'statement-verify-button verified' : 'statement-verify-button'}
                    onClick={handleStatementVerify}
                    disabled={activeStatementVerified}
                  >
                    {activeStatementVerified ? 'Statement Reviewed' : 'Mark Statement Reviewed'}
                  </button>
                  {activeStatementVerified && <span className="statement-status-badge">Reviewed</span>}
                </div>
              )}
            </>
          ) : (
            <div className="statement-empty">
              {uploadMode === 'statement_upload'
                ? 'Upload statement screenshots or add a manual line item to create your first statement.'
                : 'Upload a PDF or add a manual line item to create your first statement.'}
            </div>
          )}
        </div>
        {activeStatement ? (
          <div className={`statement-editor-region${statementEditorFullscreen ? ' fullscreen' : ''}`}>
            <div className="statement-editor-toolbar panel-card">
              <div className="statement-editor-title">
                <h4>{activeStatement}</h4>
                <span>{visibleItems.length} line item{visibleItems.length === 1 ? '' : 's'} ready for review</span>
              </div>
              <button
                type="button"
                className="secondary-button statement-fullscreen-toggle"
                onClick={() => setStatementEditorFullscreen((current) => !current)}
              >
                {statementEditorFullscreen ? 'Minimize' : 'Full Screen'}
              </button>
            </div>
          <div className="statement-tools panel-card">
            <div className="statement-tools-buttons">
               <button
                type="button"
                className={`secondary-button${appliedMultiplierKey === 'x1000' ? ' active danger-button' : ''}`}
                onClick={appliedMultiplierKey === 'x1000' ? handleStatementRemoveZeros : handleStatementAddZeros}
                disabled={!statementValueColumns.length || (multiplierApplied && appliedMultiplierKey !== 'x1000')}
              >
                {appliedMultiplierKey === 'x1000' ? 'Undo x1,000 Multiplier' : 'Apply x1,000 to Values'}
              </button>
              <button
                type="button"
                className={`secondary-button${appliedMultiplierKey === 'x1000000' ? ' active danger-button' : ''}`}
                onClick={appliedMultiplierKey === 'x1000000' ? handleStatementRemoveZeros : handleStatementAddMillionZeros}
                disabled={!statementValueColumns.length || (multiplierApplied && appliedMultiplierKey !== 'x1000000')}
              >
                {appliedMultiplierKey === 'x1000000' ? 'Undo x1,000,000 Multiplier' : 'Apply x1,000,000 to Values'}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={handleStatementMakePositive}
                disabled={!statementValueColumns.length}
              >
                Convert Negatives to Positive
              </button>
            </div>
            <span className="statement-tools-hint">
              These actions affect only the active statement.
              {multiplierApplied ? ` The ${multiplierLabel} multiplier has already been applied.` : ''}
            </span>
          </div>
          <div className="statement-table panel-card">
          {visibleItems.length ? (
            <div className="statement-table-scroll-shell">
              {showStatementTopScrollbar && (
                <div
                  className="statement-top-scrollbar"
                  ref={statementTopScrollbarRef}
                  onScroll={handleStatementTopScrollbarScroll}
                  aria-hidden="true"
                >
                  <div
                    className="statement-top-scrollbar-inner"
                    ref={statementTopScrollbarInnerRef}
                  />
                </div>
              )}
              <div
                className="table-wrapper"
                ref={statementTableWrapperRef}
                onScroll={handleStatementTableScroll}
              >
                <table>
                  <thead>
                    <tr>
                      <th className="select-column">
                        <input
                          type="checkbox"
                          ref={selectAllCheckboxRef}
                          onChange={(event) => handleSelectAllVisibleRows(event.target.checked)}
                          aria-label="Select all rows for this statement"
                          disabled={!visibleItems.length}
                        />
                      </th>
                      <th>Line Item</th>
                      {showSopMetricColumn && (
                        <th className="metric-column-header">SOP Metric</th>
                      )}
                      {statementValueColumns.map((column) => (
                        <th key={column}>
                          <div className="column-header">
                            <div className="column-header-label">
                              {buildColumnHeaderLines(column).map((line, index) => (
                                <span key={`${column}-${index}`} className="column-header-line">
                                  {line}
                                </span>
                              ))}
                            </div>
                            <div className="column-action-group">
                              <button
                                type="button"
                                className="column-rename-button"
                                onClick={() => handleRenameColumn(column)}
                                title={`Rename column ${column}`}
                              >
                                Rename
                              </button>
                              <button
                                type="button"
                                className="column-remove-button"
                                onClick={() => handleRemoveColumn(column, activeStatement)}
                                title={`Remove column ${column}`}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </th>
                      ))}
                      <th className="actions-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((row) => {
                      const isSelected = selectedRowIds.has(row.rowId);
                      const rowLabel = row.lineItem || row['Line Item'] || 'Row';
                      const rowSignAction = getRowValueSignAction(row, statementValueColumns);
                      return (
                        <tr key={row.rowId} className={isSelected ? 'selected-row' : ''}>
                          <td className="select-cell">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(event) => handleRowSelectionToggle(row.rowId, event.target.checked)}
                              aria-label={`Select ${rowLabel}`}
                            />
                          </td>
                          <td>{rowLabel}</td>
                          {showSopMetricColumn && (
                            <td className="classification-cell">
                              <select
                                value={row.classification ?? ''}
                                onChange={(event) => handleClassificationChange(row.rowId, event.target.value)}
                              >
                                <option value="">Unassigned</option>
                                {sopMetricOptions.map((metric) => (
                                  <option key={metric} value={metric}>{metric}</option>
                                ))}
                              </select>
                            </td>
                          )}
                          {statementValueColumns.map((column) => (
                            <td key={column} className="value-cell">
                              <input
                                type="text"
                                value={row[column] ?? ''}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  handleValueChange(row.rowId, column, value);
                                }}
                              />
                            </td>
                          ))}
                          <td className="row-actions">
                            <button
                              type="button"
                              className="row-action-button neutral"
                              onClick={() => handleRowSignConversion(row.rowId, rowSignAction.targetSign)}
                              disabled={!statementValueColumns.length}
                            >
                              {rowSignAction.label}
                            </button>
                            <button
                              type="button"
                              className="row-action-button"
                              onClick={() => handleDeleteRow(row.rowId)}
                            >
                              Delete Row
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="placeholder">No line items available for the selected statement.</div>
          )}
          </div>
          </div>
        ) : (
          <div className="statement-table panel-card">
            <div className="placeholder">Select a statement tab to review line items.</div>
          </div>
        )}
      </div>
    );
  };

  const handleSopJumpToSource = (row) => {
    const statementName = toTrimmed(row?.statement);
    const sourceLine = toTrimmed(row?.sourceLine);
    const sourceColumn = toTrimmed(row?.column);

    if (!statementName && !sourceLine) {
      setStatus({
        type: 'warning',
        message: `No source mapping available for ${row?.metric || 'this metric'}.`,
      });
      return;
    }

    setActiveWorkspaceTab('statements');
    if (statementName) {
      setActiveStatement(statementName);
    }

    const statementKey = normaliseKey(statementName);
    const sourceLineKey = normaliseKey(sourceLine);
    const sourceColumnKey = normaliseKey(sourceColumn);

    const matchedRow = lineItems.find((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      if (statementKey && normaliseKey(item.statement) !== statementKey) {
        return false;
      }

      const itemLine = toTrimmed(item.lineItem || item['Line Item']);
      const itemLineKey = normaliseKey(itemLine);
      if (sourceLineKey && itemLineKey) {
        if (
          itemLineKey !== sourceLineKey
          && !itemLineKey.includes(sourceLineKey)
          && !sourceLineKey.includes(itemLineKey)
        ) {
          return false;
        }
      }

      if (sourceColumnKey) {
        const matchedColumnName = Object.keys(item).find(
          (columnName) => normaliseKey(columnName) === sourceColumnKey
        );
        if (!matchedColumnName) {
          return false;
        }
        const columnValue = toTrimmed(item[matchedColumnName]);
        if (!columnValue || columnValue === '-') {
          return false;
        }
      }

      return true;
    });

    if (matchedRow?.rowId) {
      setSelectedRowIds(new Set([matchedRow.rowId]));
      setStatus({
        type: 'info',
        message: `Navigated to "${row.metric}" source row in Statements tab.`,
      });
      return;
    }

    setSelectedRowIds(new Set());
    setStatus({
      type: 'warning',
      message: `Source row for "${row.metric}" was not found in current statement rows.`,
    });
  };

  const renderSopTab = () => {
    if (!lineItems.length) {
      const helperText = uploadMode === 'statement_upload'
        ? (loading
          ? 'Falcon AI is still extracting values from your statement images. SOP metrics will appear after extraction.'
          : 'No SOP metrics yet. Run statement image extraction first so Falcon can generate SOP values.')
        : 'Upload a PDF to populate the SOP summary metrics.';
      return (
        <div className="tab-placeholder">
          {helperText}
        </div>
      );
    }

    const sopRows = (displayedSopSummary || []).map((row) => {
      const status = getSopVerificationStatus(row);
      const statementText = toTrimmed(row?.statement);
      const sourceLineText = toTrimmed(row?.sourceLine);
      const columnText = toTrimmed(row?.column);
      const cashFlowQuarterAdjustment = normaliseCashFlowQuarterAdjustment(row?.cashFlowQuarterAdjustment);
      const profitLossQuarterAdjustment = normaliseProfitLossQuarterAdjustment(row?.profitLossQuarterAdjustment);
      const needsAttention = status !== 'auto' || !statementText || !sourceLineText;
      return {
        ...row,
        status,
        statementText,
        sourceLineText,
        columnText,
        cashFlowQuarterAdjustment,
        profitLossQuarterAdjustment,
        needsAttention,
        searchText: [
          row?.metric,
          row?.value,
          statementText,
          sourceLineText,
          columnText,
          cashFlowQuarterAdjustment?.extractedCumulative,
          cashFlowQuarterAdjustment?.previousCumulative,
          cashFlowQuarterAdjustment?.quarterValue,
          profitLossQuarterAdjustment?.extractedCumulative,
          profitLossQuarterAdjustment?.previousCumulative,
          profitLossQuarterAdjustment?.quarterValue,
          getSopStatusLabel(status),
        ]
          .join(' ')
          .toLowerCase(),
      };
    });

    const totalMetricCount = sopRows.length;
    const autoMetricCount = sopRows.filter((row) => row.status === 'auto').length;
    const manualMetricCount = sopRows.filter((row) => row.status === 'manual').length;
    const missingMetricCount = sopRows.filter((row) => row.status === 'missing').length;
    const needsAttentionCount = sopRows.filter((row) => row.needsAttention).length;
    const cashFlowSopRows = sopRows.filter((row) =>
      CASH_FLOW_QUARTER_METRIC_KEYS.has(normaliseMetricKey(row.metric))
    );
    const cashFlowAdjustedRows = cashFlowSopRows.filter((row) =>
      Boolean(row.cashFlowQuarterAdjustment)
    );
    const profitLossSopRows = sopRows.filter((row) =>
      PROFIT_LOSS_Q4_METRIC_KEYS.has(normaliseMetricKey(row.metric))
    );
    const profitLossAdjustedRows = profitLossSopRows.filter((row) =>
      Boolean(row.profitLossQuarterAdjustment)
    );

    const statementOptions = Array.from(
      new Set(
        sopRows
          .map((row) => row.statementText)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    const hasCashFlowsOption = statementOptions.some(
      (name) => normaliseKey(name) === normaliseKey('Cash Flows')
    );
    if (cashFlowSopRows.length && !hasCashFlowsOption) {
      statementOptions.push('Cash Flows');
      statementOptions.sort((a, b) => a.localeCompare(b));
    }

    const searchText = toTrimmed(sopSearchTerm).toLowerCase();
    const filteredSopRows = sopRows.filter((row) => {
      if (sopStatusFilter === 'attention' && !row.needsAttention) {
        return false;
      }
      if (sopStatusFilter === 'missing' && row.status !== 'missing') {
        return false;
      }
      if (sopStatusFilter === 'manual' && row.status !== 'manual') {
        return false;
      }
      if (sopStatusFilter === 'auto' && row.status !== 'auto') {
        return false;
      }
      if (sopStatementFilter !== 'all' && row.statementText !== sopStatementFilter) {
        return false;
      }
      if (searchText && !row.searchText.includes(searchText)) {
        return false;
      }
      return true;
    });
    const showCashFlowSection = sopStatementFilter === 'all'
      || normaliseKey(sopStatementFilter) === normaliseKey('Cash Flows');
    const cashFlowScaleLabel = cashFlowScaleKey === 'x1000000'
      ? 'x1,000,000'
      : cashFlowScaleKey === 'x1000'
        ? 'x1,000'
        : 'no scaling';
    const isThousandScale = cashFlowScaleKey === 'x1000';
    const isMillionScale = cashFlowScaleKey === 'x1000000';

    const sopTableColumnCount = 7;

    return (
      <div className="tab-panel-body sop-tab">
        <div className={`panel-card sop-summary-card${sopSummaryFullscreen ? ' fullscreen' : ''}`}>
          <div className="sop-card-header">
            <div>
              <h3>SOP Summary</h3>
              <p>Use this checklist to verify each metric quickly. Start with "Needs Attention", then open breakdown/edit only when needed.</p>
            </div>
            <button
              type="button"
              className="secondary-button sop-fullscreen-toggle"
              onClick={() => setSopSummaryFullscreen((current) => !current)}
            >
              {sopSummaryFullscreen ? 'Minimize' : 'Full Screen'}
            </button>
          </div>
          <div className="sop-quick-stats">
            <div className="sop-stat-card">
              <span className="sop-stat-label">Total Metrics</span>
              <strong className="sop-stat-value">{totalMetricCount}</strong>
            </div>
            <div className="sop-stat-card auto">
              <span className="sop-stat-label">Auto</span>
              <strong className="sop-stat-value">{autoMetricCount}</strong>
            </div>
            <div className="sop-stat-card manual">
              <span className="sop-stat-label">Manual</span>
              <strong className="sop-stat-value">{manualMetricCount}</strong>
            </div>
            <div className="sop-stat-card missing">
              <span className="sop-stat-label">Missing</span>
              <strong className="sop-stat-value">{missingMetricCount}</strong>
            </div>
            <div className="sop-stat-card attention">
              <span className="sop-stat-label">Needs Attention</span>
              <strong className="sop-stat-value">{needsAttentionCount}</strong>
            </div>
          </div>
          <div className="sop-summary-controls">
            <div className="sop-summary-filter-group">
              <label htmlFor="sop-status-filter">View</label>
              <select
                id="sop-status-filter"
                value={sopStatusFilter}
                onChange={(event) => setSopStatusFilter(event.target.value)}
              >
                <option value="attention">Needs Attention</option>
                <option value="all">All</option>
                <option value="missing">Missing</option>
                <option value="manual">Manual</option>
                <option value="auto">Auto</option>
              </select>
            </div>
            <div className="sop-summary-filter-group">
              <label htmlFor="sop-statement-filter">Statement</label>
              <select
                id="sop-statement-filter"
                value={sopStatementFilter}
                onChange={(event) => setSopStatementFilter(event.target.value)}
              >
                <option value="all">All Statements</option>
                {statementOptions.map((statementName) => (
                  <option key={statementName} value={statementName}>{statementName}</option>
                ))}
              </select>
            </div>
            <div className="sop-summary-filter-group search">
              <label htmlFor="sop-search-input">Search</label>
              <input
                id="sop-search-input"
                type="text"
                value={sopSearchTerm}
                onChange={(event) => setSopSearchTerm(event.target.value)}
                placeholder="Metric, source line, or period"
              />
            </div>
            {hasPausedSopRecalculation && (
              <div className="sop-summary-filter-group">
                <label>Column Target</label>
                <button
                  type="button"
                  className="sop-action-button primary"
                  onClick={handleResumeSopRecalculation}
                  disabled={!valueColumns.length}
                >
                  Recalculate SOPs
                </button>
              </div>
            )}
          </div>
          {showCashFlowSection && (
            <div className="sop-cashflow-section">
            <div className="sop-cashflow-header">
              <h4>Cash Flow Quarter Adjustment (Database Based)</h4>
              <p>
                For cumulative cash flow values, Falcon scales extracted cumulative by {cashFlowScaleLabel} and subtracts the same fiscal year's previous quarter totals from DB.
              </p>
              <div className="sop-cashflow-scale-actions">
                <button
                  type="button"
                  className={`sop-action-button compact${isThousandScale ? ' active' : ''}`}
                  onClick={() => setCashFlowScaleKey('x1000')}
                >
                  Use x1,000
                </button>
                <button
                  type="button"
                  className={`sop-action-button compact${isMillionScale ? ' active' : ''}`}
                  onClick={() => setCashFlowScaleKey('x1000000')}
                >
                  Use x1,000,000
                </button>
                <button
                  type="button"
                  className={`sop-action-button compact${cashFlowScaleKey === 'none' ? ' active' : ''}`}
                  onClick={() => setCashFlowScaleKey('none')}
                >
                  Use Original
                </button>
              </div>
            </div>
              {cashFlowAdjustedRows.length ? (
                <div className="sop-cashflow-table-wrapper">
                  <table className="sop-cashflow-table">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th>Selected Quarter</th>
                        <th>Extracted Cumulative</th>
                        <th>Previous FY Quarters (DB)</th>
                        <th>This Quarter (Saved/Shown)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashFlowAdjustedRows.map((row) => (
                        <tr key={`cashflow-${row.metric}`}>
                          <td>{row.metric || '-'}</td>
                          <td>{row.cashFlowQuarterAdjustment?.selectedQuarter || '-'}</td>
                          <td>{row.cashFlowQuarterAdjustment?.extractedCumulative || '-'}</td>
                          <td>{row.cashFlowQuarterAdjustment?.previousCumulative || '-'}</td>
                          <td>{row.cashFlowQuarterAdjustment?.quarterValue || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="sop-breakdown-empty">
                  No cash flow quarter-adjustment rows yet. Select stock, fiscal year, and quarter before extraction.
                </p>
              )}
            </div>
          )}
          {showProfitLossQ4CumulativeAdjustment && showCashFlowSection && profitLossAdjustedRows.length > 0 && (
            <div className="sop-cashflow-section sop-pl-section">
              <div className="sop-cashflow-header">
                <h4>
                  Profit / Loss Cumulative Adjustment
                  {' '}
                  <span
                    className={`sop-pl-toggle-badge${applyProfitLossQ4CumulativeAdjustment ? ' active' : ' inactive'}`}
                  >
                    {applyProfitLossQ4CumulativeAdjustment ? 'ENABLED — This Quarter value sent to QC' : 'DISABLED — Extracted value sent to QC'}
                  </span>
                </h4>
                <p>
                  {applyProfitLossQ4CumulativeAdjustment
                    ? `Toggle is ON. The "This Quarter" value (Current Extracted Value minus Previous Quarter Value) is what gets saved and sent to Senior QC.`
                    : `Toggle is OFF. The raw extracted value is saved. Turn the toggle ON to subtract previous quarter cumulative totals.`}
                </p>
              </div>
              <div className="sop-cashflow-table-wrapper">
                <table className="sop-cashflow-table sop-pl-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Quarter</th>
                      <th>Current Extracted Value</th>
                      <th>Previous Quarter Value</th>
                      <th className={applyProfitLossQ4CumulativeAdjustment ? 'sop-pl-active-col' : ''}>This Quarter Value {applyProfitLossQ4CumulativeAdjustment ? '✓ Saved' : '(Preview)'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitLossAdjustedRows.map((row) => {
                      const adj = row.profitLossQuarterAdjustment;
                      const isDisabled = Boolean(adj?._disabled);
                      return (
                        <tr
                          key={`pl-${row.metric}`}
                          className={isDisabled ? 'sop-pl-row-disabled' : 'sop-pl-row-active'}
                        >
                          <td>{row.metric || '-'}</td>
                          <td>{adj?.selectedQuarter || '-'}</td>
                          <td>{adj?.extractedCumulative || '-'}</td>
                          <td>{adj?.previousCumulative || '-'}</td>
                          <td className={!isDisabled ? 'sop-pl-active-col' : ''}>
                            {adj?.quarterValue || '-'}
                            {isDisabled && (
                              <span className="sop-pl-preview-label"> (not applied)</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="sop-summary-table-wrapper">
            <table className="sop-summary-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Latest Quarter</th>
                  <th>Statement</th>
                  <th>Source Line</th>
                  <th>Column / Period</th>
                  <th>Status</th>
                  <th className="sop-actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSopRows.map((row) => {
                  const isEditing = editingSopMetric === row.metric;
                  const isExpanded = Boolean(expandedSopMetrics?.[row.metric]);
                  const isCashFlowMetric = CASH_FLOW_QUARTER_METRIC_KEYS.has(
                    normaliseMetricKey(row.metric)
                  );
                  const linkedRows = lineItemBreakdown[row.metric] || [];
                  const linkedSourceDraft = linkedSourceDrafts[row.metric] || {
                    statement: '',
                    rowId: '',
                  };
                  const availableSourceRows = getAvailableSourceRows(row.metric, linkedSourceDraft.statement);
                  const manualEntriesForMetric = manualSopEntries[row.metric] || [];
                  const canJumpToSource = Boolean(row.statementText || row.sourceLineText);
                  const breakdownDraft = breakdownDrafts[row.metric] || {
                    statement: '',
                    lineItem: '',
                    calculation: [createEmptyCalculationStep()],
                  };
                  const cashFlowQuarterAdjustment = row.cashFlowQuarterAdjustment;
                  const profitLossQuarterAdjustment = row.profitLossQuarterAdjustment;
                  const metricSlug = (row.metric || 'metric').toString().replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();

                  const metricKey = normaliseMetricKey(row.metric);
                  const finalSourceValue = profitLossQuarterAdjustment && !profitLossQuarterAdjustment._disabled
                    ? profitLossQuarterAdjustment.quarterValue ?? '-'
                    : row.value ?? '-';
                  const actualValue = parseNumericValue(finalSourceValue);
                  const isExcludedFromConversion =
                    metricKey.includes('shareprice') ||
                    metricKey.includes('issuedshares') ||
                    metricKey.includes('numberofshares') ||
                    metricKey === 'sharesoutstanding' ||
                    metricKey === 'totnoofshares';
                  const conversionRequested = convertCurrency && baseCurrency !== targetCurrency
                    && !isExcludedFromConversion && actualValue !== null;
                  const hasFinalConvertedValue = conversionRequested && !extractionExchangeRateLoading
                    && Number.isFinite(Number(extractionExchangeRate)) && Number(extractionExchangeRate) > 0;

                  return (
                    <Fragment key={row.metric}>
                      <tr className={`sop-summary-row status-${row.status}${row.manual ? ' manual' : ''}`}>
                        <td>{row.metric}</td>
                        <td>
                          <div className={`sop-value-display${hasFinalConvertedValue ? ' sop-converted-primary-value' : profitLossQuarterAdjustment && !profitLossQuarterAdjustment._disabled ? ' sop-pl-primary-value' : ''}`}>
                            {hasFinalConvertedValue
                              ? `${targetCurrency} ${(Number(actualValue) * Number(extractionExchangeRate)).toLocaleString(undefined, { maximumFractionDigits: 4 })}`
                              : finalSourceValue}
                            {row.manual && (
                              <span className="sop-manual-indicator">Manual</span>
                            )}
                          </div>
                          {conversionRequested && (
                            <div className="sop-original-currency-detail">
                              {hasFinalConvertedValue ? (
                                <>
                                  <span>Original ({baseCurrency}): {finalSourceValue}</span>
                                  <span>1 {baseCurrency} = {extractionExchangeRate} {targetCurrency}</span>
                                </>
                              ) : (
                                <span>{extractionExchangeRateLoading ? 'Fetching rate...' : 'Rate unavailable'}</span>
                              )}
                            </div>
                          )}
                          {cashFlowQuarterAdjustment && (
                            <div className="sop-cashflow-quarter-detail">
                              {cashFlowQuarterAdjustment.selectedQuarter ? (
                                <span className="sop-cashflow-quarter-label">
                                  {cashFlowQuarterAdjustment.selectedQuarter}
                                </span>
                              ) : null}
                              <span>
                                Cum: {cashFlowQuarterAdjustment.extractedCumulative}
                              </span>
                              <span>
                                Prev: {cashFlowQuarterAdjustment.previousCumulative}
                              </span>
                              <span>
                                This Q: {cashFlowQuarterAdjustment.quarterValue}
                              </span>
                            </div>
                          )}
                          {profitLossQuarterAdjustment && !profitLossQuarterAdjustment._disabled && (
                            <div className={`sop-cashflow-quarter-detail profit-loss${profitLossQuarterAdjustment._disabled ? ' sop-pl-detail-disabled' : ' sop-pl-detail-active'}`}>
                              {profitLossQuarterAdjustment.selectedQuarter ? (
                                <span className="sop-cashflow-quarter-label">
                                  {profitLossQuarterAdjustment.selectedQuarter}
                                </span>
                              ) : null}
                              <span title="Current extracted cumulative value from the statement">
                                <strong>Extracted:</strong> {profitLossQuarterAdjustment.extractedCumulative}
                              </span>
                              <span title="Sum of prior quarters in the same fiscal year (from DB)">
                                <strong>Prev Q:</strong> {profitLossQuarterAdjustment.previousCumulative}
                              </span>
                              {profitLossQuarterAdjustment._disabled && (
                                <span
                                  title="Toggle is OFF — this value is not applied"
                                >
                                  <strong>This Q:</strong> {profitLossQuarterAdjustment.quarterValue}
                                  <span className="sop-pl-preview-label"> (preview, not applied)</span>
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>{row.statementText || '-'}</td>
                        <td>{row.sourceLineText || '-'}</td>
                        <td>{row.columnText || '-'}</td>
                        <td>
                          <span className={`sop-status-badge ${row.status}`}>
                            {getSopStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="sop-actions-cell">
                          <div className="sop-action-buttons">
                            <button
                              type="button"
                              className="sop-action-button"
                              onClick={() => handleSopJumpToSource(row)}
                              disabled={!canJumpToSource}
                            >
                              Go to Source
                            </button>
                            <button
                              type="button"
                              className={`sop-action-button${isExpanded ? ' active' : ''}`}
                              onClick={() => toggleSopMetricExpansion(row.metric)}
                            >
                              {isExpanded ? 'Hide' : 'Edit'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="sop-edit-row">
                          <td colSpan={sopTableColumnCount}>
                            <div className="sop-edit-form">
                              <label>
                                <span>Value</span>
                                <input
                                  type="text"
                                  value={sopEditDraft.value}
                                  onChange={(event) => handleSopEditFieldChange('value', event.target.value)}
                                  placeholder="Leave blank for '-'"
                                />
                              </label>
                              <label>
                                <span>Statement</span>
                                <input
                                  type="text"
                                  value={sopEditDraft.statement}
                                  onChange={(event) => handleSopEditFieldChange('statement', event.target.value)}
                                />
                              </label>
                              <label>
                                <span>Column</span>
                                <input
                                  type="text"
                                  value={sopEditDraft.column}
                                  onChange={(event) => handleSopEditFieldChange('column', event.target.value)}
                                />
                              </label>
                              <label>
                                <span>Source Line</span>
                                <input
                                  type="text"
                                  value={sopEditDraft.sourceLine}
                                  onChange={(event) => handleSopEditFieldChange('sourceLine', event.target.value)}
                                />
                              </label>
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExpanded && (
                        <tr className="sop-breakdown-row">
                          <td colSpan={sopTableColumnCount}>
                            <div className="sop-breakdown">
                              <div className="sop-breakdown-section">
                                <div className="sop-breakdown-section-header">
                                  <h4>Linked line items</h4>
                                  <span>{linkedRows.length} linked</span>
                                </div>
                                {linkedRows.length ? (
                                  <ul className="sop-breakdown-list">
                                    {linkedRows.map((item) => {
                                      const valueEntries = Object.entries(item.values || {});
                                      const previewText = getLineItemValuePreview(item);
                                      return (
                                        <li key={item.rowId} className="sop-breakdown-list-item">
                                          <div className="sop-breakdown-item-header">
                                            <span className="sop-breakdown-title">
                                              {(item.statement || 'Statement unknown')}
                                              {' · '}
                                              {(item.lineItem || 'Unnamed line item')}
                                            </span>
                                            <div className="sop-breakdown-item-actions">
                                              <span className="sop-breakdown-tag">Linked</span>
                                              <button
                                                type="button"
                                                className="sop-action-button danger compact"
                                                onClick={() => handleRemoveLinkedSource(row.metric, item.rowId)}
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          </div>
                                          <div className="sop-breakdown-values">
                                            {valueEntries.length ? (
                                              valueEntries.map(([column, value]) => (
                                                <span key={column}>{column}: {value}</span>
                                              ))
                                            ) : (
                                              <span className="sop-breakdown-empty">No numeric values captured.</span>
                                            )}
                                          </div>
                                          {!valueEntries.length && previewText ? (
                                            <p className="sop-breakdown-empty muted">{previewText}</p>
                                          ) : null}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <p className="sop-breakdown-empty">
                                    No linked rows yet. Use the SOP Metric dropdown in the statements tab to link line items.
                                  </p>
                                )}
                                <div className="sop-breakdown-form">
                                  <div className="sop-breakdown-section-header">
                                    <h4>Add linked source</h4>
                                    <span>{availableSourceRows.length} available</span>
                                  </div>
                                  <div className="sop-breakdown-manual-grid">
                                    <label>
                                      <span>Statement</span>
                                      <select
                                        value={linkedSourceDraft.statement}
                                        onChange={(event) => handleLinkedSourceDraftChange(
                                          row.metric,
                                          'statement',
                                          event.target.value,
                                        )}
                                      >
                                        <option value="">All Statements</option>
                                        {statementSuggestions.map((statementName) => (
                                          <option key={statementName} value={statementName}>{statementName}</option>
                                        ))}
                                      </select>
                                    </label>
                                    <label>
                                      <span>Source Row</span>
                                      <select
                                        value={linkedSourceDraft.rowId}
                                        onChange={(event) => handleLinkedSourceDraftChange(
                                          row.metric,
                                          'rowId',
                                          event.target.value,
                                        )}
                                      >
                                        <option value="">Select line item</option>
                                        {availableSourceRows.map((item) => {
                                          const lineItemLabel = item.lineItem || item['Line Item'] || 'Unnamed line item';
                                          const previewText = getLineItemValuePreview(item);
                                          const currentMetric = toTrimmed(item.classification);
                                          const optionLabel = [
                                            item.statement || 'Statement unknown',
                                            lineItemLabel,
                                            previewText,
                                            currentMetric ? `Current: ${currentMetric}` : 'Current: Unassigned',
                                          ].filter(Boolean).join(' | ');
                                          return (
                                            <option key={item.rowId} value={item.rowId}>{optionLabel}</option>
                                          );
                                        })}
                                      </select>
                                    </label>
                                  </div>
                                  <p className="sop-breakdown-empty muted">
                                    {row.manual
                                      ? 'This metric has a manual override. Linked-source changes update the auto total behind it, but the displayed SOP value stays manual until that override is edited or removed.'
                                      : 'Adding or removing linked sources updates the auto-calculated metric value immediately.'}
                                  </p>
                                  <div className="sop-breakdown-form-actions">
                                    <button
                                      type="button"
                                      className="sop-action-button primary"
                                      onClick={() => handleAddLinkedSource(row.metric)}
                                      disabled={!availableSourceRows.length}
                                    >
                                      Add Source
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div className="sop-breakdown-section">
                                <div className="sop-breakdown-section-header">
                                  <h4>Manual adjustments</h4>
                                  <span>{manualEntriesForMetric.length} manual</span>
                                </div>
                                {manualEntriesForMetric.length ? (
                                  <div className="sop-breakdown-manual-list">
                                    {manualEntriesForMetric.map((entry, entryIndex) => {
                                      const metricSlug = (row.metric || 'metric').toString().replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
                                      const entrySuffix = `${metricSlug}-${entry.id || entryIndex}`;
                                      const entryStatementListId = `manual-statement-${entrySuffix}`;
                                      const entryLineItemListId = `manual-line-item-${entrySuffix}`;
                                      const calculationSteps = Array.isArray(entry.calculation) ? entry.calculation : [];
                                      const lineItemOptions = getLineItemSuggestions(entry.statement);
                                      return (
                                        <div key={entry.id} className="sop-breakdown-manual-item">
                                          <div className="sop-breakdown-manual-grid">
                                            <label>
                                              <span>Statement</span>
                                              <input
                                                type="text"
                                                list={entryStatementListId}
                                                value={entry.statement || ''}
                                                placeholder="Select statement"
                                                onChange={(event) => handleManualBreakdownValueChange(
                                                  row.metric,
                                                  entry.id,
                                                  'statement',
                                                  event.target.value,
                                                )}
                                              />
                                              <datalist id={entryStatementListId}>
                                                {statementSuggestions.map((statementName) => (
                                                  <option key={statementName} value={statementName} />
                                                ))}
                                              </datalist>
                                            </label>
                                            <label>
                                              <span>Line Item</span>
                                              <input
                                                type="text"
                                                list={entryLineItemListId}
                                                value={entry.lineItem || ''}
                                                placeholder="Select line item"
                                                onChange={(event) => handleManualBreakdownValueChange(
                                                  row.metric,
                                                  entry.id,
                                                  'lineItem',
                                                  event.target.value,
                                                )}
                                              />
                                              <datalist id={entryLineItemListId}>
                                                {lineItemOptions.map((itemName) => (
                                                  <option key={itemName} value={itemName} />
                                                ))}
                                              </datalist>
                                            </label>
                                          </div>
                                          <div className="sop-breakdown-calculation">
                                            <div className="sop-breakdown-calculation-header">
                                              <span>Calculation Steps</span>
                                            </div>
                                            {calculationSteps.length ? (
                                              <div className="sop-breakdown-calculation-list">
                                                {calculationSteps.map((step, stepIndex) => {
                                                  const stepSuffix = `${entrySuffix}-${stepIndex}`;
                                                  const stepStatementListId = `manual-step-statement-${stepSuffix}`;
                                                  const stepLineItemListId = `manual-step-line-item-${stepSuffix}`;
                                                  const stepLineItemOptions = getLineItemSuggestions(step.statement || entry.statement);
                                                  return (
                                                    <div key={stepSuffix} className="sop-breakdown-calculation-row">
                                                      <label>
                                                        <span>Operator</span>
                                                        <select
                                                          value={step.operator || '+'}
                                                          onChange={(event) => handleManualBreakdownCalculationChange(
                                                            row.metric,
                                                            entry.id,
                                                            stepIndex,
                                                            'operator',
                                                            event.target.value,
                                                          )}
                                                        >
                                                          <option value="+">+</option>
                                                          <option value="-">-</option>
                                                          <option value="*">*</option>
                                                          <option value="/">/</option>
                                                        </select>
                                                      </label>
                                                      <label>
                                                        <span>Number</span>
                                                        <input
                                                          type="text"
                                                          inputMode="decimal"
                                                          value={step.constant || ''}
                                                          placeholder="e.g. 1000"
                                                          onChange={(event) => handleManualBreakdownCalculationChange(
                                                            row.metric,
                                                            entry.id,
                                                            stepIndex,
                                                            'constant',
                                                            event.target.value,
                                                          )}
                                                        />
                                                      </label>
                                                      <label>
                                                        <span>Statement</span>
                                                        <input
                                                          type="text"
                                                          list={stepStatementListId}
                                                          value={step.statement || ''}
                                                          placeholder="Statement"
                                                          onChange={(event) => handleManualBreakdownCalculationChange(
                                                            row.metric,
                                                            entry.id,
                                                            stepIndex,
                                                            'statement',
                                                            event.target.value,
                                                          )}
                                                        />
                                                        <datalist id={stepStatementListId}>
                                                          {statementSuggestions.map((statementName) => (
                                                            <option key={statementName} value={statementName} />
                                                          ))}
                                                        </datalist>
                                                      </label>
                                                      <label>
                                                        <span>Line Item</span>
                                                        <input
                                                          type="text"
                                                          list={stepLineItemListId}
                                                          value={step.lineItem || ''}
                                                          placeholder="Line item"
                                                          onChange={(event) => handleManualBreakdownCalculationChange(
                                                            row.metric,
                                                            entry.id,
                                                            stepIndex,
                                                            'lineItem',
                                                            event.target.value,
                                                          )}
                                                        />
                                                        <datalist id={stepLineItemListId}>
                                                          {stepLineItemOptions.map((itemName) => (
                                                            <option key={itemName} value={itemName} />
                                                          ))}
                                                        </datalist>
                                                      </label>
                                                      <button
                                                        type="button"
                                                        className="sop-breakdown-remove-step"
                                                        onClick={() => handleRemoveManualBreakdownCalculationStep(row.metric, entry.id, stepIndex)}
                                                      >
                                                        Remove
                                                      </button>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <p className="sop-breakdown-empty muted">No calculation steps configured.</p>
                                            )}
                                            <div className="sop-breakdown-add-step-container">
                                              <button
                                                type="button"
                                                className="sop-breakdown-add-step"
                                                onClick={() => handleAddManualBreakdownCalculationStep(row.metric, entry.id)}
                                              >
                                                Add Step
                                              </button>
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            className="sop-action-button danger"
                                            onClick={() => handleRemoveManualBreakdownEntry(row.metric, entry.id)}
                                          >
                                            Remove Entry
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <p className="sop-breakdown-empty">No manual adjustments recorded.</p>
                                )}
                                <div className="sop-breakdown-form">
                                  <div className="sop-breakdown-manual-grid">
                                    <label>
                                      <span>Statement</span>
                                      <input
                                        type="text"
                                        list={`draft-statement-${metricSlug}`}
                                        value={breakdownDraft.statement}
                                        placeholder="Select statement"
                                        onChange={(event) => handleManualBreakdownDraftChange(
                                          row.metric,
                                          'statement',
                                          event.target.value,
                                        )}
                                      />
                                      <datalist id={`draft-statement-${metricSlug}`}>
                                        {statementSuggestions.map((statementName) => (
                                          <option key={statementName} value={statementName} />
                                        ))}
                                      </datalist>
                                    </label>
                                    <label>
                                      <span>Line Item</span>
                                      <input
                                        type="text"
                                        list={`draft-line-item-${metricSlug}`}
                                        value={breakdownDraft.lineItem}
                                        placeholder="Select line item"
                                        onChange={(event) => handleManualBreakdownDraftChange(
                                          row.metric,
                                          'lineItem',
                                          event.target.value,
                                        )}
                                      />
                                      <datalist id={`draft-line-item-${metricSlug}`}>
                                        {getLineItemSuggestions(breakdownDraft.statement).map((itemName) => (
                                          <option key={itemName} value={itemName} />
                                        ))}
                                      </datalist>
                                    </label>
                                  </div>
                                  <div className="sop-breakdown-calculation">
                                    <div className="sop-breakdown-calculation-header">
                                      <span>Calculation Steps</span>
                                    </div>
                                    {Array.isArray(breakdownDraft.calculation) && breakdownDraft.calculation.length ? (
                                      <div className="sop-breakdown-calculation-list">
                                        {breakdownDraft.calculation.map((step, stepIndex) => {
                                          const stepSuffix = `${metricSlug}-draft-${stepIndex}`;
                                          const stepStatementListId = `draft-step-statement-${stepSuffix}`;
                                          const stepLineItemListId = `draft-step-line-item-${stepSuffix}`;
                                          const stepLineItemOptions = getLineItemSuggestions(step.statement || breakdownDraft.statement);
                                          return (
                                            <div key={stepSuffix} className="sop-breakdown-calculation-row">
                                              <label>
                                                <span>Operator</span>
                                                <select
                                                  value={step.operator || '+'}
                                                  onChange={(event) => handleManualBreakdownDraftCalculationChange(
                                                    row.metric,
                                                    stepIndex,
                                                    'operator',
                                                    event.target.value,
                                                  )}
                                                >
                                                  <option value="+">+</option>
                                                  <option value="-">-</option>
                                                  <option value="*">*</option>
                                                  <option value="/">/</option>
                                                </select>
                                              </label>
                                              <label>
                                                <span>Number</span>
                                                <input
                                                  type="text"
                                                  inputMode="decimal"
                                                  value={step.constant || ''}
                                                  placeholder="e.g. 1000"
                                                  onChange={(event) => handleManualBreakdownDraftCalculationChange(
                                                    row.metric,
                                                    stepIndex,
                                                    'constant',
                                                    event.target.value,
                                                  )}
                                                />
                                              </label>
                                              <label>
                                                <span>Statement</span>
                                                <input
                                                  type="text"
                                                  list={stepStatementListId}
                                                  value={step.statement || ''}
                                                  placeholder="Statement"
                                                  onChange={(event) => handleManualBreakdownDraftCalculationChange(
                                                    row.metric,
                                                    stepIndex,
                                                    'statement',
                                                    event.target.value,
                                                  )}
                                                />
                                                <datalist id={stepStatementListId}>
                                                  {statementSuggestions.map((statementName) => (
                                                    <option key={statementName} value={statementName} />
                                                  ))}
                                                </datalist>
                                              </label>
                                              <label>
                                                <span>Line Item</span>
                                                <input
                                                  type="text"
                                                  list={stepLineItemListId}
                                                  value={step.lineItem || ''}
                                                  placeholder="Line item"
                                                  onChange={(event) => handleManualBreakdownDraftCalculationChange(
                                                    row.metric,
                                                    stepIndex,
                                                    'lineItem',
                                                    event.target.value,
                                                  )}
                                                />
                                                <datalist id={stepLineItemListId}>
                                                  {stepLineItemOptions.map((itemName) => (
                                                    <option key={itemName} value={itemName} />
                                                  ))}
                                                </datalist>
                                              </label>
                                              <button
                                                type="button"
                                                className="sop-breakdown-remove-step"
                                                onClick={() => handleRemoveManualBreakdownDraftStep(row.metric, stepIndex)}
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <p className="sop-breakdown-empty muted">No calculation steps configured.</p>
                                    )}
                                    <div className="sop-breakdown-add-step-container">
                                      <button
                                        type="button"
                                        className="sop-breakdown-add-step"
                                        onClick={() => handleAddManualBreakdownDraftStep(row.metric)}
                                      >
                                        Add Step
                                      </button>
                                    </div>
                                  </div>
                                  <div className="sop-breakdown-form-actions">
                                    <button
                                      type="button"
                                      className="sop-action-button primary"
                                      onClick={() => handleAddManualBreakdownEntry(row.metric)}
                                    >
                                      Add Entry
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!filteredSopRows.length && (
                  <tr className="sop-empty-row">
                    <td colSpan={sopTableColumnCount}>
                      No metrics match the current filter. Try changing View/Statement/Search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExportsTab = () => {
    if (!lineItems.length) {
      return (
        <div className="tab-placeholder">
          Upload a PDF to unlock export tools.
        </div>
      );
    }

    const allStatementsReviewed = reviewedCount === totalStatements && totalStatements > 0;
    const canSaveToDatabase = qcComplete && Boolean(selectedTicker) && Boolean(validFiscalYear) && Boolean(selectedQuarter);

    return (
      <div className="tab-panel-body exports-tab">
        <div className="panel-card export-card">
          <h3>Finalize & Export JSON</h3>
          <p>
            Finalize QC once every statement is reviewed, then submit to the senior QC queue.
            Production database updates happen only after senior approval.
          </p>
          <ul className="export-schema">
            <li><strong>metadata</strong>: PDF name, QC status, and counts.</li>
            <li><strong>statements</strong>: statement-wise line items with column values.</li>
            <li><strong>sopSummary</strong>: derived metrics with source mapping.</li>
          </ul>
          <div className="export-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={handleFinalize}
              disabled={loading || !totalRows}
            >
              Finalize Quality Control
            </button>
            <button
              type="button"
              className="save-finalized-button"
              onClick={handleSaveFinalizedMetrics}
              disabled={!canSaveToDatabase || savingReview}
            >
              {savingReview ? 'Submitting to QC...' : 'Submit Finalized Metrics for QC Approval'}
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={handleDownloadXlsx}
              disabled={!qcComplete}
            >
              Download Spreadsheet (.xlsx)
            </button>
          </div>
          <div className="export-status">
            {qcComplete ? (
              <p className="export-ready">
                Quality control is complete.
                {canSaveToDatabase
                  ? ' You can now submit the finalized selected-quarter metrics for senior approval.'
                  : ' Select the correct stock, fiscal year, and quarter before submitting the finalized metrics.'}
              </p>
            ) : (
              <p className="export-hint">
                {allStatementsReviewed
                  ? 'Finalize QC to enable the JSON download.'
                  : 'Review every statement before finalizing.'}
              </p>
            )}
            {selectedQuarter ? (
              <p className="export-hint">
                Target quarter: {selectedQuarter.label}
                {savedFalconQuarterMap.get(selectedQuarter.date)
                  ? ' | Production tables already contain Falcon-committed data for this quarter.'
                  : ' | No Falcon data has been committed to production for this quarter yet.'}
              </p>
            ) : null}
            <p className="export-hint">
              To review past uploads and resubmit edits, use the <strong>Falcon Data Entry</strong> section from the sidebar.
            </p>
            <p className="export-hint">
              QC approval/rejection is now handled only in the <strong>Falcon Senior QC</strong> section.
            </p>
          </div>
          <div className="export-status">
            <p className="export-hint">
              Workflow submissions are versioned and stored in Falcon. Each approval event commits only the approved version to production.
            </p>
          </div>
        </div>
        <div className="panel-card json-preview-card">
          <div className="json-preview-header">
            <div>
              <h3>Statement-wise JSON Preview</h3>
              <p>Preview shows the first 5 line items per statement. Download for the full export.</p>
            </div>
            <div className="json-preview-metrics">
              <span>Statements: {totalStatements || 0}</span>
              <span>Line Items: {totalRows || 0}</span>
            </div>
          </div>
          <pre className="json-preview">
            <code>{jsonPreviewText}</code>
          </pre>
        </div>
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeWorkspaceTab) {
      case 'statements':
        return renderStatementsTab();
      case 'sop':
        return renderSopTab();
      case 'exports':
        return renderExportsTab();
      case 'overview':
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="app-shell">
      {(loading || hydratingWorkflowVersion) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner" />
            <p>{hydratingWorkflowVersion ? 'Loading workflow submission...' : (status?.message || 'Processing PDF...')}</p>
            <p className="loading-subtext">
              {hydratingWorkflowVersion
                ? 'Preparing previous submission for review and editing.'
                : 'Large reports can take a couple of minutes. Please keep this tab open.'}
            </p>
          </div>
        </div>
      )}
      <header className="shell-header">
        <div className="shell-brand">
          <img
            src="https://www.valueit.io/wp-content/uploads/2024/06/Valueit.png"
            alt="Valueit logo"
            className="shell-logo"
          />
          <h1>Financial Data QC Workbench</h1>
        </div>
        <div className="shell-actions">
          <label className="shell-mode-field">
            <span>Falcon Mode</span>
            <select
              value={uploadMode}
              onChange={(event) => handleUploadModeChange(event.target.value)}
              disabled={loading || savingReview}
            >
              {UPLOAD_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
          </label>
          {uploadMode === 'pdf_upload' && (
            <label className="upload-button">
              <svg
                className="upload-button-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M12 3a1 1 0 0 1 .78.37l4 5a1 1 0 1 1-1.56 1.26L13 6.54V15a1 1 0 0 1-2 0V6.54L8.78 9.63a1 1 0 0 1-1.56-1.26l4-5A1 1 0 0 1 12 3zm-7 12a1 1 0 0 1 1 1v3h12v-3a1 1 0 1 1 2 0v3a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-3a1 1 0 0 1 1-1z"
                />
              </svg>
              <span>Upload PDF</span>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                disabled={loading || !extractionPrerequisitesReady}
              />
            </label>
          )}
        </div>
      </header>

      <section className="report-meta-bar">
        <div className="report-meta-field">
          <label>Stock Companies (Max 2)</label>
          <div className="stock-picker-multi-container">
            {selectedStocks.map((stock) => (
              <div key={getStockSelectionKey(stock)} className="selected-stock-tag">
                <span className="stock-tag-name">
                  {stock.company_name || stock.ticker_symbol} ({stock.ticker_symbol})
                </span>
                {stock.market_name && (
                  <span className="stock-tag-market">{stock.market_name}</span>
                )}
                <button
                  type="button"
                  className="stock-tag-remove-btn"
                  onClick={() => {
                    const stockKey = getStockSelectionKey(stock);
                    setSelectedStockKeys((prev) => prev.filter((key) => key !== stockKey));
                  }}
                  disabled={loading || savingReview}
                  title={`Remove ${stock.ticker_symbol}`}
                >
                  &times;
                </button>
              </div>
            ))}
            {selectedStocks.length < 2 && (
              <button
                type="button"
                className="stock-picker-trigger-multi"
                onClick={() => {
                  setStockSearchTerm('');
                  setStockSearchModalOpen(true);
                }}
                disabled={loading || savingReview}
              >
                <span className="stock-picker-action-multi">+ Select Stock</span>
              </button>
            )}
            {selectedStocks.length === 0 && (
              <span className="stock-picker-placeholder-multi">Search and select up to 2 stocks</span>
            )}
          </div>
        </div>
        <div className="report-meta-field report-meta-year">
          <label htmlFor="falcon-fiscal-year">Fiscal Year</label>
          <input
            id="falcon-fiscal-year"
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={fiscalYear}
            onChange={(event) => setFiscalYear(event.target.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g. 2025"
            disabled={loading || savingReview}
          />
        </div>
        <div className="report-meta-field report-meta-quarter">
          <label htmlFor="falcon-quarter-select-trigger">Target Quarter</label>
          <div className="quarter-search-wrap">
            <button
              id="falcon-quarter-select-trigger"
              type="button"
              className="quarter-select-trigger"
              disabled={quarterSelectDisabled}
              onClick={() => setShowQuarterSuggestions((prev) => !prev)}
              onBlur={() => window.setTimeout(() => setShowQuarterSuggestions(false), 140)}
            >
              {selectedQuarter
                ? `${selectedQuarter.label}${selectedQuarter.saved ? ' | Falcon saved' : ''}`
                : quarterPlaceholderLabel}
            </button>
            {showQuarterSuggestions && !quarterSelectDisabled && quarterOptions.length > 0 && (
              <div className="quarter-suggestions-panel">
                {quarterOptions.map((option) => (
                  <button
                    key={option.date}
                    type="button"
                    className="quarter-suggestion-item"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setSelectedQuarterDate(option.date);
                      setShowQuarterSuggestions(false);
                      setApplyProfitLossQ4CumulativeAdjustment(false);
                      setSopMetadata((prev) => ({
                        ...(prev || {}),
                        applyProfitLossQ4CumulativeAdjustment: false,
                      }));
                    }}
                  >
                    <span className="quarter-suggestion-label">{option.label}</span>
                    <span className="quarter-suggestion-status">
                      {option.saved ? 'Falcon saved' : 'Not saved'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {showProfitLossQ4CumulativeAdjustment && selectedQuarter && (
          <div className="report-meta-field report-meta-q4-adjustment">
            <label htmlFor="falcon-q4-profit-loss-toggle">
              {`Q${selectedQuarter.quarter} Profit/Loss`}
            </label>
            <button
              id="falcon-q4-profit-loss-toggle"
              type="button"
              className={`q4-adjustment-toggle${applyProfitLossQ4CumulativeAdjustment ? ' active' : ''}`}
              aria-pressed={applyProfitLossQ4CumulativeAdjustment}
              onClick={async () => {
                const next = !applyProfitLossQ4CumulativeAdjustment;
                setApplyProfitLossQ4CumulativeAdjustment(next);
                setSopMetadata((prev) => ({
                  ...(prev || {}),
                  applyProfitLossQ4CumulativeAdjustment: next,
                }));
                await fetchSopAdjustments(next);
              }}
              disabled={loading || savingReview}
            >
              <span className="q4-adjustment-toggle-dot" aria-hidden="true" />
              <span>
                {applyProfitLossQ4CumulativeAdjustment
                  ? `✓ Cumulative ON — subtracting Q1–Q${selectedQuarter.quarter - 1}`
                  : `Cumulative OFF — click to subtract Q1–Q${selectedQuarter.quarter - 1}`}
              </span>
            </button>
          </div>
        )}
        <div className="report-meta-field report-meta-currency-conversion" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
          <label style={{ fontWeight: 600 }}>Currency Conversion:</label>
          <button
            type="button"
            className={`q4-adjustment-toggle${convertCurrency ? ' active' : ''}`}
            aria-pressed={convertCurrency}
            onClick={() => setConvertCurrency(!convertCurrency)}
            disabled={loading || savingReview}
          >
            <span className="q4-adjustment-toggle-dot" aria-hidden="true" />
            <span>{convertCurrency ? '✓ Convert ON' : 'Convert OFF'}</span>
          </button>
          {convertCurrency && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <select
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                disabled={loading || savingReview}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="SAR">SAR</option>
                <option value="INR">INR</option>
                <option value="LKR">LKR</option>
                <option value="QAR">QAR</option>
                <option value="AED">AED</option>
                <option value="BHD">BHD</option>
                <option value="KWD">KWD</option>
                <option value="SGD">SGD</option>
                <option value="SDG">SDG</option>
                <option value="AUD">AUD</option>
                <option value="CAD">CAD</option>
              </select>
              <span>→</span>
              <select
                value={targetCurrency}
                onChange={(e) => setTargetCurrency(e.target.value)}
                disabled={loading || savingReview}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
              >
                <option value="AED">AED</option>
                <option value="USD">USD</option>
                <option value="LKR">LKR</option>
                <option value="SAR">SAR</option>
                <option value="QAR">QAR</option>
                <option value="INR">INR</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="BHD">BHD</option>
                <option value="KWD">KWD</option>
                <option value="SGD">SGD</option>
                <option value="SDG">SDG</option>
                <option value="AUD">AUD</option>
                <option value="CAD">CAD</option>
              </select>
              {convertCurrency && (
                <span style={{
                  background: '#e0f2fe',
                  border: '1px dashed #38bdf8',
                  color: '#0284c7',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  marginLeft: '4px'
                }}>
                  {extractionExchangeRateLoading ? (
                    'Fetching quarter rate...'
                  ) : !selectedQuarter?.date && !selectedQuarterDate ? (
                    'Select quarter to fetch rate'
                  ) : (
                    Number.isFinite(Number(extractionExchangeRate)) && Number(extractionExchangeRate) > 0
                      ? `Quarter Date Rate (${extractionExchangeRateDate || selectedQuarter?.date || selectedQuarterDate}): 1 ${baseCurrency} = ${extractionExchangeRate} ${targetCurrency}`
                      : `Rate unavailable (${extractionExchangeRateDate || selectedQuarter?.date || selectedQuarterDate}): ${baseCurrency} to ${targetCurrency}`
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="report-meta-note">
          <strong>Database save target:</strong>{' '}
          {selectedTickers.length > 0 && validFiscalYear && selectedQuarter
            ? `${selectedTickers.join(' & ')} FY ${validFiscalYear} | ${selectedQuarter.label}`
            : 'select stock, fiscal year, and quarter to link this Falcon review to a database record'}
        </div>
      </section>

      <StockSearchModal
        isModalOpen={stockSearchModalOpen}
        searchQuery={stockSearchTerm}
        setSearchQuery={setStockSearchTerm}
        filteredStocks={filteredStockSearchResults}
        handleSelectStock={handleStockSuggestionSelect}
        setIsModalOpen={setStockSearchModalOpen}
        callCompareFunction={() => {}}
        loading={!stockOptions.length}
        clearSearchOnSelect={false}
      />

      {uploadMode === 'statement_upload' && (
        <section className="statement-upload-panel panel-card">
          <div className="statement-upload-header">
            <div>
              <h3>Statement Image Upload</h3>
              <p>
                Upload screenshots under each statement header, then run one extraction for all three statements.
                Include the heading in each image so Falcon can map rows to the correct statement.
              </p>
            </div>
          </div>
          <div className="statement-upload-hint statement-upload-hint-overall">
            <span>{statementUploadTotalImageCount} image{statementUploadTotalImageCount === 1 ? '' : 's'} selected across all statements</span>
            <button
              type="button"
              className="finalize-button"
              onClick={handleStatementUploadSubmit}
              disabled={loading || !statementUploadTotalImageCount || !extractionPrerequisitesReady}
            >
              Extract All Uploaded Statements
            </button>
          </div>
          <div className="statement-upload-sections">
            {statementUploadSections.map((section) => (
              <div key={section.statement} className="statement-upload-section-card">
                <div className="statement-upload-section-header">
                  <div>
                    <h4>{section.label}</h4>
                    <span>
                      {section.images.length}/{STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION} images selected
                    </span>
                  </div>
                  <label className="upload-button">
                    <svg
                      className="upload-button-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fill="currentColor"
                        d="M12 3a1 1 0 0 1 .78.37l4 5a1 1 0 1 1-1.56 1.26L13 6.54V15a1 1 0 0 1-2 0V6.54L8.78 9.63a1 1 0 0 1-1.56-1.26l4-5A1 1 0 0 1 12 3zm-7 12a1 1 0 0 1 1 1v3h12v-3a1 1 0 1 1 2 0v3a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-3a1 1 0 0 1 1-1z"
                      />
                    </svg>
                    <span>Add Images</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(event) => handleStatementImageSelection(section.statement, event)}
                      disabled={loading || section.images.length >= STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION}
                    />
                  </label>
                </div>
                <div className="statement-paste-box">
                  <label htmlFor={`statement-paste-${section.statement}`} className="statement-paste-label">
                    Paste Screenshot
                  </label>
                  <textarea
                    id={`statement-paste-${section.statement}`}
                    className="statement-paste-input"
                    placeholder="Click here and press Ctrl+V (or Cmd+V) to paste an image"
                    onPaste={(event) => handleStatementImagePaste(section.statement, event)}
                    onChange={(event) => {
                      event.target.value = '';
                    }}
                    disabled={loading || section.images.length >= STATEMENT_UPLOAD_MAX_IMAGES_PER_SECTION}
                  />
                </div>
                <div className="statement-upload-grid">
                  {section.images.length ? section.images.map((image, index) => (
                    <div key={image.id} className="statement-upload-image-card">
                      <img src={image.dataUrl} alt={image.name} className="statement-upload-image-preview" />
                      <div className="statement-upload-image-meta">
                        <strong>{`Image ${index + 1}`}</strong>
                        <span>{image.name}</span>
                      </div>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => handleRemoveStatementImage(section.statement, image.id)}
                        disabled={loading}
                      >
                        Remove
                      </button>
                    </div>
                  )) : (
                    <div className="statement-upload-empty">
                      Add screenshot(s) for {section.label}.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="statement-upload-hint statement-upload-hint-bottom">
            <span>Run extraction from here after finishing all statement uploads.</span>
            <button
              type="button"
              className="finalize-button"
              onClick={handleStatementUploadSubmit}
              disabled={loading || !statementUploadTotalImageCount || !extractionPrerequisitesReady}
            >
              Extract All Uploaded Statements
            </button>
          </div>
        </section>
      )}

      <section className="workflow-strip">
        {workflowSteps.map((step) => {
          const statusClass = step.status.toLowerCase().replace(/\s+/g, '-');
          return (
            <div key={step.title} className={`workflow-step ${statusClass}`}>
              <div className="workflow-step-header">
                <span className="workflow-status">{step.status}</span>
                <h3>{step.title}</h3>
              </div>
              <p>{step.description}</p>
            </div>
          );
        })}
      </section>

      {error && (
        <div className="alert error">
          {error}
        </div>
      )}

      {status && (
        <div className={`alert ${status.type}`}>
          {status.message}
        </div>
      )}

      <div className="workspace-grid">
        <section className="workspace-left">
          <div className="workspace-card pdf-card">
            <div className="pdf-card-header">
              <div>
                <h2>{uploadMode === 'statement_upload' ? 'Source Images' : 'Source PDF'}</h2>
                <span className="file-name">
                  {uploadMode === 'statement_upload'
                    ? (statementUploadTotalImageCount
                      ? `${statementUploadSections.filter((section) => section.images.length).length} statements | ${statementUploadTotalImageCount} image${statementUploadTotalImageCount === 1 ? '' : 's'}`
                      : 'No images uploaded')
                    : (pdfName || 'No file uploaded')}
                </span>
              </div>
              {uploadMode !== 'statement_upload' && (
                <div className="pdf-controls">
                  <span>Zoom</span>
                  <div className="pdf-controls-buttons">
                    <button
                      type="button"
                      onClick={() => setPdfZoom((value) => Math.max(0.5, Number((value - 0.1).toFixed(2))))}
                      disabled={!iframeSrc}
                    >
                      -
                    </button>
                    <span className="zoom-value">{Math.round(pdfZoom * 100)}%</span>
                    <button
                      type="button"
                      onClick={() => setPdfZoom((value) => Math.min(3, Number((value + 0.1).toFixed(2))))}
                      disabled={!iframeSrc}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => setPdfZoom(1)}
                      disabled={!iframeSrc || pdfZoom === 1}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
            {uploadMode === 'statement_upload' ? (
              statementUploadSourceImages.length ? (
                <div className="statement-source-gallery">
                  {statementUploadSourceImages.map((image) => (
                    <div key={image.id} className="statement-source-image-frame">
                      <strong>{image.statementLabel}</strong>
                      <img src={image.dataUrl} alt={image.name} className="statement-source-image" />
                      <span className="file-name">{image.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="placeholder">Upload statement screenshots to begin.</div>
              )
            ) : iframeSrc ? (
              <div className="pdf-frame-scroll">
                <div
                  className="pdf-frame-wrapper"
                  style={{
                    transform: `scale(${pdfZoom})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <iframe title="Uploaded PDF" src={iframeSrc} className="pdf-frame" />
                </div>
              </div>
            ) : (
              <div className="placeholder">Upload a financial PDF to begin.</div>
            )}
          </div>
        </section>
        <section className="workspace-right">
          <div className="right-tab-bar">
            {WORKSPACE_TABS.map((tab) => {
              const disabled = !tabAvailability[tab.id];
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`right-tab${activeWorkspaceTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveWorkspaceTab(tab.id)}
                  disabled={disabled}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="right-tab-panel">
            {renderActiveTab()}
          </div>
        </section>
      </div>
    </div>
  );
}

export default AnnualReportExtraction;
